// lib/components/Accordion/Accordion.tsx
import * as RadixAccordion from "@radix-ui/react-accordion";
import React, { forwardRef } from "react";

// lib/components/Accordion/Accordion.styles.tsx
import styled from "@emotion/styled";

// lib/theme/base.ts
var boxShadow = {
  drop: "0px 2px 2px",
  inner: "inset 0px 2px 2px"
};
var fontSize = {
  1: "1.75rem",
  // 28px
  2: "1.625rem",
  // 26px
  3: "1.5rem",
  // 24px
  4: "1.375rem",
  // 22px
  5: "1.25rem",
  // 20px
  6: "1.125rem",
  // 18px
  7: "1rem",
  // 16px
  8: "0.875rem",
  // 14px
  9: "0.75rem",
  // 12px
  body: "1rem"
};
var lineHeight = {
  1: "2.5rem",
  // 40px
  2: "2.25rem",
  // 36px
  3: "2rem",
  // 32px
  4: "1.875rem",
  // 30px
  5: "1.75rem",
  // 28px
  6: "1.625rem",
  // 26px
  7: "1.75rem",
  // 28px
  8: "1.5rem",
  // 24px
  9: "1.2rem",
  // 14.4px
  body: "1.75rem"
  // 28px
};
var radius = {
  1: "0.25rem",
  // 4px
  2: "0.5rem"
  // 8px
};
var base = {
  boxShadow,
  fontSize,
  radius,
  fontWeight: 400,
  letterSpacing: "-0.01em",
  lineHeight
};
var base_default = base;

// lib/theme/font.ts
function font(level) {
  const resolvedLevel = Object.hasOwnProperty.call(base_default.fontSize, level) ? level : 7;
  return {
    fontSize: base_default.fontSize[resolvedLevel],
    fontWeight: base_default.fontWeight,
    letterSpacing: base_default.letterSpacing,
    lineHeight: base_default.lineHeight[resolvedLevel]
  };
}

// lib/theme/color.ts
var DEFAULT_SHADE = 600;
function getPaletteValue(palette, shade) {
  return Array.isArray(palette) ? palette : palette[shade ?? DEFAULT_SHADE];
}
function rgb(palette, shade) {
  return `rgb(${getPaletteValue(palette, shade)})`;
}
var color = {
  system: {
    border: {
      50: [225, 234, 237],
      600: [208, 215, 221],
      950: [175, 185, 188]
    },
    negative: {
      50: [252, 237, 231],
      100: [248, 219, 207],
      200: [245, 201, 183],
      300: [241, 183, 159],
      400: [235, 148, 110],
      500: [228, 112, 62],
      600: [221, 76, 14],
      700: [177, 61, 11],
      800: [133, 46, 8],
      900: [88, 30, 6],
      950: [44, 15, 3]
    },
    notice: {
      50: [254, 243, 230],
      100: [252, 230, 204],
      200: [251, 218, 179],
      300: [249, 205, 153],
      400: [246, 180, 103],
      500: [243, 155, 52],
      600: [240, 130, 1],
      700: [192, 104, 1],
      800: [144, 78, 1],
      900: [96, 52, 0],
      950: [48, 26, 0]
    },
    positive: {
      50: [230, 253, 247],
      100: [209, 245, 235],
      200: [187, 236, 224],
      300: [166, 228, 212],
      400: [145, 220, 200],
      500: [60, 187, 153],
      600: [17, 170, 130],
      700: [14, 136, 104],
      800: [10, 102, 78],
      900: [7, 68, 52],
      950: [3, 34, 26]
    },
    gray: {
      50: [248, 248, 248],
      100: [242, 242, 242],
      200: [235, 235, 235],
      400: [203, 203, 203],
      600: [152, 152, 152],
      800: [76, 76, 76],
      950: [38, 38, 38]
    },
    black: [0, 0, 0],
    white: [255, 255, 255]
  },
  severity: {
    "very-low": {
      50: [208, 248, 229],
      400: [167, 192, 184],
      600: [43, 127, 99],
      800: [25, 81, 63],
      950: [21, 70, 54]
    },
    low: {
      50: [233, 242, 219],
      400: [207, 215, 195],
      600: [152, 174, 114],
      800: [112, 125, 88],
      950: [87, 101, 64]
    },
    medium: {
      50: [255, 245, 206],
      400: [220, 206, 158],
      600: [221, 173, 0],
      800: [154, 120, 0],
      950: [112, 88, 0]
    },
    high: {
      50: [255, 234, 226],
      400: [235, 181, 158],
      600: [215, 79, 21],
      800: [154, 55, 12],
      950: [123, 37, 0]
    },
    "very-high": {
      50: [255, 224, 227],
      400: [221, 159, 165],
      600: [189, 21, 36],
      800: [129, 10, 21],
      950: [88, 0, 8]
    }
  },
  categorical: {
    matterhorn: {
      50: [255, 232, 231],
      950: [77, 58, 57]
    },
    charade: {
      50: [237, 237, 255],
      950: [48, 48, 63]
    },
    coffee: {
      50: [255, 237, 218],
      950: [87, 63, 37]
    },
    slate: {
      50: [219, 249, 255],
      950: [45, 72, 78]
    },
    voodoo: {
      50: [255, 233, 254],
      950: [75, 30, 73]
    },
    kelp: {
      50: [255, 250, 223],
      950: [81, 78, 59]
    },
    tuna: {
      50: [232, 240, 255],
      950: [43, 52, 70]
    }
  },
  data: {
    casablanca: [255, 182, 79],
    festival: [236, 224, 106],
    lime: [193, 205, 72],
    celery: [146, 185, 81],
    robin: [78, 194, 194],
    sky: [78, 173, 236],
    fuchsia: [115, 78, 194],
    amethyst: [167, 78, 194],
    mulberry: [210, 69, 137],
    charm: [230, 118, 155]
  }
};

// lib/theme/utils.ts
function rgb2(color2, alpha = "100%") {
  return `rgb(${color2} / ${alpha})`;
}

// lib/theme/palette.ts
var DEFAULT_ALPHA_LEVEL = "100%";
function getPaletteValue2(palette, key = DEFAULT_ALPHA_LEVEL) {
  return palette[key];
}
function rgb3(palette, key = DEFAULT_ALPHA_LEVEL) {
  const color2 = getPaletteValue2(palette, key);
  return rgb2(color2);
}
function rgba(palette, key = DEFAULT_ALPHA_LEVEL) {
  const color2 = getPaletteValue2(palette, DEFAULT_ALPHA_LEVEL);
  return rgb2(color2, key);
}
var flat = rgb3;
var translucent = rgba;
var system = {
  black: {
    "100%": "0 0 0"
  },
  white: {
    "100%": "255 255 255"
  },
  border: {
    "100%": "175 185 188",
    "60%": "208 215 221",
    "20%": "225 234 237"
  },
  gray: {
    "100%": "43 45 45",
    "80%": "85 87 87",
    "60%": "123 125 125",
    "40%": "143 143 143",
    "20%": "230 230 230",
    "10%": "247 247 247",
    "5%": "252 252 252"
  },
  negative: {
    "100%": "221 76 14",
    "80%": "228 112 62",
    "60%": "235 148 110",
    "40%": "241 183 159",
    "20%": "248 219 207",
    "10%": "252 237 231",
    "5%": "253 246 243"
  },
  notice: {
    "100%": "255 153 0",
    "80%": "255 173 51",
    "60%": "255 194 102",
    "40%": "255 214 153",
    "20%": "255 235 204",
    "10%": "255 245 230",
    "5%": "255 250 242"
  },
  positive: {
    "100%": "22 184 142",
    "80%": "69 198 165",
    "60%": "115 212 187",
    "40%": "162 227 210",
    "20%": "208 241 232",
    "10%": "232 248 244",
    "5%": "243 251 249"
  }
};
var shadow = {
  drop: {
    "100%": "153 153 153",
    "80%": "173 173 173",
    "60%": "194 194 194",
    "40%": "214 214 214",
    "20%": "235 235 235",
    "10%": "245 245 245",
    "5%": "250 250 250"
  },
  inner: {
    "100%": "127 133 133",
    "80%": "153 157 157",
    "60%": "178 182 182",
    "40%": "204 206 206",
    "20%": "229 231 231",
    "10%": "242 243 243",
    "5%": "249 249 249"
  }
};
var category = {
  carmine: {
    "100%": "231 104 110",
    "80%": "236 134 139",
    "60%": "241 164 168",
    "40%": "245 195 197",
    "20%": "250 225 226",
    "10%": "253 240 241",
    "5%": "254 247 248"
  },
  forest: {
    "100%": "36 97 77",
    "80%": "80 129 113",
    "60%": "124 160 148",
    "40%": "167 192 184",
    "20%": "211 223 219",
    "10%": "233 239 237",
    "5%": "244 247 246"
  },
  fuchsia: {
    "100%": "150 85 146",
    "80%": "171 119 168",
    "60%": "192 153 190",
    "40%": "213 187 211",
    "20%": "234 221 233",
    "10%": "245 238 244",
    "5%": "250 247 250"
  },
  indigo: {
    "100%": "23 62 89",
    "80%": "69 101 122",
    "60%": "116 139 155",
    "40%": "162 178 189",
    "20%": "209 216 222",
    "10%": "232 236 238",
    "5%": "243 245 247"
  },
  lavender: {
    "100%": "99 82 141",
    "80%": "130 117 164",
    "60%": "161 151 187",
    "40%": "193 186 209",
    "20%": "224 220 232",
    "10%": "239 238 244",
    "5%": "247 246 249"
  },
  mandarin: {
    "100%": "238 131 81",
    "80%": "241 156 116",
    "60%": "245 181 151",
    "40%": "248 205 185",
    "20%": "252 230 220",
    "10%": "253 243 238",
    "5%": "254 249 246"
  },
  merigold: {
    "100%": "242 170 60",
    "80%": "245 187 99",
    "60%": "247 204 138",
    "40%": "250 221 177",
    "20%": "252 238 216",
    "10%": "254 247 236",
    "5%": "254 251 245"
  },
  satin: {
    "100%": "197 89 134",
    "80%": "209 122 158",
    "60%": "220 155 182",
    "40%": "232 189 207",
    "20%": "243 222 231",
    "10%": "249 238 243",
    "5%": "252 247 249"
  },
  spruce: {
    "100%": "53 74 120",
    "80%": "93 110 147",
    "60%": "134 146 174",
    "40%": "174 183 201",
    "20%": "215 219 228",
    "10%": "235 237 242",
    "5%": "245 246 248"
  }
};
var diverging = {
  minus3: {
    "100%": "170 15 29",
    "80%": "187 63 74",
    "60%": "204 111 119",
    "40%": "221 159 165",
    "20%": "238 207 210",
    "10%": "255 255 255",
    "5%": "255 255 255"
  },
  minus2: {
    "100%": "206 71 13",
    "80%": "216 108 61",
    "60%": "226 145 110",
    "40%": "235 181 158",
    "20%": "245 218 207",
    "10%": "255 255 255",
    "5%": "255 255 255"
  },
  minus1: {
    "100%": "167 133 12",
    "80%": "185 157 61",
    "60%": "202 182 109",
    "40%": "220 206 158",
    "20%": "237 231 206",
    "10%": "255 255 255",
    "5%": "255 255 255"
  },
  neutral: {
    "100%": "232 229 219",
    "80%": "237 234 226",
    "60%": "241 239 233",
    "40%": "246 245 241",
    "20%": "250 250 248",
    "10%": "255 255 255",
    "5%": "255 255 255"
  },
  plus1: {
    "100%": "136 154 106",
    "80%": "160 174 136",
    "60%": "184 194 166",
    "40%": "207 215 195",
    "20%": "231 235 225",
    "10%": "255 255 255",
    "5%": "255 255 255"
  },
  plus2: {
    "100%": "92 127 92",
    "80%": "125 153 125",
    "60%": "157 178 157",
    "40%": "190 204 190",
    "20%": "222 229 222",
    "10%": "255 255 255",
    "5%": "255 255 255"
  },
  plus3: {
    "100%": "36 97 77",
    "80%": "80 129 113",
    "60%": "124 160 148",
    "40%": "167 192 184",
    "20%": "211 223 219",
    "10%": "255 255 255",
    "5%": "255 255 255"
  }
};

// lib/theme/theme.ts
function createTheme(overrides, brand) {
  const theme = {
    ...base_default,
    color: { brand, category, diverging, shadow, system },
    colorV2: { ...color, ...overrides }
  };
  return theme;
}

// lib/theme/themes/external.ts
var ExternalTheme = createTheme(
  {
    brand: {
      50: [252, 243, 254],
      100: [254, 240, 255],
      200: [240, 218, 242],
      300: [204, 157, 209],
      400: [179, 108, 187],
      500: [153, 59, 164],
      600: [128, 10, 141],
      700: [102, 8, 113],
      800: [77, 6, 85],
      900: [51, 4, 56],
      950: [26, 2, 28]
    }
  },
  {
    primary: {
      "100%": "128 10 141",
      "90%": "141 35 152",
      "80%": "153 59 164",
      "70%": "155 84 175",
      "60%": "179 108 187",
      "50%": "192 133 198",
      "40%": "204 157 209",
      "30%": "217 182 221",
      "20%": "230 206 232",
      "10%": "242 231 244",
      "5%": "252 243 254"
    },
    dark: {
      "100%": "38 3 42",
      "60%": "114 41 123",
      "20%": "216 209 225"
    },
    light: {
      "100%": "188 135 248",
      "60%": "215 183 251",
      "20%": "242 231 254"
    },
    lighter: {
      "100%": "246 197 251",
      "60%": "250 220 253",
      "20%": "253 243 254"
    }
  }
);

// lib/theme/themes/internal.ts
var InternalTheme = createTheme(
  {
    brand: {
      50: [245, 255, 255],
      100: [240, 255, 255],
      200: [208, 237, 236],
      300: [165, 208, 207],
      400: [105, 175, 174],
      500: [55, 149, 147],
      600: [5, 122, 120],
      700: [4, 98, 96],
      800: [3, 73, 72],
      900: [2, 49, 48],
      950: [4, 36, 36]
    }
  },
  {
    primary: {
      "100%": "5 122 120",
      "90%": "30 135 134",
      "80%": "55 149 147",
      "70%": "80 162 161",
      "60%": "105 175 174",
      "50%": "130 189 188",
      "40%": "155 202 201",
      "30%": "180 215 215",
      "20%": "205 228 228",
      "10%": "230 242 242",
      "5%": "239 249 249"
    },
    dark: {
      "100%": "23 73 90",
      "60%": "116 146 156",
      "20%": "209 219 222"
    },
    light: {
      "100%": "133 203 211",
      "60%": "182 224 229",
      "20%": "231 245 246"
    },
    lighter: {
      "100%": "198 232 234",
      "60%": "221 241 242",
      "20%": "244 250 251"
    }
  }
);

// lib/theme/themes/trustcloud.ts
var TrustCloudTheme = createTheme(
  {
    brand: {
      50: [247, 247, 247],
      100: [245, 245, 247],
      200: [215, 216, 224],
      300: [175, 177, 192],
      400: [95, 98, 131],
      500: [55, 59, 98],
      600: [42, 46, 78],
      700: [44, 47, 78],
      800: [33, 35, 59],
      900: [22, 24, 39],
      950: [11, 12, 20]
    }
  },
  {
    primary: {
      "100%": "55 59 98",
      "90%": "75 79 114",
      "80%": "95 98 129",
      "70%": "115 118 145",
      "60%": "135 137 161",
      "50%": "155 157 177",
      "40%": "175 177 192",
      "30%": "195 196 208",
      "20%": "215 216 224",
      "10%": "235 235 239",
      "5%": "245 245 247"
    },
    dark: {
      "100%": "29 33 64",
      "60%": "119 122 140",
      "20%": "210 211 217"
    },
    light: {
      "100%": "80 86 149",
      "60%": "150 154 191",
      "20%": "220 221 234"
    },
    lighter: {
      "100%": "168 173 222",
      "60%": "203 206 235",
      "20%": "238 239 248"
    }
  }
);

// lib/components/Accordion/Accordion.styles.tsx
var Root = styled.div({
  label: "accordion",
  display: "grid",
  gap: "8px"
});
var Item = styled.div(
  {
    borderStyle: "solid",
    borderWidth: "1px",
    borderRadius: "8px"
  },
  ({ theme }) => ({
    borderColor: flat(theme.color.system.gray, "20%"),
    '&[data-state="open"]': {
      borderColor: flat(theme.color.brand.primary)
    },
    "&:hover": {
      borderColor: flat(theme.color.brand.primary)
    }
  })
);
var Header = styled.div({
  alignItems: "center",
  display: "grid",
  gridAutoFlow: "column",
  gridTemplateColumns: "1fr auto"
});
var Content = styled.div({
  padding: "0px 16px 16px"
});
var Trigger = styled.button(
  {
    appearance: "none",
    background: "none",
    border: "none",
    color: "black",
    cursor: "pointer",
    padding: "0px",
    textAlign: "left",
    "& .content": {
      alignItems: "center",
      display: "grid",
      gridAutoFlow: "column",
      gridTemplateColumns: "1fr auto",
      padding: "16px"
    },
    "& .icon": {
      alignItems: "center",
      display: "inline-flex",
      lineHeight: "1"
    }
  },
  ({ theme }) => ({
    "& .heading": {
      fontSize: theme.fontSize.body,
      lineHeight: theme.lineHeight.body
    },
    '&[data-state="closed"]': {
      "&:hover": {
        backgroundColor: flat(theme.color.brand.primary, "10%"),
        borderRadius: "8px"
      },
      "&:active": {
        backgroundColor: flat(theme.color.brand.primary, "10%"),
        borderRadius: "8px"
      }
    },
    "&:hover": {
      color: flat(theme.color.brand.primary)
    },
    "&:active": {
      color: flat(theme.color.brand.primary)
    }
  })
);

// lib/components/Accordion/Accordion.tsx
var Accordion = forwardRef(
  ({ collapsible = true, defaultValue, disabled, onValueChange, type, value, ...delegated }, ref) => {
    return /* @__PURE__ */ React.createElement(
      RadixAccordion.Root,
      {
        asChild: true,
        collapsible,
        defaultValue,
        disabled,
        onValueChange,
        value,
        type
      },
      /* @__PURE__ */ React.createElement(
        Root,
        {
          ...delegated,
          ref
        }
      )
    );
  }
);
var Accordion_default = Accordion;

// lib/components/Accordion/AccordionItem.tsx
import * as RadixAccordion2 from "@radix-ui/react-accordion";
import { AnimatePresence, motion } from "framer-motion";
import React3, { forwardRef as forwardRef3 } from "react";

// lib/components/Accordion/AccordionTrigger.tsx
import { UilAngleLeft, UilAngleDown } from "@iconscout/react-unicons";
import React2, { forwardRef as forwardRef2 } from "react";
function AccordionTrigger(props, ref) {
  const isOpen = props["data-state"] === "open";
  const icon = isOpen ? /* @__PURE__ */ React2.createElement(UilAngleDown, null) : /* @__PURE__ */ React2.createElement(UilAngleLeft, null);
  return /* @__PURE__ */ React2.createElement(
    Trigger,
    {
      ...props,
      ref,
      type: "button"
    },
    /* @__PURE__ */ React2.createElement("span", { className: "content" }, /* @__PURE__ */ React2.createElement("span", { className: "heading" }, props.children), /* @__PURE__ */ React2.createElement("span", { className: "icon" }, icon))
  );
}
var AccordionTrigger_default = forwardRef2(AccordionTrigger);

// lib/components/Accordion/AccordionItem.tsx
var ANIMATION = {
  initial: { opacity: 0, y: -24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -24 }
};
function AccordionItem(props, ref) {
  const { children, heading, value, ...restProps } = props;
  return /* @__PURE__ */ React3.createElement(
    RadixAccordion2.Item,
    {
      ...restProps,
      asChild: true,
      value
    },
    /* @__PURE__ */ React3.createElement(Item, { ref }, /* @__PURE__ */ React3.createElement(RadixAccordion2.Header, { asChild: true }, /* @__PURE__ */ React3.createElement(Header, null, /* @__PURE__ */ React3.createElement(RadixAccordion2.Trigger, { asChild: true }, /* @__PURE__ */ React3.createElement(AccordionTrigger_default, null, heading ?? value)))), /* @__PURE__ */ React3.createElement(AnimatePresence, null, /* @__PURE__ */ React3.createElement(RadixAccordion2.Content, { asChild: true }, /* @__PURE__ */ React3.createElement(Content, null, /* @__PURE__ */ React3.createElement(motion.div, { ...ANIMATION }, children)))))
  );
}
var AccordionItem_default = forwardRef3(AccordionItem);

// lib/components/AnnouncementDialog/AnnouncementDialog.tsx
import React8, { forwardRef as forwardRef8, useState } from "react";
import { UilAngleLeft as UilAngleLeft2, UilAngleRight, UilMultiply as CloseIcon } from "@iconscout/react-unicons";

// lib/components/AnnouncementDialog/AnnouncementDialog.styles.tsx
import isPropValid from "@emotion/is-prop-valid";
import styled3 from "@emotion/styled";
import * as RadixDialog from "@radix-ui/react-dialog";
import * as RadixTabs from "@radix-ui/react-tabs";

// lib/components/Typography/Heading.tsx
import React5, { forwardRef as forwardRef5 } from "react";

// lib/components/Typography/Phrasing.tsx
import React4, { forwardRef as forwardRef4 } from "react";

// lib/components/Typography/Phrasing.styles.tsx
import styled2 from "@emotion/styled";
var PROPS_TO_EXCLUDE = /* @__PURE__ */ new Set(["as", "color", "level", "strength"]);
function getColorValue({ color: color2, strength, theme }) {
  switch (color2) {
    case "black":
      return "black";
    case "white":
      return "white";
    case "dark":
      return flat(theme.color.brand.dark, strength);
    case "light":
      return flat(theme.color.brand.light, strength);
    case "lighter":
      return flat(theme.color.brand.lighter, strength);
    case "primary":
      return flat(theme.color.brand.primary, strength);
    case "gray":
      return flat(theme.color.system.gray, strength);
    case "negative":
      return flat(theme.color.system.negative, strength);
    case "notice":
      return flat(theme.color.system.notice, strength);
    case "positive":
      return flat(theme.color.system.positive, strength);
    default:
      return flat(theme.color.system.gray, "100%");
  }
}
var Root3 = styled2("span", {
  shouldForwardProp: (prop) => !PROPS_TO_EXCLUDE.has(prop)
})(
  {
    margin: "0px",
    overflowWrap: "break-word"
    // Avoid text overflows by arbitrarily breaking long words.
  },
  ({ color: color2, level, strength, theme }) => {
    const getterProps = { color: color2, strength, theme };
    const colorValue = getColorValue(getterProps) ?? "black";
    return {
      color: colorValue,
      fontSize: theme.fontSize[level],
      fontWeight: theme.fontWeight,
      letterSpacing: theme.letterSpacing,
      lineHeight: theme.lineHeight[level]
    };
  }
);

// lib/components/Typography/Phrasing.tsx
function Phrasing(props, ref) {
  const { color: color2 = "gray", level = "7", strength = "100%", ...restProps } = props;
  return /* @__PURE__ */ React4.createElement(
    Root3,
    {
      ...restProps,
      ref,
      color: color2,
      level,
      strength
    }
  );
}
var Phrasing_default = forwardRef4(Phrasing);

// lib/components/Typography/Heading.tsx
var ELEMENT_LEVELS = {
  "1": "h1",
  "2": "h2",
  "3": "h3",
  "4": "h4",
  "5": "h5",
  "6": "h6",
  "7": "h6",
  "8": "h6",
  "9": "h6"
};
function Heading(props, ref) {
  const { color: color2, level = "2", strength, ...restProps } = props;
  const colorProps = { color: color2, strength };
  const elementType = restProps.as ?? ELEMENT_LEVELS[level];
  return /* @__PURE__ */ React5.createElement(
    Phrasing_default,
    {
      ...restProps,
      ref,
      as: elementType,
      level,
      ...colorProps
    }
  );
}
var Heading_default = forwardRef5(Heading);

// lib/components/Typography/Typography.tsx
import React6, { forwardRef as forwardRef6 } from "react";
var Typography = forwardRef6((props, ref) => {
  const { color: color2, strength, ...restProps } = props;
  const colorProps = { color: color2, strength };
  if (!colorProps.color && !colorProps.strength) {
    colorProps.color = "gray";
    colorProps.strength = "100%";
  }
  return /* @__PURE__ */ React6.createElement(
    Phrasing_default,
    {
      ...restProps,
      ref,
      ...colorProps
    }
  );
});
var Typography_default = Typography;

// lib/components/AnnouncementDialog/AnnouncementDialog.styles.tsx
var Overlay2 = styled3(RadixDialog.Overlay)({
  zIndex: 25e3
});
var Content4 = styled3(RadixDialog.Content, { shouldForwardProp: isPropValid })(
  {
    backgroundColor: "white",
    borderRadius: "8px",
    overflow: "hidden",
    position: "relative"
  },
  ({ theme, multipleTabs }) => ({
    boxShadow: `${theme.boxShadow.drop} ${translucent(theme.color.shadow.drop, "10%")}`,
    width: multipleTabs ? "1020px" : "680px"
  })
);
var TabsRoot = styled3(RadixTabs.Root)({
  display: "flex",
  height: "100%",
  width: "100%"
});
var SidePanel = styled3.div(
  {
    minWidth: "316px",
    padding: "24px"
  },
  ({ theme }) => ({
    borderRight: `2px solid ${flat(theme.color.system.gray, "20%")}`,
    backgroundColor: flat(theme.color.system.gray, "5%")
  })
);
var MainContent = styled3.div({
  width: "100%"
});
var Title = styled3(Typography_default)({
  paddingBottom: "16px",
  display: "block"
});
var Pagination = styled3(RadixTabs.TabsList)({
  display: "flex",
  gap: "12px",
  alignItems: "center"
});
var Actions = styled3.div({
  display: "flex",
  gap: "12px",
  alignItems: "center",
  justifyContent: "flex-end",
  width: "100%"
});
var Footer = styled3.div({
  display: "flex",
  justifyContent: "space-between",
  padding: "12px 32px 32px 32px",
  gap: "12px",
  bottom: 0,
  left: 0,
  width: "100%",
  backgroundColor: "white",
  "& > *:first-child": {
    flex: 1
  }
});
var Dot = styled3(RadixTabs.Trigger)(
  {
    borderRadius: "100%",
    height: "8px",
    width: "8px",
    cursor: "default",
    border: "none",
    padding: 0
  },
  ({ theme }) => ({
    backgroundColor: flat(theme.color.system.gray, "20%"),
    cursor: "pointer",
    '&[data-state="active"]': {
      backgroundColor: flat(theme.color.brand.primary),
      cursor: "default"
    }
  })
);
var ArrowButton = styled3.button(
  {
    width: "40px",
    height: "40px",
    alignItems: "center",
    backgroundImage: "none",
    borderRadius: "4px",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    textAlign: "center",
    backgroundColor: "white",
    "&:disabled": {
      pointerEvents: "none",
      borderColor: "transparent"
    }
  },
  (props) => {
    const { theme } = props;
    return {
      borderColor: flat(theme.color.system.gray, "20%"),
      color: flat(theme.color.system.gray, "40%"),
      "&:hover": {
        borderColor: flat(theme.color.brand.primary),
        color: flat(theme.color.brand.primary)
      },
      "&:disabled": {
        backgroundColor: flat(theme.color.system.gray, "20%")
      }
    };
  }
);
var HideButton = styled3(RadixDialog.Close)(
  {
    appearance: "none",
    background: "none",
    border: "none",
    height: "fit-content",
    width: "fit-content",
    position: "absolute",
    top: "10px",
    right: "6px",
    cursor: "pointer"
  },
  ({ theme }) => ({
    color: flat(theme.color.system.gray)
  })
);
var Styles = {
  Overlay: Overlay2,
  Content: Content4,
  SidePanel,
  MainContent,
  Title,
  Portal: RadixDialog.Portal,
  Root: RadixDialog.Root,
  Pagination,
  Footer,
  Dot,
  ArrowButton,
  HideButton,
  Actions,
  TabsRoot
};

// lib/components/Overlay/Overlay.tsx
import React7, { forwardRef as forwardRef7 } from "react";
import { Slot } from "@radix-ui/react-slot";

// lib/components/Overlay/Overlay.styles.js
import styled4 from "@emotion/styled";
var Overlay3 = styled4.div(
  {
    label: "overlay",
    position: "fixed",
    inset: 0,
    display: "grid",
    placeItems: "center",
    overflowY: "auto"
  },
  (props) => {
    const { color: color2 } = props.theme;
    return {
      backgroundColor: rgba(color2.system.gray, "40%")
    };
  }
);

// lib/components/Overlay/Overlay.tsx
var Overlay4 = forwardRef7(({ asChild = false, ...delegated }, ref) => {
  const Comp = asChild ? Overlay3.withComponent(Slot) : Overlay3;
  return /* @__PURE__ */ React7.createElement(
    Comp,
    {
      ref,
      ...delegated
    }
  );
});

// lib/components/AnnouncementDialog/AnnouncementDialog.tsx
var AnnouncementDialog = forwardRef8(
  ({
    dialog,
    shouldCloseOnOverlayClick = false,
    tabsList,
    tabRootProps,
    tabsActionsList,
    tabsContentList,
    title
  }, ref) => {
    const triggers = (tabsList == null ? void 0 : tabsList.props.children) || [];
    const values = Array.isArray(triggers) ? triggers.map((trigger) => trigger.props.value) : [triggers.props.value];
    const [selectedValue, setSelectedValue] = useState((tabRootProps == null ? void 0 : tabRootProps.defaultValue) || values[0]);
    const handleTabClick = (direction) => {
      const delta = direction === "next" ? 1 : -1;
      const { length } = values;
      const index = (values.indexOf(selectedValue) + delta) % length;
      const nextValue = index < 0 ? values[length + delta] : values[index];
      setSelectedValue(nextValue);
    };
    const [tabOrientation, setTabOrientation] = useState("vertical");
    const handleOrientationChange = (orientation) => {
      setTabOrientation(orientation);
    };
    return /* @__PURE__ */ React8.createElement(Styles.Root, { open: dialog.isOpen }, /* @__PURE__ */ React8.createElement(Styles.Portal, null, /* @__PURE__ */ React8.createElement(Styles.Overlay, { asChild: true }, /* @__PURE__ */ React8.createElement(Overlay4, null, /* @__PURE__ */ React8.createElement(
      Styles.Content,
      {
        multipleTabs: values.length > 1,
        onPointerDownOutside: shouldCloseOnOverlayClick ? dialog.hide : void 0,
        ref
      },
      /* @__PURE__ */ React8.createElement(
        Styles.TabsRoot,
        {
          ...tabRootProps,
          value: selectedValue,
          onValueChange: setSelectedValue,
          orientation: tabOrientation
        },
        values.length > 1 && /* @__PURE__ */ React8.createElement(Styles.SidePanel, null, /* @__PURE__ */ React8.createElement(
          Styles.Title,
          {
            level: "5",
            color: "gray",
            strength: "100%"
          },
          title
        ), /* @__PURE__ */ React8.createElement("div", null, tabsList)),
        /* @__PURE__ */ React8.createElement(Styles.MainContent, null, /* @__PURE__ */ React8.createElement("div", null, tabsContentList), /* @__PURE__ */ React8.createElement(Styles.Footer, null, values.length > 1 ? /* @__PURE__ */ React8.createElement(
          Styles.Pagination,
          {
            onFocus: () => handleOrientationChange("horizontal"),
            onBlur: () => handleOrientationChange("vertical")
          },
          values.length <= 8 && values.map((value) => {
            return /* @__PURE__ */ React8.createElement(
              Styles.Dot,
              {
                key: value,
                value
              }
            );
          }),
          /* @__PURE__ */ React8.createElement(
            Styles.ArrowButton,
            {
              onClick: () => handleTabClick("previous"),
              "aria-label": "Previous slide"
            },
            /* @__PURE__ */ React8.createElement(UilAngleLeft2, { size: 26 })
          ),
          /* @__PURE__ */ React8.createElement(
            Styles.ArrowButton,
            {
              onClick: () => handleTabClick("next"),
              "aria-label": "Next slide"
            },
            /* @__PURE__ */ React8.createElement(UilAngleRight, { size: 26 })
          )
        ) : /* @__PURE__ */ React8.createElement("div", null), tabsActionsList)),
        /* @__PURE__ */ React8.createElement(
          Styles.HideButton,
          {
            onClick: dialog.hide,
            type: "button"
          },
          /* @__PURE__ */ React8.createElement(CloseIcon, { size: 14 })
        )
      )
    )))));
  }
);
var AnnouncementDialog_default = AnnouncementDialog;

// lib/components/AnnouncementDialog/AnnouncementDialogTabsTrigger.tsx
import React9 from "react";

// lib/components/AnnouncementDialog/AnnouncementDialogTabsTrigger.styles.tsx
import styled5 from "@emotion/styled";
import * as RadixTabs2 from "@radix-ui/react-tabs";
var AnnouncementDialogTabsTrigger = styled5(RadixTabs2.Trigger)(
  {
    padding: "12px 16px 12px 0",
    display: "block",
    border: 0
  },
  ({ theme }) => ({
    backgroundColor: flat(theme.color.system.gray, "5%"),
    color: flat(theme.color.system.gray, "60%"),
    borderLeft: "none",
    cursor: "pointer",
    paddingLeft: 0,
    width: "100%",
    height: "52px",
    textAlign: "left",
    "& > span": {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    },
    '&[data-state="active"]': {
      backgroundColor: flat(theme.color.brand.primary, "10%"),
      color: flat(theme.color.brand.primary),
      borderLeft: `2px solid ${flat(theme.color.brand.primary)}`,
      cursor: "default",
      paddingLeft: 12
    }
  })
);
var Styles2 = {
  AnnouncementDialogTabsTrigger
};

// lib/components/AnnouncementDialog/AnnouncementDialogTabsTrigger.tsx
function AnnouncementDialogTabsTrigger2(props) {
  return /* @__PURE__ */ React9.createElement(Styles2.AnnouncementDialogTabsTrigger, { ...props }, /* @__PURE__ */ React9.createElement(Typography_default, { level: "7" }, props.children));
}
var AnnouncementDialogTabsTrigger_default = AnnouncementDialogTabsTrigger2;

// lib/components/AnnouncementDialog/index.ts
import { List } from "@radix-ui/react-tabs";

// lib/components/AnnouncementDialog/AnnouncementDialogTabsContent.tsx
import React10 from "react";

// lib/components/AnnouncementDialog/AnnouncementDialogTabsContent.styles.tsx
import styled6 from "@emotion/styled";
import { Content as Content5 } from "@radix-ui/react-tabs";
var Root6 = styled6(Content5)({
  outline: "none"
});

// lib/components/AnnouncementDialog/AnnouncementDialogTabsContent.tsx
function AnnouncementDialogTabsContent(props) {
  return /* @__PURE__ */ React10.createElement(Root6, { ...props });
}
var AnnouncementDialogTabsContent_default = AnnouncementDialogTabsContent;

// lib/components/AppLogo/AppLogo.tsx
import React11, { forwardRef as forwardRef9 } from "react";

// lib/components/AppLogo/AppLogo.styles.tsx
import styled7 from "@emotion/styled";
var PROPS_TO_EXCLUDE2 = /* @__PURE__ */ new Set(["as"]);
var Root7 = styled7("span", {
  shouldForwardProp: (prop) => !PROPS_TO_EXCLUDE2.has(prop)
})({
  label: "app-logo",
  display: "inline-block",
  width: "min-content"
});

// lib/components/AppLogo/AppLogo.tsx
var AppLogo = forwardRef9((props, ref) => {
  const { height = 32, width = "auto", ...restProps } = props;
  return /* @__PURE__ */ React11.createElement(
    Root7,
    {
      ...restProps,
      ref,
      height,
      width
    }
  );
});
var AppLogo_default = AppLogo;

// lib/components/Avatar/Avatar.tsx
import React12, { forwardRef as forwardRef10 } from "react";
import { UilUser } from "@iconscout/react-unicons";
import { Fallback as BaseFallback } from "@radix-ui/react-avatar";

// lib/components/Avatar/Avatar.styles.tsx
import styled8 from "@emotion/styled";
import { Root as BaseRoot, Image as BaseAvatarImage } from "@radix-ui/react-avatar";
import isPropValid2 from "@emotion/is-prop-valid";
var AvatarFallbackFontSize = {
  16: base_default.fontSize["9"],
  24: base_default.fontSize["8"],
  32: base_default.fontSize["7"],
  48: base_default.fontSize["6"],
  64: base_default.fontSize["4"]
};
var Root8 = styled8(BaseRoot, { shouldForwardProp: isPropValid2 })(
  {
    label: "avatar",
    alignItems: "center",
    borderRadius: "100%",
    display: "flex",
    justifyContent: "center"
  },
  (props) => {
    const { _size, theme } = props;
    return {
      backgroundColor: flat(theme.color.system.gray, "20%"),
      color: flat(theme.color.system.gray),
      fontSize: AvatarFallbackFontSize[_size],
      fontWeight: theme.fontWeight,
      height: `${_size}px`,
      width: `${_size}px`
    };
  }
);
var AvatarImage = styled8(BaseAvatarImage)({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "inherit"
});

// lib/components/Avatar/Avatar.tsx
var AVATAR_SIZE = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64
};
function getLastInitials(name) {
  const lastSpaceIdx = name.lastIndexOf(" ");
  if (lastSpaceIdx > 0 && name.length > lastSpaceIdx + 1) {
    return name.charAt(lastSpaceIdx + 1).toUpperCase();
  }
  return name.charAt(0).toUpperCase();
}
function getInitials(name) {
  const lastSpaceIdx = name.lastIndexOf(" ");
  let initials = name.charAt(0);
  if (lastSpaceIdx > 0 && name.length > lastSpaceIdx + 1) {
    initials += name.charAt(lastSpaceIdx + 1);
  }
  return initials.toUpperCase();
}
var Avatar = forwardRef10(
  ({ size = "sm", name, src, ...restProps }, ref) => {
    const sizeInDimension = AVATAR_SIZE[size];
    const initialsBasedOnSize = size === "xs" ? getLastInitials(name ?? "") : getInitials(name ?? "");
    const initials = name ? initialsBasedOnSize : null;
    return /* @__PURE__ */ React12.createElement(
      Root8,
      {
        _size: sizeInDimension,
        ref,
        ...restProps
      },
      src || initials ? /* @__PURE__ */ React12.createElement(
        AvatarImage,
        {
          src,
          alt: initialsBasedOnSize
        }
      ) : /* @__PURE__ */ React12.createElement(UilUser, { size: sizeInDimension / 1.5 }),
      initials && /* @__PURE__ */ React12.createElement(BaseFallback, null, initials)
    );
  }
);
var Avatar_default = Avatar;

// lib/components/Avatar/AvatarWithPopoverContent.tsx
import React14, { forwardRef as forwardRef11 } from "react";

// lib/components/Popover/Popover.tsx
import React13 from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { UilInfoCircle as InfoCircleIcon } from "@iconscout/react-unicons";

// lib/components/Popover/Popover.styles.tsx
import styled9 from "@emotion/styled";
import { Arrow, Content as Content6 } from "@radix-ui/react-tooltip";
var PopoverStyles = styled9(Content6)(
  {
    padding: "12px 13px",
    borderRadius: "4px",
    maxWidth: "302px",
    zIndex: 25001,
    display: "flex",
    alignItems: "center",
    "& > .info-icon": {
      marginRight: "12px",
      minWidth: "20px"
    }
  },
  (props) => {
    const { theme, variant } = props;
    switch (variant) {
      case "primary": {
        return {
          backgroundColor: flat(theme.color.system.gray, "60%"),
          color: "white"
        };
      }
      case "inverse": {
        return {
          backgroundColor: "white",
          color: flat(theme.color.system.gray),
          boxShadow: `2px 2px 6px ${flat(theme.color.system.gray, "20%")}`
        };
      }
    }
    return {};
  }
);
var PopoverArrow = styled9(Arrow)((props) => {
  const { theme, variant } = props;
  switch (variant) {
    case "primary": {
      return {
        fill: flat(theme.color.system.gray, "60%")
      };
    }
    case "inverse": {
      return {
        fill: "white"
      };
    }
  }
  return {};
});
var PopoverContent = styled9(Typography_default)({
  color: "inherit",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  overflowWrap: "break-word",
  wordBreak: "break-word"
});

// lib/components/Popover/Popover.tsx
function Popover(props) {
  const { children, side, sideOffset = 5, trigger, variant = "primary", showInfoIcon = false, ...restProps } = props;
  return /* @__PURE__ */ React13.createElement(Tooltip.Root, { ...restProps }, /* @__PURE__ */ React13.createElement(Tooltip.Trigger, { asChild: true }, trigger), /* @__PURE__ */ React13.createElement(Tooltip.Portal, null, /* @__PURE__ */ React13.createElement(
    PopoverStyles,
    {
      variant,
      side,
      sideOffset
    },
    showInfoIcon && /* @__PURE__ */ React13.createElement(
      InfoCircleIcon,
      {
        className: "info-icon",
        size: 20
      }
    ),
    /* @__PURE__ */ React13.createElement(PopoverContent, { level: "8" }, children),
    /* @__PURE__ */ React13.createElement(PopoverArrow, { variant })
  )));
}
var Popover_default = Popover;

// lib/components/Avatar/AvatarWithPopoverContent.tsx
function AvatarWithPopoverContent(props, ref) {
  const { popoverContent, ...restProps } = props;
  return /* @__PURE__ */ React14.createElement(
    Popover_default,
    {
      trigger: /* @__PURE__ */ React14.createElement(
        Avatar_default,
        {
          ref,
          ...restProps
        }
      )
    },
    popoverContent
  );
}
var AvatarWithPopoverContent_default = forwardRef11(AvatarWithPopoverContent);

// lib/components/Badge/Badge.tsx
import React15, { forwardRef as forwardRef12 } from "react";

// lib/components/Badge/Badge.styles.tsx
import styled10 from "@emotion/styled";
var Root10 = styled10.span(
  {
    label: "badge",
    alignItems: "center",
    borderRadius: "4px",
    borderStyle: "solid",
    borderWidth: "1px",
    display: "inline-flex",
    gap: "12px",
    justifyContent: "center",
    minHeight: "24px",
    minWidth: "24px",
    padding: "0px 3px",
    textAlign: "center",
    svg: {
      width: "1em",
      height: "1em"
    }
  },
  (props) => {
    const { theme, variant } = props;
    switch (variant) {
      case "primary": {
        return {
          backgroundColor: flat(theme.color.brand.primary, "100%"),
          color: "white"
        };
      }
      case "secondary": {
        return {
          borderColor: "currentColor",
          color: flat(theme.color.brand.primary, "100%")
        };
      }
      case "bullet": {
        return {
          borderColor: flat(theme.color.brand.primary, "100%"),
          borderRadius: "9999px",
          borderWidth: "4px",
          minWidth: "8px",
          minHeight: "8px",
          padding: 0,
          verticalAlign: "middle"
        };
      }
    }
    return {};
  }
);
var Content7 = styled10(Typography_default)({
  color: "currentColor",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap"
});

// lib/components/Badge/Badge.tsx
var Badge = forwardRef12(
  ({ children, variant = "primary", ...restProps }, ref) => {
    return /* @__PURE__ */ React15.createElement(
      Root10,
      {
        ...restProps,
        ref,
        variant
      },
      variant === "bullet" ? null : /* @__PURE__ */ React15.createElement(Content7, { level: "9" }, children)
    );
  }
);
var Badge_default = Badge;

// lib/components/Banner/Banner.tsx
import React16, { forwardRef as forwardRef13 } from "react";
import {
  UilExclamationTriangle as ErrorIcon,
  UilCheck as SuccessIcon,
  UilInfo as InfoIcon,
  UilExclamation as WarningIcon
} from "@iconscout/react-unicons";

// lib/components/Banner/Banner.styles.tsx
import styled11 from "@emotion/styled";
var StandardIcon = styled11.svg({
  border: "2px solid currentColor",
  borderRadius: "9999px"
});
var Root11 = styled11.div(
  {
    label: "banner",
    border: "1px solid transparent",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "12px 16px",
    // Set icon dimensions here to allow for custom icons.
    "& > svg, & > .icon": {
      "&:first-child": {
        // Ensure to target only left-end icons.
        flexShrink: 0,
        width: "20px",
        height: "20px"
      }
    }
  },
  (props) => {
    const { variant, theme } = props;
    switch (variant) {
      case "error": {
        return {
          backgroundColor: flat(theme.color.system.negative, "5%"),
          borderColor: flat(theme.color.system.negative, "40%")
        };
      }
      case "info": {
        return {
          backgroundColor: flat(theme.color.system.gray, "5%"),
          borderColor: flat(theme.color.system.gray, "40%")
        };
      }
      case "warning": {
        return {
          backgroundColor: flat(theme.color.system.notice, "5%"),
          borderColor: flat(theme.color.system.notice, "40%")
        };
      }
      case "brand": {
        return {
          backgroundColor: flat(theme.color.brand.primary),
          color: rgb3(theme.color.system.white)
        };
      }
      default:
        return {
          backgroundColor: flat(theme.color.system.positive, "5%"),
          borderColor: flat(theme.color.system.positive, "40%")
        };
    }
  }
);
var Actions2 = styled11.div({
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  // Don't allow actions to collapse.
  gap: "12px",
  // Push the actions to the right
  marginLeft: "auto"
});

// lib/components/Banner/Banner.tsx
var ICON_MAP = {
  success: /* @__PURE__ */ React16.createElement(StandardIcon2, { icon: SuccessIcon }),
  warning: /* @__PURE__ */ React16.createElement(StandardIcon2, { icon: WarningIcon }),
  error: /* @__PURE__ */ React16.createElement(ErrorIcon, null),
  info: /* @__PURE__ */ React16.createElement(StandardIcon2, { icon: InfoIcon }),
  brand: /* @__PURE__ */ React16.createElement(StandardIcon2, { icon: InfoIcon })
};
function StandardIcon2({ icon }) {
  const Comp = StandardIcon.withComponent(icon);
  return /* @__PURE__ */ React16.createElement(Comp, null);
}
var Banner = forwardRef13(
  ({ actions = null, content, showIcon = true, icon: rawIcon, variant = "success", ...delegated }, ref) => {
    const icon = rawIcon ?? ICON_MAP[variant] ?? ICON_MAP.success;
    return /* @__PURE__ */ React16.createElement(
      Root11,
      {
        ref,
        variant,
        ...delegated
      },
      showIcon && icon,
      content,
      actions !== null && /* @__PURE__ */ React16.createElement(Actions2, null, actions)
    );
  }
);
var Banner_default = Banner;

// lib/components/Breadcrumbs/Breadcrumbs.tsx
import React17, { forwardRef as forwardRef14 } from "react";

// lib/components/Breadcrumbs/Breadcrumbs.styles.tsx
import styled12 from "@emotion/styled";
var Root12 = styled12.nav(
  {
    label: "breadcrumbs",
    "& > ol": {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      listStyleType: "none"
    }
  },
  ({ theme }) => ({ color: rgb3(theme.color.system.gray, "60%") }),
  ({ level, theme }) => {
    if (level) {
      return { fontSize: theme.fontSize[level] };
    }
    return {};
  }
);

// lib/components/Breadcrumbs/Breadcrumbs.tsx
var Breadcrumbs = forwardRef14(
  ({ children, level, ...delegated }, ref) => {
    return /* @__PURE__ */ React17.createElement(
      Root12,
      {
        level,
        ref,
        ...delegated
      },
      /* @__PURE__ */ React17.createElement("ol", null, children)
    );
  }
);

// lib/components/Breadcrumbs/BreadcrumbItem.tsx
import React19, { forwardRef as forwardRef16 } from "react";
import { UilAngleRight as UilAngleRight2 } from "@iconscout/react-unicons";

// lib/components/Breadcrumbs/BreadcrumbItem.styles.tsx
import styled14 from "@emotion/styled";

// lib/components/Link/Link.tsx
import React18, { forwardRef as forwardRef15 } from "react";
import { Slot as Slot2 } from "@radix-ui/react-slot";

// lib/components/Link/Link.styles.js
import isPropValid3 from "@emotion/is-prop-valid";
import styled13 from "@emotion/styled";
var Link = styled13("a", {
  shouldForwardProp: (prop) => {
    return isPropValid3(prop) && prop !== "type";
  }
})(
  {
    label: "link",
    textDecoration: "underline",
    /**
     * A common use case is to render link as button.
     * - Adjust styling to work when using native <button />.
     * - Adjust styling to work when using Glide's <Button />.
     */
    "&:is(button)": {
      border: "none",
      borderRadius: 0,
      minHeight: "auto",
      padding: 0,
      background: "none",
      font: "inherit",
      "&:hover, &:active, &:disabled": {
        backgroundColor: "unset",
        boxShadow: "unset"
      }
    }
  },
  (props) => {
    const { color: color2 } = props.theme;
    return {
      color: flat(color2.brand.primary),
      "&:hover": {
        color: flat(color2.brand.dark)
      },
      '&[aria-disabled="true"]': {
        color: flat(color2.system.gray, "20%")
      }
    };
  }
);

// lib/components/Link/Link.tsx
var Link2 = forwardRef15(
  ({ asChild = false, disabled = false, href, ...delegated }, ref) => {
    const Comp = asChild ? Link.withComponent(Slot2) : Link;
    return /* @__PURE__ */ React18.createElement(
      Comp,
      {
        "aria-disabled": disabled ? true : void 0,
        href: disabled ? void 0 : href,
        ref,
        ...delegated
      }
    );
  }
);

// lib/components/Breadcrumbs/BreadcrumbItem.styles.tsx
var ItemWrapper = styled14.li({
  display: "flex",
  alignItems: "center",
  gap: 8
});
var Item3 = styled14(Link2)(
  {
    label: "breadcrumb-item",
    outlineWidth: 0,
    border: "1px solid transparent",
    borderRadius: 8,
    paddingRight: 8,
    paddingLeft: 8,
    color: "currentColor",
    lineHeight: 1.75,
    textDecoration: "none"
  },
  ({ theme }) => {
    return {
      "&:active, &:hover, &:focus": {
        color: rgb3(theme.color.brand.primary)
      },
      "&:hover": {
        textDecoration: "underline"
      },
      "&:focus": {
        borderColor: rgb3(theme.color.brand.primary, "30%"),
        backgroundColor: rgb3(theme.color.brand.primary, "5%"),
        boxShadow: `0 0 0 2px ${rgba(theme.color.shadow.drop, "10%")}`,
        textDecoration: "none"
      },
      // The attribute selectors below take precedence over pseudo selectors above.
      '&[aria-disabled="true"]': {
        color: "currentColor",
        textDecoration: "none"
      },
      '&[data-active="true"]': {
        color: rgb3(theme.color.brand.primary)
      }
    };
  }
);

// lib/components/Breadcrumbs/BreadcrumbItem.tsx
var BreadcrumbItem = forwardRef16(
  ({ active = false, href = null, ...delegated }, ref) => {
    return /* @__PURE__ */ React19.createElement(ItemWrapper, { ref }, /* @__PURE__ */ React19.createElement(
      Item3,
      {
        "data-active": active,
        disabled: href === null,
        href,
        ...delegated
      }
    ), /* @__PURE__ */ React19.createElement(UilAngleRight2, { size: "1.25em" }));
  }
);

// lib/components/Button/Button.tsx
import React21, { cloneElement, forwardRef as forwardRef17, isValidElement } from "react";
import { Slot as Slot3, Slottable } from "@radix-ui/react-slot";

// lib/components/Button/Button.styles.tsx
import styled15 from "@emotion/styled";

// lib/utils.ts
import React20, { useImperativeHandle, useRef } from "react";
var _a;
var nativeInputValueSetter = (_a = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")) == null ? void 0 : _a.set;
var idx = 0;
var useId = React20.useId ?? (() => {
  return `id-${idx++}`;
});
function nativeSetValue(target, value) {
  if (target) {
    nativeInputValueSetter == null ? void 0 : nativeInputValueSetter.call(target, value);
    target.dispatchEvent(new Event("input", { bubbles: true }));
  }
}
function useComposedRefs(ref) {
  const innerRef = useRef(null);
  useImperativeHandle(ref, () => innerRef.current, []);
  return innerRef;
}
var INTELLIGENCE_BACKGROUND = "linear-gradient(90deg, #FEEBFF 0%, #FFFFF1 48.5%, #E9FCFF 100%)";
var INTELLIGENCE_BORDER = "linear-gradient(90deg, #B0E9EC 0%, #E7E7AB 50%, #FFAFF6 100%)";

// lib/components/Button/Button.styles.tsx
var Root13 = styled15.button(
  font(7),
  {
    label: "button",
    alignItems: "center",
    backgroundImage: "none",
    borderRadius: "9999px",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    display: "inline-flex",
    gap: "12px",
    justifyContent: "center",
    minHeight: "40px",
    padding: "4px 12px",
    textAlign: "center",
    "&:disabled": {
      pointerEvents: "none"
    },
    "&:is(a)": {
      textDecoration: "none"
    }
  },
  (props) => {
    const { theme, variant } = props;
    switch (variant) {
      case "primary": {
        return {
          backgroundColor: rgb3(theme.color.brand.primary, "100%"),
          borderColor: "transparent",
          color: "white",
          "&:hover": {
            backgroundColor: rgb3(theme.color.brand.dark, "100%"),
            color: "white"
          },
          "&:active": {
            backgroundColor: rgb3(theme.color.brand.primary, "80%"),
            boxShadow: `${theme.boxShadow.inner} ${rgba(theme.color.shadow.inner, "10%")}`
          },
          "&:disabled": {
            backgroundColor: rgb3(theme.color.system.gray, "20%"),
            boxShadow: "none",
            color: rgb3(theme.color.system.gray, "40%")
          }
        };
      }
      case "secondary": {
        return {
          backgroundColor: "white",
          borderColor: rgb3(theme.color.brand.dark, "20%"),
          color: rgb3(theme.color.brand.primary),
          boxShadow: `${theme.boxShadow.drop} ${rgba(theme.color.shadow.drop, "10%")}`,
          "&:hover": {
            backgroundColor: rgb3(theme.color.brand.primary, "5%"),
            borderColor: rgb3(theme.color.brand.primary, "20%"),
            color: rgb3(theme.color.brand.primary)
          },
          "&:active": {
            backgroundColor: rgb3(theme.color.brand.primary, "5%"),
            borderColor: rgb3(theme.color.brand.primary, "20%"),
            boxShadow: `${theme.boxShadow.inner} ${rgba(theme.color.shadow.inner, "10%")}`
          },
          "&:disabled": {
            borderColor: rgb3(theme.color.system.gray, "40%"),
            boxShadow: "none",
            color: rgb3(theme.color.system.gray, "40%")
          }
        };
      }
      case "ghost": {
        return {
          backgroundColor: "transparent",
          borderColor: "transparent",
          color: rgb3(theme.color.brand.primary),
          "&:hover": {
            backgroundColor: rgb3(theme.color.brand.primary, "5%"),
            boxShadow: `${theme.boxShadow.inner} ${rgba(theme.color.shadow.inner, "10%")}`,
            color: rgb3(theme.color.brand.primary)
          },
          "&:active": {
            backgroundColor: rgb3(theme.color.brand.primary, "5%")
          },
          "&:disabled": {
            color: rgb(theme.colorV2.system.gray, 400),
            boxShadow: "none"
          }
        };
      }
      case "intelligence": {
        return {
          position: "relative",
          isolation: "isolate",
          color: rgb(theme.colorV2.system.gray, 800),
          borderColor: rgb(theme.colorV2.system.border, 600),
          background: `
            ${INTELLIGENCE_BACKGROUND} padding-box,
            ${INTELLIGENCE_BORDER} border-box
          `,
          boxShadow: `${theme.boxShadow.drop} ${rgba(theme.color.shadow.inner, "10%")}`,
          zIndex: 1,
          // We use a pseudo element to create a gradient background on hover
          // since backgroundImage is not an animatable property
          "&::before": {
            position: "absolute",
            content: '""',
            inset: 0,
            backgroundImage: "linear-gradient(90deg, #D7F9F9 0%, #FFFDD2 48.5%, #FEDAFB 100%)",
            zIndex: -1,
            opacity: 0,
            transition: "opacity ease-in 0.25s",
            borderRadius: "9999px"
          },
          "&:hover": {
            borderColor: "transparent",
            color: rgb(theme.colorV2.system.gray, 950),
            "&::before": {
              opacity: 1
            }
          },
          "&:disabled": {
            backgroundImage: "none",
            border: "none",
            backgroundColor: rgb(theme.colorV2.system.gray, 100),
            color: rgb(theme.colorV2.system.gray, 400)
          }
        };
      }
    }
    return {};
  }
);
var Content8 = styled15(Typography_default)({
  alignItems: "center",
  color: "inherit",
  display: "inline-grid",
  gap: "8px",
  gridAutoFlow: "column",
  justifyContent: "center"
});

// lib/components/Button/Button.tsx
var Button = forwardRef17(
  ({
    asChild = false,
    children,
    disabled,
    icon,
    iconPosition = "start",
    type = "button",
    variant = "primary",
    ...restProps
  }, ref) => {
    const Comp = asChild ? Root13.withComponent(Slot3) : Root13;
    const inner = asChild && isValidElement(children) ? /* @__PURE__ */ React21.createElement(Slottable, null, cloneElement(children, void 0, /* @__PURE__ */ React21.createElement(Content8, { level: "7" }, children.props.children))) : /* @__PURE__ */ React21.createElement(Content8, { level: "7" }, children);
    return /* @__PURE__ */ React21.createElement(
      Comp,
      {
        ...restProps,
        ref,
        disabled: !!disabled,
        type,
        variant
      },
      iconPosition === "start" && icon && /* @__PURE__ */ React21.createElement(React21.Fragment, null, icon),
      inner,
      iconPosition === "end" && icon && /* @__PURE__ */ React21.createElement(React21.Fragment, null, icon)
    );
  }
);
var Button_default = Button;

// lib/components/Dropdown/Dropdown.tsx
import React26, { forwardRef as forwardRef22 } from "react";
import Select, {
  components as SelectComponents
} from "react-select";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import CreatableSelect from "react-select/creatable";
import { useTheme } from "@emotion/react";
import { UilAngleDown as UilAngleDown2, UilArrowDown, UilArrowUp, UilEnter, UilPlus, UilTimes } from "@iconscout/react-unicons";

// lib/components/Dropdown/Dropdown.styles.tsx
import styled16 from "@emotion/styled";
function getReactSelectStyles(theme) {
  return {
    clearIndicator: (base2) => ({
      ...base2,
      "&:focus, &:hover": {
        color: rgb3(theme.color.system.gray)
      }
    }),
    container: (base2, state) => ({
      ...base2,
      ...state.isDisabled && {
        cursor: "not-allowed",
        pointerEvents: "auto"
      }
    }),
    control: (base2, state) => ({
      ...base2,
      border: `1px solid ${rgb3(theme.color.system.border, "60%")}`,
      borderRadius: 4,
      padding: "6px 12px",
      backgroundColor: rgb3(theme.color.system.white),
      boxShadow: `inset 0 2px 2px 0 ${rgb3(theme.color.shadow.inner, "10%")}`,
      color: rgb3(theme.color.system.gray),
      ".dropdown-is-invalid > &": {
        borderColor: rgb3(theme.color.system.negative)
      },
      "&:focus-within, &:hover": {
        borderColor: rgb3(theme.color.brand.primary),
        boxShadow: "none"
      },
      "&:focus-within": {
        boxShadow: `0 0 8px 0 ${rgb3(theme.color.brand.lighter)}`
      },
      ...state.isDisabled && {
        pointerEvents: "none",
        borderColor: "transparent",
        backgroundColor: rgb3(theme.color.system.gray, "10%"),
        boxShadow: "none",
        color: rgb3(theme.color.system.gray, "40%")
      }
    }),
    dropdownIndicator: (base2) => ({
      ...base2,
      "&:focus, &:hover": {
        color: rgb3(theme.color.system.gray)
      }
    }),
    group: (base2) => ({
      ...base2,
      "& + &": {
        marginTop: 16
      }
    }),
    groupHeading: (base2) => ({
      ...base2,
      padding: "8px 12px",
      backgroundColor: rgb(theme.colorV2.system.gray, 50),
      color: rgb(theme.colorV2.system.gray),
      fontSize: theme.fontSize["9"],
      fontWeight: 500
    }),
    indicatorsContainer: (base2) => ({
      ...base2,
      gap: 8,
      color: rgb3(theme.color.system.gray, "60%")
    }),
    indicatorSeparator: (base2) => ({
      ...base2,
      marginTop: 7,
      marginBottom: 7,
      backgroundColor: rgb3(theme.color.system.gray, "20%"),
      // Hide separator there is just one indicator.
      "&:first-child": {
        display: "none"
      }
    }),
    loadingMessage: (base2) => ({
      ...base2,
      padding: 12,
      color: rgb3(theme.color.system.gray, "60%")
    }),
    menu: (base2) => ({
      ...base2,
      marginTop: 8,
      border: `1px solid ${rgb3(theme.color.system.border, "60%")}`,
      borderRadius: 8,
      backgroundColor: rgb3(theme.color.system.white),
      boxShadow: `${theme.boxShadow.drop} rgba(153, 153, 153, 0.2)`,
      color: rgb3(theme.color.system.gray, "80%"),
      fontSize: theme.fontSize["8"],
      // Ensure children don't overlap border top radius.
      "& > *:first-child": {
        borderTopLeftRadius: 7,
        borderTopRightRadius: 7
      }
    }),
    multiValue: (base2) => ({
      ...base2,
      gap: 8
    }),
    multiValueRemove: (base2) => ({
      ...base2,
      color: rgb3(theme.color.system.gray, "80%"),
      "&:focus, &:hover": {
        color: rgb3(theme.color.system.gray)
      }
    }),
    noOptionsMessage: (base2) => ({
      ...base2,
      padding: 12,
      color: rgb3(theme.color.system.gray, "60%")
    }),
    option: (base2, state) => ({
      ...base2,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      borderRadius: 4,
      margin: 8,
      padding: "8px 12px",
      width: "auto",
      // Override default of 100% which breaks layout given margins below.
      ...state.isFocused && {
        backgroundColor: rgb3(theme.color.brand.primary, "5%")
      },
      "&:active": {
        backgroundColor: rgb3(theme.color.brand.primary, "20%")
      }
    }),
    placeholder: (base2) => ({
      ...base2,
      display: "flex",
      gap: 8,
      color: rgb3(theme.color.system.gray, "60%")
    }),
    valueContainer: (base2) => ({
      ...base2,
      gap: 4
    })
  };
}
var DropdownSelectToggle = styled16.div(
  // NOTE: the toggle is not a "button" since its parent element is clickable and accessible.
  {
    label: "dropdown-select-toggle",
    border: "1px solid",
    borderRadius: 9999,
    padding: 6,
    lineHeight: 0
  },
  ({ isSelected, theme }) => ({
    borderColor: rgb(isSelected ? theme.colorV2.system.negative : theme.colorV2.system.positive, 200),
    color: rgb(isSelected ? theme.colorV2.system.negative : theme.colorV2.system.positive)
  })
);
var HintTypography = styled16(Typography_default)(
  {
    label: "dropdown-hint",
    padding: "8px 12px",
    color: "currentColor",
    "& svg": {
      verticalAlign: "middle"
    },
    // Adjacent child does not require a top margin.
    "& + *": {
      marginTop: 0
    }
  },
  ({ theme }) => ({
    borderBottom: `1px solid ${rgb3(theme.color.system.border, "60%")}`,
    backgroundColor: rgb(theme.colorV2.system.gray, 50),
    "& kbd, & svg": {
      color: rgb3(theme.color.system.gray)
    }
  })
);
var MultiValueWrapper = styled16.div({
  label: "dropdown-multi-value-wrapper",
  marginTop: 12,
  "& > * + *": {
    marginTop: 8
  },
  "& > ul": {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    listStyleType: "none"
  }
});

// lib/components/Dropdown/DropdownOption.tsx
import React22, { forwardRef as forwardRef18 } from "react";

// lib/components/Dropdown/DropdownOption.styles.ts
import styled17 from "@emotion/styled";
var Wrapper = styled17.div(
  {
    label: "dropdown-option",
    display: "grid",
    gridTemplateColumns: "[indicator-start] max-content [indicator-end label-start] 1fr [label-end]",
    alignItems: "center",
    columnGap: 8,
    rowGap: 4
  },
  ({ theme }) => ({
    fontSize: theme.fontSize["9"],
    fontWeight: 500
  })
);
var IndicatorSlot = styled17.span({
  label: "dropdown-option-icon",
  lineHeight: 0,
  "& > svg": {
    width: "1.25em",
    height: "auto"
  }
});
var Label = styled17.p({
  label: "dropdown-option-label",
  gridColumn: "label",
  overflowWrap: "anywhere"
});
var Title2 = styled17(Label)({
  label: "dropdown-option-title",
  overflowWrap: "anywhere",
  // If there is no icon, extend both title and subtitle to the left boundary of the wrapper.
  "&:first-child, &:first-child + *": {
    gridColumnStart: "indicator"
  }
}).withComponent(Typography_default);

// lib/components/Dropdown/DropdownOption.tsx
var DropdownOption = forwardRef18(
  ({ option, ...delegated }, ref) => {
    return /* @__PURE__ */ React22.createElement(
      Wrapper,
      {
        ref,
        ...delegated
      },
      option.icon ? /* @__PURE__ */ React22.createElement(IndicatorSlot, null, option.icon) : null,
      /* @__PURE__ */ React22.createElement(Title2, { level: "8" }, option.title),
      option.subtitle ? /* @__PURE__ */ React22.createElement(Label, null, option.subtitle) : null
    );
  }
);

// lib/components/Tag/Tag.tsx
import React25, { forwardRef as forwardRef21 } from "react";

// lib/components/Tag/LegacyTag.tsx
import React23, { forwardRef as forwardRef19 } from "react";

// lib/components/Tag/LegacyTag.styles.tsx
import styled18 from "@emotion/styled";
var Root14 = styled18.span(
  {
    borderStyle: "solid",
    borderWidth: "1px",
    alignItems: "center",
    display: "inline-flex",
    minHeight: "24px",
    padding: "2px 12px",
    textAlign: "center"
  },
  (props) => {
    const { rounded, tagColor, theme, variant } = props;
    switch (variant) {
      case "primary": {
        return {
          backgroundColor: flat(theme.color.brand.primary, "5%"),
          borderColor: flat(theme.color.brand.primary, "40%"),
          color: "black",
          borderRadius: rounded ? "9999px" : "4px"
        };
      }
      case "secondary": {
        return {
          backgroundColor: "white",
          borderColor: flat(theme.color.brand.primary),
          color: flat(theme.color.brand.primary),
          borderRadius: rounded ? "9999px" : "4px"
        };
      }
      case "success": {
        return {
          backgroundColor: flat(theme.color.system.positive, "5%"),
          borderColor: flat(theme.color.system.positive, "40%"),
          color: "black",
          borderRadius: rounded ? "9999px" : "4px"
        };
      }
      case "warning": {
        return {
          backgroundColor: flat(theme.color.system.notice, "5%"),
          borderColor: flat(theme.color.system.notice, "40%"),
          color: "black",
          borderRadius: rounded ? "9999px" : "4px"
        };
      }
      case "error": {
        return {
          backgroundColor: flat(theme.color.system.negative, "5%"),
          borderColor: flat(theme.color.system.negative, "40%"),
          color: "black",
          borderRadius: rounded ? "9999px" : "4px"
        };
      }
      case "gray": {
        return {
          backgroundColor: flat(theme.color.system.gray, "10%"),
          borderColor: flat(theme.color.system.gray, "10%"),
          color: "black",
          borderRadius: rounded ? "9999px" : "4px"
        };
      }
      case "white": {
        return {
          backgroundColor: "white",
          borderColor: flat(theme.color.system.gray, "80%"),
          color: "black",
          borderRadius: rounded ? "9999px" : "4px"
        };
      }
      case "custom": {
        return {
          backgroundColor: flat(theme.color.category[tagColor], "10%"),
          borderColor: flat(theme.color.category[tagColor]),
          color: flat(theme.color.category[tagColor]),
          borderRadius: rounded ? "9999px" : "4px"
        };
      }
    }
    return {};
  }
);
var Content9 = styled18(Typography_default)({
  color: "inherit",
  display: "inline-grid"
});

// lib/components/Tag/LegacyTag.tsx
function Tag({ children, rounded = false, tagColor = "indigo", variant = "primary", ...delegated }, ref) {
  return /* @__PURE__ */ React23.createElement(
    Root14,
    {
      ...delegated,
      ref,
      rounded,
      tagColor,
      variant
    },
    /* @__PURE__ */ React23.createElement(Content9, { level: "8" }, children)
  );
}
var LegacyTag_default = forwardRef19(Tag);

// lib/components/Tag/TagV2.tsx
import React24, { forwardRef as forwardRef20 } from "react";
import { Slot as Slot4, Slottable as Slottable2 } from "@radix-ui/react-slot";

// lib/components/Tag/TagV2.styles.tsx
import isPropValid4 from "@emotion/is-prop-valid";
import styled19 from "@emotion/styled";
var SYSTEM_VARIANT_PENDING = "pending";
var DEFAULT_CATEGORY_VARIANT = "matterhorn";
var DEFAULT_DATA_VARIANT = "sky";
var DEFAULT_SEVERITY_VARIANT = "medium";
var DEFAULT_SYSTEM_VARIANT = SYSTEM_VARIANT_PENDING;
function getDataForeground(variant) {
  switch (variant) {
    case "fuchsia":
    case "amethyst":
    case "mulberry":
    case "charm":
      return "white";
    default:
      return "black";
  }
}
function isTagPropValid(prop) {
  return isPropValid4(prop) && prop !== "type";
}
var Wrapper2 = styled19("span", { shouldForwardProp: isTagPropValid })(
  {
    label: "tag",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    borderRadius: 4,
    padding: "4px 8px",
    background: "var(--background)",
    color: "rgba(var(--foreground), var(--text-opacity, 1))",
    fontWeight: 500,
    lineHeight: "1.25rem",
    // Applies the disabled styling
    '&[aria-disabled="true"]': {
      "--text-opacity": 0.5
    },
    "& > svg, & > .icon": {
      flexShrink: 0,
      width: "auto",
      height: "1.25em"
    }
  },
  // Apply size related styles
  ({ size, theme }) => {
    const result = { fontSize: theme.fontSize["8"] };
    if (size === "small") {
      return { ...result, paddingTop: 2, paddingBottom: 2 };
    }
    if (size === "large") {
      return { ...result, paddingTop: 6, paddingBottom: 6 };
    }
    return result;
  },
  // Apply type and variant based styles
  /** We're intentionally disabling lint rules here since we believe, at this time, it makes sense to collocate the
   * function in one place as it improves readability.
   */
  // eslint-disable-next-line max-lines-per-function, complexity
  ({ theme, type, variant }) => {
    const { colorV2 } = theme;
    switch (type) {
      case "brand": {
        return {
          "--background": rgb(colorV2.brand, 100),
          "--foreground": String(getPaletteValue(colorV2.system.black))
        };
      }
      case "category": {
        const v = variant ?? DEFAULT_CATEGORY_VARIANT;
        return {
          "--background": rgb(colorV2.categorical[v], 50),
          "--foreground": String(getPaletteValue(colorV2.categorical[v], 950))
        };
      }
      case "data": {
        const defaultDataVariant = variant ?? DEFAULT_DATA_VARIANT;
        const foreground = getDataForeground(defaultDataVariant);
        return {
          "--background": rgb(colorV2.data[defaultDataVariant]),
          "--foreground": String(getPaletteValue(colorV2.system[foreground]))
        };
      }
      case "severity": {
        const v = variant ?? DEFAULT_SEVERITY_VARIANT;
        const shade = v === "very-high" ? 600 : 950;
        return {
          "--background": rgb(colorV2.severity[v], 50),
          "--foreground": String(getPaletteValue(colorV2.severity[v], shade))
        };
      }
      case "system": {
        const v = variant ?? DEFAULT_SYSTEM_VARIANT;
        if (v === "white") {
          return {
            "--background": rgb(colorV2.system.white),
            "--foreground": rgb(colorV2.system.gray, 950),
            "& > svg, & > .icon": {
              color: rgb(colorV2.system.gray, 800)
            }
          };
        }
        if (v === "intelligence") {
          return {
            "--background": INTELLIGENCE_BACKGROUND,
            "--foreground": String(getPaletteValue(colorV2.system.gray, 800))
          };
        }
        if (v !== SYSTEM_VARIANT_PENDING) {
          return {
            "--background": rgb(colorV2.system[v], 50),
            "--foreground": String(getPaletteValue(colorV2.system[v], 600))
          };
        }
      }
      default:
        return {
          "--background": rgb(colorV2.system.gray, 100),
          "--foreground": String(getPaletteValue(colorV2.system.gray, 950)),
          // The default appearance has a fixed text color when disabled.
          '&[aria-disabled="true"]': {
            color: rgb(colorV2.system.gray, 400)
          }
        };
    }
  }
);
function isVariantPropValid(prop) {
  return isPropValid4(prop) && prop !== "variant";
}
var Bullet = styled19("circle", { shouldForwardProp: isVariantPropValid })(
  {
    label: "tag-bullet"
  },
  ({ theme, variant = DEFAULT_SEVERITY_VARIANT }) => {
    const { colorV2 } = theme;
    return {
      fill: rgb(colorV2.severity[variant], 600)
    };
  }
);

// lib/components/Tag/TagV2.tsx
var SeverityBullet = ({ variant }) => {
  return /* @__PURE__ */ React24.createElement(
    "svg",
    {
      viewBox: "0 0 16 16",
      width: "64",
      height: "64"
    },
    /* @__PURE__ */ React24.createElement(
      Bullet,
      {
        cx: "8",
        cy: "8",
        r: "3",
        variant
      }
    )
  );
};
var Tag2 = forwardRef20(
  ({
    asChild = false,
    children,
    disabled = false,
    size = "medium",
    startIcon = null,
    endIcon = null,
    type,
    variant,
    ...delegated
  }, ref) => {
    const Comp = asChild ? Wrapper2.withComponent(Slot4) : Wrapper2;
    const startElement = startIcon ?? (type === "severity" ? /* @__PURE__ */ React24.createElement(SeverityBullet, { variant }) : null);
    return /* @__PURE__ */ React24.createElement(
      Comp,
      {
        ref,
        size,
        ...disabled && { "aria-disabled": true },
        type,
        variant,
        ...delegated
      },
      startElement,
      /* @__PURE__ */ React24.createElement(Slottable2, null, children),
      endIcon
    );
  }
);

// lib/components/Tag/Tag.tsx
var Tag3 = forwardRef21(
  ({ legacy = false, ...delegated }, ref) => {
    if (legacy) {
      return /* @__PURE__ */ React25.createElement(
        LegacyTag_default,
        {
          ref,
          ...delegated
        }
      );
    }
    return /* @__PURE__ */ React25.createElement(
      Tag2,
      {
        ref,
        ...delegated
      }
    );
  }
);

// lib/components/Dropdown/Dropdown.tsx
function formatCreateLabel(inputValue) {
  return `Create new: ${inputValue}`;
}
function getComponent({ async, creatable }) {
  if (creatable) {
    return async ? AsyncCreatableSelect : CreatableSelect;
  }
  return async ? AsyncSelect : Select;
}
function getNewOptionData(inputValue, label) {
  return { title: label, value: inputValue };
}
function getOptionTitle(option) {
  return option.title;
}
function ClearIndicator(props) {
  return /* @__PURE__ */ React26.createElement(SelectComponents.ClearIndicator, { ...props }, /* @__PURE__ */ React26.createElement(UilTimes, null));
}
function DropdownIndicator(props) {
  return /* @__PURE__ */ React26.createElement(SelectComponents.DropdownIndicator, { ...props }, /* @__PURE__ */ React26.createElement(UilAngleDown2, null));
}
function DropdownSelectToggle2({ isSelected }) {
  const icon = isSelected ? /* @__PURE__ */ React26.createElement(UilTimes, { size: "1.25em" }) : /* @__PURE__ */ React26.createElement(UilPlus, { size: "1.25em" });
  return /* @__PURE__ */ React26.createElement(DropdownSelectToggle, { isSelected }, icon);
}
function Hint() {
  return /* @__PURE__ */ React26.createElement(
    HintTypography,
    {
      as: "p",
      level: "9"
    },
    "Press ",
    /* @__PURE__ */ React26.createElement(UilEnter, null),
    " (",
    /* @__PURE__ */ React26.createElement("kbd", null, "enter"),
    ") or ",
    /* @__PURE__ */ React26.createElement("kbd", null, "tab"),
    " to select, ",
    /* @__PURE__ */ React26.createElement(UilArrowUp, null),
    /* @__PURE__ */ React26.createElement(UilArrowDown, null),
    " to navigate, ",
    /* @__PURE__ */ React26.createElement("kbd", null, "esc"),
    " to dismiss."
  );
}
function MenuList(props) {
  const { children, selectProps } = props;
  return /* @__PURE__ */ React26.createElement(SelectComponents.MenuList, { ...props }, selectProps.hint ? /* @__PURE__ */ React26.createElement(Hint, null) : null, children);
}
function MultiValueWrapper2(props) {
  const { getValue, selectProps } = props;
  const { components, formatOptionLabel, getOptionLabel, getOptionValue, innerRef, inputValue, isDisabled } = selectProps;
  const values = getValue();
  if (values.length === 0) {
    return null;
  }
  return /* @__PURE__ */ React26.createElement(MultiValueWrapper, null, /* @__PURE__ */ React26.createElement(
    Typography_default,
    {
      as: "p",
      level: "8",
      strength: "80%"
    },
    selectProps.multiValueLabel ?? "Added Options"
  ), /* @__PURE__ */ React26.createElement("ul", null, values.map((opt, index) => /* @__PURE__ */ React26.createElement(
    SelectComponents.MultiValue,
    {
      components: {
        Container: components.MultiValueContainer ?? SelectComponents.MultiValueContainer,
        Label: components.MultiValueLabel ?? SelectComponents.MultiValueLabel,
        Remove: components.MultiValueRemove ?? SelectComponents.MultiValueRemove
      },
      data: opt,
      index,
      innerProps: {},
      isDisabled,
      isFocused: false,
      key: `${getOptionLabel(opt)}-${getOptionValue(opt)}`,
      removeProps: {
        onClick: () => {
          var _a2;
          return (_a2 = innerRef.current) == null ? void 0 : _a2.removeValue(opt);
        },
        onTouchEnd: () => {
          var _a2;
          return (_a2 = innerRef.current) == null ? void 0 : _a2.removeValue(opt);
        },
        onMouseDown: (e) => e.preventDefault()
      },
      ...props
    },
    (formatOptionLabel == null ? void 0 : formatOptionLabel(opt, { context: "value", inputValue, selectValue: [] })) ?? getOptionLabel(opt)
  ))));
}
function MultiSelectContainer({ children, className, innerProps, ...commonProps }) {
  return /* @__PURE__ */ React26.createElement(
    SelectComponents.SelectContainer,
    {
      className,
      innerProps,
      ...commonProps
    },
    children,
    /* @__PURE__ */ React26.createElement(MultiValueWrapper2, { ...commonProps })
  );
}
function MultiValueContainer(props) {
  return /* @__PURE__ */ React26.createElement(
    Tag3,
    {
      size: "small",
      type: "system",
      variant: "pending",
      asChild: true
    },
    /* @__PURE__ */ React26.createElement("li", null, /* @__PURE__ */ React26.createElement(SelectComponents.MultiValueContainer, { ...props }))
  );
}
function MultiValueLabel(props) {
  const { subtitle: _, ...option } = props.data;
  return /* @__PURE__ */ React26.createElement(SelectComponents.MultiValueLabel, { ...props }, /* @__PURE__ */ React26.createElement(DropdownOption, { option }));
}
function MultiValueRemove(props) {
  return /* @__PURE__ */ React26.createElement(SelectComponents.MultiValueRemove, { ...props }, /* @__PURE__ */ React26.createElement(UilTimes, { size: "1.25em" }));
}
function Option(props) {
  const { data, isMulti, isSelected } = props;
  return /* @__PURE__ */ React26.createElement(SelectComponents.Option, { ...props }, /* @__PURE__ */ React26.createElement(DropdownOption, { option: data }), isMulti ? /* @__PURE__ */ React26.createElement(DropdownSelectToggle2, { isSelected }) : null);
}
function SingleValue(props) {
  const { subtitle: _, ...option } = props.data;
  return /* @__PURE__ */ React26.createElement(SelectComponents.SingleValue, { ...props }, /* @__PURE__ */ React26.createElement(DropdownOption, { option }));
}
var COMPONENT_OVERRIDES = {
  ClearIndicator,
  DropdownIndicator,
  MenuList,
  MultiValueContainer,
  MultiValueLabel,
  MultiValueRemove,
  Option,
  SingleValue
};
var Dropdown = forwardRef22(
  ({
    components,
    creatable = false,
    disabled = false,
    "aria-invalid": invalid = false,
    isMulti = false,
    options,
    required = false,
    ...delegated
  }, ref) => {
    const innerRef = useComposedRefs(ref);
    const theme = useTheme();
    const Comp = getComponent({ async: !options, creatable });
    return /* @__PURE__ */ React26.createElement(
      Comp,
      {
        classNamePrefix: "dropdown",
        closeMenuOnSelect: !isMulti,
        components: {
          ...COMPONENT_OVERRIDES,
          ...isMulti && { SelectContainer: MultiSelectContainer },
          ...components
        },
        controlShouldRenderValue: !isMulti,
        getOptionLabel: getOptionTitle,
        hideSelectedOptions: !isMulti,
        isClearable: !required,
        isDisabled: disabled,
        isMulti,
        menuPortalTarget: document.body,
        options,
        placeholder: "Type to search or select",
        ref: innerRef,
        styles: getReactSelectStyles(theme),
        unstyled: true,
        ...creatable && {
          formatCreateLabel,
          getNewOptionData
        },
        ...invalid && {
          "aria-invalid": true,
          className: "dropdown-is-invalid"
        },
        ...delegated,
        innerRef
      }
    );
  }
);

// lib/components/RadioButton/RadioButton.tsx
import React27, { forwardRef as forwardRef23 } from "react";
import * as RadioGroup2 from "@radix-ui/react-radio-group";

// lib/components/RadioButton/RadioButton.styles.tsx
import styled20 from "@emotion/styled";
import isPropValid5 from "@emotion/is-prop-valid";
import * as RadioGroup from "@radix-ui/react-radio-group";
var FormContainer = styled20(RadioGroup.Root)(
  {
    display: "flex"
  },
  ({ orientation }) => ({
    flexDirection: orientation === "horizontal" ? "row" : "column",
    gap: orientation === "horizontal" ? "32px" : "24px"
  })
);
var RadioButtonContainer = styled20("label", { shouldForwardProp: isPropValid5 })(
  {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    ".RadioGroupItem": {
      transform: "translateY(0.125em)",
      flexShrink: 0,
      border: "2px solid currentColor",
      borderRadius: "9999px",
      padding: 0,
      width: "1em",
      height: "1em",
      cursor: "pointer",
      fontSize: "inherit",
      '&[data-state="checked"]': {
        backgroundColor: "currentColor"
      },
      /**
       * Safari only: the vertical alignment is off when the radio is not checked. Therefore, when
       * the button is empty, add a pseudo-element.
       */
      "&:empty::after": {
        content: '""',
        display: "block",
        width: "100%",
        height: "100%"
      }
    },
    ".RadioGroupIndicator": {
      display: "block",
      width: "100%",
      height: "100%",
      border: "2.5px solid currentColor",
      borderRadius: "9999px",
      backgroundColor: "white"
    },
    // The label should take up all remaining horizontal space.
    "& > div": {
      flexGrow: 1
    }
  },
  ({ labelPosition }) => {
    if (labelPosition === "left") {
      return {
        flexDirection: "row-reverse",
        justifyContent: "space-between"
      };
    }
    return {};
  },
  ({ theme }) => ({
    ".RadioGroupItem": {
      color: rgb3(theme.color.system.gray, "60%"),
      backgroundColor: rgb3(theme.color.system.white),
      '&[data-state="checked"]': {
        color: rgb3(theme.color.brand.primary)
      },
      "&:disabled, &:disabled + *": {
        color: rgb3(theme.color.system.gray, "20%")
      }
    }
  })
);

// lib/components/RadioButton/RadioButton.tsx
var RadioButton = forwardRef23(
  ({ disabled, value, label, labelPosition = "right", ...restOfProps }, ref) => {
    return /* @__PURE__ */ React27.createElement(
      RadioButtonContainer,
      {
        labelPosition,
        ...restOfProps
      },
      /* @__PURE__ */ React27.createElement(
        RadioGroup2.Item,
        {
          className: "RadioGroupItem",
          disabled: !!disabled,
          ref,
          value
        },
        /* @__PURE__ */ React27.createElement(RadioGroup2.Indicator, { className: "RadioGroupIndicator" })
      ),
      label ? /* @__PURE__ */ React27.createElement("div", null, label) : null
    );
  }
);

// lib/components/RadioButton/RadioButtonGroup.tsx
import React28, { forwardRef as forwardRef24 } from "react";
import { Slot as Slot5 } from "@radix-ui/react-slot";
var RadioButtonGroup = forwardRef24(
  ({
    onChange,
    orientation = "vertical",
    defaultValue,
    label,
    asChild,
    ...restOfProps
  }, ref) => {
    const Comp = asChild ? FormContainer.withComponent(Slot5) : FormContainer;
    return /* @__PURE__ */ React28.createElement(
      Comp,
      {
        defaultValue,
        "aria-label": label,
        onValueChange: onChange,
        orientation,
        ref,
        ...restOfProps
      }
    );
  }
);

// lib/components/Card/Card.tsx
import React29, { forwardRef as forwardRef25 } from "react";

// lib/components/Card/Card.styles.tsx
import styled21 from "@emotion/styled";
import isPropValid6 from "@emotion/is-prop-valid";
var Card = styled21.div(
  {
    label: "card",
    padding: "36px",
    borderRadius: "8px"
  },
  ({ theme }) => ({
    boxShadow: `${base_default.boxShadow.drop} ${translucent(theme.color.shadow.drop, "10%")}`,
    border: `1px solid ${rgb3(theme.color.system.border, "60%")}`,
    backgroundColor: rgb3(theme.color.system.white)
  })
);
var CardTitle = styled21.div({
  label: "card-header",
  display: "flex",
  whiteSpace: "nowrap",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap",
  svg: {
    flexShrink: 0
  }
});
var CardContent = styled21.div({
  label: "card-content",
  ".card > &:not(:first-child)": {
    marginTop: "12px"
  }
});
var CardActions = styled21("div", {
  shouldForwardProp: (prop) => isPropValid6(prop) && !["end"].includes(prop)
})(
  {
    label: "card-actions",
    flex: "1",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    ".card > &:not(:first-child)": {
      marginTop: "24px"
    }
  },
  ({ end }) => ({
    justifyContent: end ? "flex-end" : "flex-start"
  })
);

// lib/components/Card/Card.tsx
var Card2 = forwardRef25((props, ref) => {
  return /* @__PURE__ */ React29.createElement(
    Card,
    {
      ref,
      className: "card",
      ...props
    }
  );
});
var Card_default = Card2;

// lib/components/Card/CardContent.tsx
var CardContent_default = CardContent;

// lib/components/Card/CardTitle.tsx
var CardTitle_default = CardTitle;

// lib/components/Card/CardActions.tsx
var CardActions_default = CardActions;

// lib/components/Checkbox/Checkbox.tsx
import React30, { useCallback, useState as useState2 } from "react";
import { UilCheck as CheckIcon, UilMinus as MinusIcon } from "@iconscout/react-unicons";

// lib/components/Checkbox/Checkbox.styles.tsx
import styled22 from "@emotion/styled";
import * as CheckboxBase from "@radix-ui/react-checkbox";
var Root17 = styled22(CheckboxBase.Root)(
  {
    label: "checkbox",
    borderRadius: "2px",
    alignItems: "center",
    cursor: "pointer",
    display: "flex",
    height: "1rem",
    justifyContent: "center",
    outline: "none",
    padding: 0,
    width: "1rem"
  },
  (props) => {
    const { theme } = props;
    return {
      backgroundColor: rgb3(theme.color.system.white),
      border: `1px solid ${flat(theme.color.system.gray, "40%")}`,
      boxShadow: `${theme.boxShadow.inner} ${translucent(theme.color.shadow.inner, "10%")}`,
      // Normal Checked State
      '&[data-state="checked"]': {
        backgroundColor: flat(theme.color.brand.primary),
        borderWidth: 0
      },
      // Indeterminate State
      '&[data-state="indeterminate"]': {
        backgroundColor: flat(theme.color.brand.primary),
        borderWidth: 0
      },
      // Disabled State
      "&[disabled]": {
        background: flat(theme.color.system.gray, "20%"),
        borderColor: flat(theme.color.system.gray, "20%"),
        boxShadow: "none",
        pointerEvents: "none",
        // Disabled Checked
        '&[data-state="checked"]': {
          border: "none"
        },
        // Disabled Indeterminate
        '&[data-state="indeterminate"]': {
          border: "none"
        },
        // Disabled Unchecked
        '&[data-state="unchecked"]': {
          background: rgb3(theme.color.system.white)
        }
      }
    };
  }
);
var Indicator3 = styled22(CheckboxBase.Indicator)({
  alignItems: "center",
  color: "white",
  display: "flex"
});

// lib/components/Checkbox/Checkbox.tsx
function Checkbox(props) {
  const { checked, defaultChecked, onCheckedChange } = props;
  const [state, setState] = useState2(checked ?? defaultChecked);
  const handleCheckedChange = useCallback(
    (nextChecked) => {
      setState(nextChecked);
      if (onCheckedChange)
        onCheckedChange(nextChecked);
    },
    [onCheckedChange, setState]
  );
  const finalState = checked ?? state;
  return /* @__PURE__ */ React30.createElement(
    Root17,
    {
      ...props,
      onCheckedChange: handleCheckedChange
    },
    /* @__PURE__ */ React30.createElement(Indicator3, null, finalState === true && /* @__PURE__ */ React30.createElement(CheckIcon, { size: 16 }), finalState === "indeterminate" && /* @__PURE__ */ React30.createElement(MinusIcon, { size: 16 }))
  );
}
var Checkbox_default = Checkbox;

// lib/components/Color/Color.tsx
import React32 from "react";

// lib/components/Color/Color.styles.tsx
import styled23 from "@emotion/styled";
var Root18 = styled23.div({
  label: "color",
  display: "grid",
  gridAutoFlow: "column",
  gridAutoColumns: "1fr",
  width: "100%"
});

// lib/components/Color/Swatch.tsx
import React31 from "react";

// lib/components/Color/Swatch.styles.tsx
import styled24 from "@emotion/styled";
var Root19 = styled24.div(
  {
    display: "inline-grid",
    placeItems: "center",
    height: "64px",
    "&:first-of-type": {
      borderRadius: "8px 0px 0px 8px"
    },
    "&:last-of-type": {
      borderRadius: "0px 8px 8px 0px"
    }
  },
  ({ rgb: rgb4 }) => ({
    backgroundColor: rgb(rgb4)
  })
);

// lib/components/Color/Swatch.tsx
function Swatch({ idx: idx2, rgb: rgb4 }) {
  const hexValue = rgb4.reduce((value, rgbChannel) => {
    const hexChannel = Number.parseInt(String(rgbChannel), 10).toString(16).padStart(2, "0").toUpperCase();
    return `${value}${hexChannel}`;
  }, "#");
  return /* @__PURE__ */ React31.createElement(
    Root19,
    {
      rgb: rgb4,
      title: `${idx2} - ${hexValue}`
    },
    idx2
  );
}

// lib/components/Color/Color.tsx
function sortByValue(a, b) {
  return a[1] - b[1];
}
function Color({ palette = {} }) {
  const swatches = Array.from(Object.entries(palette).sort(sortByValue), ([idx2, rgb4]) => /* @__PURE__ */ React32.createElement(
    Swatch,
    {
      idx: idx2,
      key: idx2,
      rgb: rgb4
    }
  ));
  return /* @__PURE__ */ React32.createElement(Root18, null, swatches);
}

// lib/components/Field/Field.tsx
import React34, { forwardRef as forwardRef26 } from "react";
import { UilInfoCircle } from "@iconscout/react-unicons";
import * as RadixLabel from "@radix-ui/react-label";
import { Slot as Slot6 } from "@radix-ui/react-slot";

// lib/components/Field/Field.styles.js
import styled25 from "@emotion/styled";
var Root20 = styled25.div(
  {
    label: "field",
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  ({ theme }) => ({
    color: rgb3(theme.color.system.gray, "80%"),
    '&[aria-disabled="true"]': {
      color: rgb3(theme.color.system.gray, "40%")
    }
  })
);
var Label2 = styled25(Typography_default)({
  label: "label",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  color: "inherit"
});
var HintTrigger = styled25.span({
  label: "hint",
  flexShrink: 0,
  lineHeight: 0
});
var MandatoryLabel = styled25(Typography_default)({}, ({ theme }) => ({
  label: "mandatory",
  backgroundColor: rgb(theme.colorV2.system.negative, 600),
  lineHeight: 0,
  width: "0.2em",
  height: "0.2em",
  display: "inline-block",
  borderRadius: 9999
}));
var Secondary = styled25(Typography_default)(
  {
    label: "secondary",
    color: "inherit"
  },
  ({ theme }) => ({
    '[aria-invalid="true"] > &': {
      color: rgb3(theme.color.system.negative)
    }
  })
);

// lib/components/Popover/PopoverProvider.tsx
import React33 from "react";
import * as Tooltip2 from "@radix-ui/react-tooltip";
function PopoverProvider(props) {
  return /* @__PURE__ */ React33.createElement(
    Tooltip2.Provider,
    {
      delayDuration: 0,
      ...props
    }
  );
}
var PopoverProvider_default = PopoverProvider;

// lib/components/Field/Field.tsx
var HINT_TRIGGER = /* @__PURE__ */ React34.createElement(HintTrigger, null, /* @__PURE__ */ React34.createElement(UilInfoCircle, { size: "1.25em" }));
function ControlSlot(props) {
  return /* @__PURE__ */ React34.createElement(Slot6, { ...props });
}
var Field = forwardRef26(
  ({ control, description, disabled = false, error = false, hint, label, required = false, ...delegated }, ref) => {
    const controlId = useId();
    const secondaryId = useId();
    const secondary = error === false ? description : error;
    const hintElement = hint ? /* @__PURE__ */ React34.createElement(
      Popover_default,
      {
        side: "top",
        trigger: HINT_TRIGGER
      },
      hint
    ) : null;
    return /* @__PURE__ */ React34.createElement(
      Root20,
      {
        ref,
        ...delegated,
        ...disabled && { "aria-disabled": true },
        ...error && { "aria-invalid": true }
      },
      /* @__PURE__ */ React34.createElement(
        RadixLabel.Root,
        {
          htmlFor: controlId,
          asChild: true
        },
        /* @__PURE__ */ React34.createElement(
          Label2,
          {
            as: "label",
            level: "9"
          },
          hintElement,
          /* @__PURE__ */ React34.createElement("span", null, label, required ? /* @__PURE__ */ React34.createElement(React34.Fragment, null, " ", /* @__PURE__ */ React34.createElement(
            MandatoryLabel,
            {
              "aria-label": "Required",
              as: "sup",
              color: "negative",
              level: "7"
            }
          )) : null)
        )
      ),
      /* @__PURE__ */ React34.createElement(
        ControlSlot,
        {
          disabled,
          id: controlId,
          required,
          ...error !== false && { "aria-invalid": true },
          ...secondary && error !== false && { "aria-errormessage": secondaryId },
          ...secondary && error === false && { "aria-describedby": secondaryId }
        },
        control
      ),
      secondary ? /* @__PURE__ */ React34.createElement(
        Secondary,
        {
          className: "secondary",
          id: secondaryId,
          level: "9"
        },
        secondary
      ) : null
    );
  }
);

// lib/components/Field/TextField.tsx
import React35, { forwardRef as forwardRef27 } from "react";

// lib/components/Field/TextField.styles.js
import styled26 from "@emotion/styled";
import { UilExclamationCircle } from "@iconscout/react-unicons";
var Wrapper3 = styled26.div(
  {
    label: "wrapper",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    /* Don't allow start and end elements to shrink. */
    "& > *": {
      flexShrink: 0
    },
    border: "1px solid transparent",
    borderRadius: "4px",
    paddingRight: "12px",
    paddingLeft: "12px"
  },
  ({ theme }) => ({
    borderColor: rgb3(theme.color.system.border, "60%"),
    backgroundColor: rgb3(theme.color.system.white),
    boxShadow: `inset 0 2px 2px 0 ${rgb3(theme.color.shadow.inner, "10%")}`,
    color: rgb3(theme.color.system.gray, "60%"),
    "&:focus-within, &:hover": {
      borderColor: rgb3(theme.color.brand.primary),
      boxShadow: "none"
    },
    "&:focus-within": {
      boxShadow: `0 0 8px 0 ${rgb3(theme.color.brand.lighter)}`
    },
    '&[aria-invalid="true"]': {
      borderColor: rgb3(theme.color.system.negative)
    },
    '&[aria-disabled="true"]': {
      cursor: "not-allowed",
      borderColor: "transparent",
      backgroundColor: rgb3(theme.color.system.gray, "10%"),
      boxShadow: "none",
      color: rgb3(theme.color.system.gray, "40%")
    }
  })
);
var Indicator4 = styled26(UilExclamationCircle)(({ theme }) => ({
  label: "indicator",
  color: rgb3(theme.color.system.negative)
}));
var Input = styled26.input(
  {
    label: "input",
    all: "unset",
    flex: 1,
    minWidth: 0,
    paddingTop: 7,
    paddingBottom: 7,
    lineHeight: 1.5
  },
  ({ theme }) => ({
    color: rgb3(theme.color.system.gray),
    "&::placeholder": {
      color: rgb3(theme.color.system.gray, "60%")
    },
    "&:disabled, &:disabled::placeholder": {
      color: rgb3(theme.color.system.gray, "40%")
    }
  })
);

// lib/components/Field/TextField.tsx
var TextField = forwardRef27(
  ({ className, disabled, end, "aria-invalid": invalid = false, start, ...delegated }, ref) => {
    const endElement = end ?? (invalid ? /* @__PURE__ */ React35.createElement(Indicator4, null) : null);
    return /* @__PURE__ */ React35.createElement(
      Wrapper3,
      {
        className,
        ...disabled && { "aria-disabled": true },
        ...invalid && { "aria-invalid": true }
      },
      start,
      /* @__PURE__ */ React35.createElement(
        Input,
        {
          disabled,
          ref,
          ...delegated
        }
      ),
      endElement
    );
  }
);

// lib/components/FileUpload/FileUpload.tsx
import React36, { forwardRef as forwardRef28, useCallback as useCallback2, useImperativeHandle as useImperativeHandle2, useState as useState3 } from "react";
import { useDropzone } from "react-dropzone";
import { UilArrowToBottom, UilCheckCircle, UilExclamationCircle as UilExclamationCircle2, UilUpload } from "@iconscout/react-unicons";

// lib/components/FileUpload/FileUpload.styles.ts
import styled27 from "@emotion/styled";
var Action = styled27.span(
  {
    label: "file-upload-action",
    textDecoration: "underline"
  },
  ({ theme }) => ({
    color: rgb(theme.colorV2.brand)
  })
);
var FileUpload = styled27.div(
  {
    label: "file-upload",
    display: "grid",
    gridTemplateAreas: '"content"',
    alignItems: "stretch",
    fontWeight: 500,
    textAlign: "center",
    '&[aria-disabled="true"]': {
      cursor: "not-allowed",
      "& > *": {
        opacity: 0.5
      }
    },
    "& > *": {
      gridArea: "content"
    }
  },
  ({ theme }) => ({
    fontSize: theme.fontSize["8"]
  })
);
var Zone = styled27.div(
  {
    label: "file-upload-zone",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    borderRadius: 8,
    padding: 32
  },
  ({ theme }) => ({
    border: `2px dashed ${rgb(theme.colorV2.system.border)}`,
    backgroundColor: rgb(theme.colorV2.system.gray, 50),
    '&[data-state="accepted"]': {
      backgroundColor: rgb(theme.colorV2.system.white),
      "& > svg": {
        color: rgb(theme.colorV2.system.positive)
      }
    },
    '&[data-state="active"]': {
      borderColor: "currentcolor",
      backgroundColor: rgb(theme.colorV2.brand, 50),
      color: rgb(theme.colorV2.brand)
    },
    '&[data-state="rejected"]': {
      backgroundColor: rgb(theme.colorV2.system.white),
      "& > svg": {
        color: rgb(theme.colorV2.system.negative)
      }
    }
  })
);

// lib/components/FileUpload/FileUpload.tsx
var DEFAULT_MAX_SIZE = 25 * 1e3 * 1e3;
var STATE = {
  ACCEPTED: "accepted",
  REJECTED: "rejected"
};
var listFormatter = new Intl.ListFormat("en", { type: "disjunction" });
var sizeFormatter = new Intl.NumberFormat("en", {
  notation: "compact",
  // Upsizes unit based on value.
  style: "unit",
  unit: "byte",
  unitDisplay: "narrow"
});
function getFileExtensions(accept) {
  const values = Object.values(accept ?? {}).flatMap((extensions) => {
    return extensions.map((extension) => extension.slice(1).toUpperCase());
  }).sort();
  return new Set(values);
}
function AcceptedZone() {
  return /* @__PURE__ */ React36.createElement(Zone, { "data-state": "accepted" }, /* @__PURE__ */ React36.createElement(UilCheckCircle, null), /* @__PURE__ */ React36.createElement("p", null, "File(s) successfully uploaded. ", /* @__PURE__ */ React36.createElement(Action, null, "Upload another file")));
}
function ActiveZone() {
  return /* @__PURE__ */ React36.createElement(Zone, { "data-state": "active" }, /* @__PURE__ */ React36.createElement(UilArrowToBottom, null), /* @__PURE__ */ React36.createElement("p", null, "Drop here\u2026"));
}
function RejectedZone({ error }) {
  const inner = error ?? /* @__PURE__ */ React36.createElement(React36.Fragment, null, "There was an error uploading your file(s). ", /* @__PURE__ */ React36.createElement(Action, null, "Try again"));
  return /* @__PURE__ */ React36.createElement(Zone, { "data-state": "rejected" }, /* @__PURE__ */ React36.createElement(UilExclamationCircle2, null), /* @__PURE__ */ React36.createElement("p", null, inner));
}
function DropZone({ accept, maxSize }) {
  return /* @__PURE__ */ React36.createElement(Zone, null, /* @__PURE__ */ React36.createElement(UilUpload, null), /* @__PURE__ */ React36.createElement("p", null, "Drag and Drop or ", /* @__PURE__ */ React36.createElement(Action, null, "Browse to Upload")), /* @__PURE__ */ React36.createElement(
    RestrictionTag,
    {
      extensions: getFileExtensions(accept),
      maxSize
    }
  ));
}
function RestrictionTag({ extensions, maxSize }) {
  const extensionString = extensions.size > 0 ? listFormatter.format(extensions) + (extensions.size === 1 ? " only" : "") : "";
  const maxSizeString = maxSize ? (extensionString === "" ? "Up to " : " up to ") + sizeFormatter.format(maxSize) : "";
  return /* @__PURE__ */ React36.createElement(Tag3, { type: "brand" }, extensionString, maxSizeString);
}
var FileUpload2 = forwardRef28(
  ({ accept, disabled = false, maxSize = DEFAULT_MAX_SIZE, onDrop, ...delegated }, ref) => {
    const [latestDropState, setLatestDropState] = useState3(null);
    const wrappedOnDrop = useCallback2(
      (acceptedFiles, rejectedFiles, evt) => {
        const isAccepted = rejectedFiles.length === 0;
        setLatestDropState(isAccepted ? STATE.ACCEPTED : STATE.REJECTED);
        return onDrop == null ? void 0 : onDrop(isAccepted ? acceptedFiles : [], rejectedFiles, evt);
      },
      [onDrop, setLatestDropState]
    );
    const { getInputProps, getRootProps, isDragActive, isDragReject, rootRef } = useDropzone({
      accept,
      disabled,
      maxSize,
      onDrop: wrappedOnDrop,
      ...delegated
    });
    useImperativeHandle2(ref, () => rootRef.current, [rootRef]);
    const isDragAccept = isDragActive && !isDragReject;
    return /* @__PURE__ */ React36.createElement(
      FileUpload,
      {
        ...getRootProps({
          "aria-disabled": disabled ? true : void 0,
          ...delegated
        })
      },
      /* @__PURE__ */ React36.createElement(
        DropZone,
        {
          accept,
          maxSize
        }
      ),
      latestDropState === STATE.ACCEPTED ? /* @__PURE__ */ React36.createElement(AcceptedZone, null) : null,
      latestDropState === STATE.REJECTED ? /* @__PURE__ */ React36.createElement(RejectedZone, null) : null,
      isDragAccept ? /* @__PURE__ */ React36.createElement(ActiveZone, null) : null,
      isDragReject ? /* @__PURE__ */ React36.createElement(RejectedZone, { error: "The selected file(s) cannot be accepted" }) : null,
      /* @__PURE__ */ React36.createElement("input", { ...getInputProps() })
    );
  }
);

// lib/components/IconButton/IconButton.tsx
import React37, { forwardRef as forwardRef29 } from "react";
import { Slot as Slot7, Slottable as Slottable3 } from "@radix-ui/react-slot";

// lib/components/IconButton/IconButton.styles.ts
import styled28 from "@emotion/styled";
var IconButton = styled28.button(
  {
    label: "iconbutton",
    display: "inline-block",
    border: "unset",
    borderRadius: 9999,
    padding: 4,
    backgroundColor: "unset",
    cursor: "pointer",
    lineHeight: 0
    // Let icon determine this instead.
  },
  ({ theme, variant }) => {
    const baseColor = variant === "brand" ? theme.colorV2.brand : theme.colorV2.system[variant];
    return {
      color: rgb(theme.colorV2.system.gray, 800),
      "&:focus, &:hover": {
        backgroundColor: rgb(baseColor, 50),
        ...variant !== "gray" && { color: rgb(baseColor) }
      }
    };
  }
);

// lib/components/IconButton/IconButton.tsx
var IconButton2 = forwardRef29(
  ({ asChild = false, children, icon, label, variant = "gray", ...delegated }, ref) => {
    const Comp = asChild ? IconButton.withComponent(Slot7) : IconButton;
    return /* @__PURE__ */ React37.createElement(
      Comp,
      {
        "aria-label": label,
        ref,
        type: "button",
        variant,
        ...delegated
      },
      asChild ? /* @__PURE__ */ React37.createElement(Slottable3, null, children) : null,
      icon
    );
  }
);

// lib/components/Modal/Modal.tsx
import React38, { forwardRef as forwardRef30 } from "react";
import { UilTimes as XIcon } from "@iconscout/react-unicons";
import * as RadixDialog3 from "@radix-ui/react-dialog";

// lib/components/Modal/Modal.styles.tsx
import styled29 from "@emotion/styled";
import isPropValid7 from "@emotion/is-prop-valid";
import * as RadixDialog2 from "@radix-ui/react-dialog";
var Overlay6 = styled29(RadixDialog2.Overlay)({
  zIndex: 25e3
});
var Content11 = styled29(RadixDialog2.Content, { shouldForwardProp: isPropValid7 })(
  {
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    backgroundColor: "white",
    borderRadius: "8px",
    borderStyle: "solid",
    borderWidth: "1px",
    padding: "32px",
    maxHeight: "calc(100% - 64px)",
    width: "680px"
  },
  ({ sticky, theme }) => ({
    borderColor: flat(theme.color.system.gray, "20%"),
    boxShadow: `${theme.boxShadow.drop} ${translucent(theme.color.shadow.drop, "10%")}`,
    ...sticky && {
      position: "relative",
      overflow: "hidden"
    }
  })
);
var Header3 = styled29.div({
  alignItems: "center",
  display: "flex",
  gap: "10px",
  paddingBottom: "12px"
});
var Title3 = styled29(Heading_default)(({ theme }) => ({
  color: flat(theme.color.system.gray, "100%")
}));
var HideButton2 = styled29(RadixDialog2.Close)(
  {
    alignItems: "center",
    appearance: "none",
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "inline-flex",
    marginLeft: "auto"
  },
  ({ theme }) => ({
    color: flat(theme.color.system.gray, "100%")
  })
);
var Preamble = styled29.div({
  paddingBottom: "24px",
  "&:empty": {
    display: "none"
  }
});
var Body = styled29.div({
  overflow: "auto"
});
var Actions3 = styled29.div({
  alignItems: "center",
  display: "flex",
  flexDirection: "row-reverse",
  flexWrap: "wrap",
  gap: "12px",
  paddingTop: "48px"
});
var Styles3 = {
  Actions: Actions3,
  Body,
  Content: Content11,
  Header: Header3,
  HideButton: HideButton2,
  Overlay: Overlay6,
  Portal: RadixDialog2.Portal,
  Preamble,
  Root: RadixDialog2.Root,
  Title: Title3
};

// lib/components/Modal/ModalContext.tsx
import { createContext } from "react";
var ModalContext = createContext(null);

// lib/components/Modal/Modal.tsx
var NOOP = () => null;
var Modal = forwardRef30(
  ({
    actions,
    children,
    dialog,
    subtitle,
    title,
    titleIcon,
    container,
    showCloseIcon = true,
    shouldHideOnOverlayClick = true,
    sticky = false,
    ...restProps
  }, ref) => {
    const subtitleElement = subtitle ? /* @__PURE__ */ React38.createElement(Typography_default, { level: "7" }, subtitle) : null;
    const titleIconElement = titleIcon ?? null;
    const handleOpenChange = (newOpenState) => newOpenState ? dialog.show : dialog.hide;
    return /* @__PURE__ */ React38.createElement(
      Styles3.Root,
      {
        open: dialog.isOpen,
        onOpenChange: handleOpenChange
      },
      /* @__PURE__ */ React38.createElement(ModalContext.Provider, { value: dialog }, /* @__PURE__ */ React38.createElement(Styles3.Portal, { container }, /* @__PURE__ */ React38.createElement(Styles3.Overlay, { asChild: true }, /* @__PURE__ */ React38.createElement(Overlay4, null, /* @__PURE__ */ React38.createElement(
        Styles3.Content,
        {
          onEscapeKeyDown: shouldHideOnOverlayClick ? dialog.hide : NOOP,
          onPointerDownOutside: shouldHideOnOverlayClick ? dialog.hide : NOOP,
          ref,
          sticky,
          ...restProps
        },
        /* @__PURE__ */ React38.createElement(Styles3.Header, null, titleIconElement, /* @__PURE__ */ React38.createElement(RadixDialog3.Title, { asChild: true }, /* @__PURE__ */ React38.createElement(
          Styles3.Title,
          {
            as: "h2",
            level: "4"
          },
          title
        )), showCloseIcon === true && /* @__PURE__ */ React38.createElement(
          Styles3.HideButton,
          {
            onClick: dialog.hide,
            type: "button"
          },
          /* @__PURE__ */ React38.createElement(XIcon, { size: 32 })
        )),
        /* @__PURE__ */ React38.createElement(Styles3.Body, null, /* @__PURE__ */ React38.createElement(Styles3.Preamble, null, subtitleElement), children),
        /* @__PURE__ */ React38.createElement(Styles3.Actions, null, actions)
      )))))
    );
  }
);

// lib/components/Modal/ModalButton.tsx
import React39, { forwardRef as forwardRef31, useCallback as useCallback3, useContext } from "react";
function ModalButton(props, ref) {
  const { autoHide = false, onClick, ...restProps } = props;
  const dialog = useContext(ModalContext);
  const handleClick = useCallback3(
    (event) => {
      if (onClick)
        onClick(event);
      if (autoHide)
        dialog.hide();
    },
    [autoHide, dialog.hide, onClick]
  );
  return /* @__PURE__ */ React39.createElement(
    Button_default,
    {
      ref,
      onClick: handleClick,
      ...restProps
    }
  );
}
var ModalButton_default = forwardRef31(ModalButton);

// lib/components/Modal/useModal.tsx
import React40 from "react";
function useModal(props) {
  const { initialOpen = false } = props ?? {};
  const [isOpen, setOpen] = React40.useState(initialOpen);
  const show = React40.useCallback(() => setOpen(true), [setOpen]);
  const hide2 = React40.useCallback(() => setOpen(false), [setOpen]);
  return { isOpen, show, hide: hide2 };
}
var useModal_default = useModal;

// lib/components/Notification/NotificationContext.tsx
import React41, { useCallback as useCallback4, useContext as useContext2, useState as useState4, createContext as createContext2, useEffect } from "react";
var NotificationContext = createContext2([]);
function NotificationContextProvider({ children }) {
  const [notifications, setNotifications] = useState4([]);
  const addNotification = useCallback4(
    (notification) => {
      setNotifications((current) => [...current, notification]);
    },
    [setNotifications]
  );
  const removeNotification = useCallback4(() => setNotifications((current) => current.slice(1)), [setNotifications]);
  useEffect(() => {
    const interval = setInterval(() => {
      removeNotification();
    }, 3e3);
    return () => clearInterval(interval);
  }, [removeNotification]);
  const contextValue = [notifications, { addNotification, removeNotification }];
  return /* @__PURE__ */ React41.createElement(NotificationContext.Provider, { value: contextValue }, children);
}
function useNotificationContext() {
  return useContext2(NotificationContext);
}

// lib/components/Notification/NotificationContainer.tsx
import React44 from "react";

// lib/components/Notification/Notification.styles.tsx
import styled30 from "@emotion/styled";
var Root23 = styled30.div(
  {
    height: "52px",
    width: "680px",
    left: "28px",
    top: "20px",
    borderRadius: "8px",
    borderColor: "transparent",
    padding: "12px 16px 12px 16px",
    color: "white",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
    alignItems: "center"
  },
  ({ theme, variant }) => {
    const colors = {
      primary: flat(theme.color.brand.primary, "100%"),
      positive: flat(theme.color.system.positive),
      negative: flat(theme.color.system.negative),
      notice: flat(theme.color.system.notice)
    };
    return {
      backgroundColor: variant ? colors[variant] : flat(theme.color.brand.primary, "100%")
    };
  }
);
var Content12 = styled30(Typography_default)({
  color: "white"
});
var Link3 = styled30.a({
  textDecoration: "underline",
  textDecorationColor: "white"
});
var Container = styled30.div(
  {
    position: "absolute",
    display: "grid",
    gap: "10px"
  },
  ({ position }) => {
    switch (position) {
      case "top-right": {
        return {
          top: 0,
          right: 0
        };
      }
      case "top-left": {
        return {
          top: 0,
          left: 0
        };
      }
      case "bottom-right": {
        return {
          bottom: 0,
          right: 0
        };
      }
      case "bottom-left": {
        return {
          bottom: 0,
          left: 0
        };
      }
    }
    return {};
  }
);

// lib/components/Notification/Notification.tsx
import React43, { forwardRef as forwardRef33 } from "react";
import { UilExclamationTriangle, UilExclamationCircle as UilExclamationCircle3, UilCheckCircle as UilCheckCircle2, UilTimes as UilTimes2 } from "@iconscout/react-unicons";
import { AnimatePresence as AnimatePresence2, motion as motion2 } from "framer-motion";

// lib/components/WithIcon/WithIcon.tsx
import React42, { forwardRef as forwardRef32 } from "react";

// lib/components/WithIcon/WithIcon.styles.tsx
import styled31 from "@emotion/styled";
var Root24 = styled31.span({}, (props) => {
  const { block, center, gap, reversed } = props;
  return {
    display: block ? "flex" : "inline-flex",
    alignItems: center ? "center" : "stretch",
    "& > input[type='checkbox'], & > input[type='radio'], & > svg, & .icon": {
      flexShrink: 0,
      marginInlineEnd: reversed ? 0 : gap,
      marginInlineStart: reversed ? gap : 0
    }
  };
});

// lib/components/WithIcon/WithIcon.tsx
function WithIcon(props, ref) {
  const { children, block, center, gap, reversed } = props;
  return /* @__PURE__ */ React42.createElement(
    Root24,
    {
      ref,
      block,
      center,
      gap,
      reversed
    },
    children
  );
}
var WithIcon_default = forwardRef32(WithIcon);

// lib/components/Notification/Notification.tsx
function Notification(props, ref) {
  const { variant, value, href, link = "Link", position = "left", removeNotification } = props;
  const ICONS = {
    negative: /* @__PURE__ */ React43.createElement(UilExclamationCircle3, null),
    notice: /* @__PURE__ */ React43.createElement(UilExclamationTriangle, null),
    positive: /* @__PURE__ */ React43.createElement(UilCheckCircle2, null)
  };
  return /* @__PURE__ */ React43.createElement(AnimatePresence2, null, /* @__PURE__ */ React43.createElement(
    motion2.div,
    {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { ease: "easeIn", duration: 0.5 }
    },
    /* @__PURE__ */ React43.createElement(
      Root23,
      {
        ref,
        variant,
        position,
        key: value
      },
      /* @__PURE__ */ React43.createElement(
        WithIcon_default,
        {
          block: false,
          center: true,
          gap: "14px",
          reversed: false
        },
        ICONS[variant],
        /* @__PURE__ */ React43.createElement(Content12, { level: "7" }, value)
      ),
      /* @__PURE__ */ React43.createElement(
        WithIcon_default,
        {
          block: false,
          center: true,
          gap: "14px",
          reversed: true
        },
        href && /* @__PURE__ */ React43.createElement(
          Link3,
          {
            href,
            target: "_blank"
          },
          /* @__PURE__ */ React43.createElement(Content12, { level: "7" }, link)
        ),
        /* @__PURE__ */ React43.createElement(
          UilTimes2,
          {
            onClick: removeNotification,
            size: "25"
          }
        )
      )
    )
  ));
}
var Notification_default = forwardRef33(Notification);

// lib/components/Notification/NotificationContainer.tsx
function NotificationContainer({ position = "top-right" }) {
  const [notifications, { removeNotification }] = useNotificationContext();
  return /* @__PURE__ */ React44.createElement(Container, { position }, notifications.map(({ variant, value }) => {
    return /* @__PURE__ */ React44.createElement(
      Notification_default,
      {
        variant,
        value,
        key: value,
        removeNotification
      }
    );
  }));
}

// lib/components/NumberInput/NumberInput.tsx
import React45, { forwardRef as forwardRef34, useCallback as useCallback5 } from "react";
import { UilMinus, UilPlus as UilPlus2 } from "@iconscout/react-unicons";

// lib/components/NumberInput/NumberInput.styles.tsx
import styled32 from "@emotion/styled";
var Control = styled32(TextField)(
  {
    label: "number",
    // Hide default number input arrows.
    input: {
      appearance: "textfield",
      // Firefox.
      // Others.
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        appearance: "none"
      }
    }
  },
  ({ fullWidth }) => ({ ...!fullWidth && { maxWidth: 240 } })
);
var ActionWrapper = styled32.div({
  display: "flex",
  alignItems: "center",
  gap: 4
});
var Action2 = styled32.button(
  {
    label: "number-action",
    all: "unset",
    padding: 4,
    lineHeight: 0
  },
  ({ theme }) => ({
    "&:focus, &:hover": {
      color: rgb3(theme.color.system.gray)
    },
    "&:disabled": {
      color: "inherit"
    }
  })
);
var Separator = styled32.hr(
  {
    borderWidth: 0,
    width: 1,
    height: "1.5em"
  },
  ({ theme }) => ({
    backgroundColor: rgb3(theme.color.system.gray, "20%")
  })
);

// lib/components/NumberInput/NumberInput.tsx
function step(control, fnName) {
  if (control) {
    control[fnName]();
    control.dispatchEvent(new Event("input", { bubbles: true }));
  }
}
function Actions4({ control, disabled }) {
  const decrement = useCallback5(() => step(control.current, "stepDown"), []);
  const increment = useCallback5(() => step(control.current, "stepUp"), []);
  return /* @__PURE__ */ React45.createElement(ActionWrapper, null, /* @__PURE__ */ React45.createElement(
    Action2,
    {
      "aria-label": "Decrement",
      disabled,
      onClick: decrement,
      type: "button"
    },
    /* @__PURE__ */ React45.createElement(UilMinus, { size: "1em" })
  ), /* @__PURE__ */ React45.createElement(Separator, { "aria-orientation": "vertical" }), /* @__PURE__ */ React45.createElement(
    Action2,
    {
      "aria-label": "Increment",
      disabled,
      onClick: increment,
      type: "button"
    },
    /* @__PURE__ */ React45.createElement(UilPlus2, { size: "1em" })
  ));
}
var NumberInput = forwardRef34(
  ({ disabled, end, ...delegated }, ref) => {
    const innerRef = useComposedRefs(ref);
    return /* @__PURE__ */ React45.createElement(
      Control,
      {
        disabled,
        end: end ?? /* @__PURE__ */ React45.createElement(
          Actions4,
          {
            control: innerRef,
            disabled
          }
        ),
        ref: innerRef,
        type: "number",
        ...delegated
      }
    );
  }
);

// lib/components/OverflowMenu/OverflowMenu.tsx
import React47, { forwardRef as forwardRef36 } from "react";
import * as DropdownMenu2 from "@radix-ui/react-dropdown-menu";

// lib/components/OverflowMenu/OverflowMenu.styles.tsx
import styled33 from "@emotion/styled";
var Menu = styled33.div(
  {
    label: "overflow-menu",
    backgroundColor: "white",
    borderRadius: "8px",
    zIndex: "1"
  },
  ({ theme, className }) => ({
    boxShadow: `0 4px 20px ${flat(theme.color.system.gray, "20%")}`,
    marginTop: (className == null ? void 0 : className.includes("top-space")) ? "12px" : "0"
  })
);

// lib/components/OverflowMenu/OverflowMenuButton.tsx
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import React46, { forwardRef as forwardRef35 } from "react";

// lib/components/OverflowMenu/OverflowMenuButton.styles.tsx
import styled34 from "@emotion/styled";
import { motion as motion3 } from "framer-motion";
var Root25 = styled34(motion3.button)(
  {
    border: "none",
    borderRadius: "100px",
    cursor: "pointer",
    height: "32px",
    outline: "none",
    width: "44px"
  },
  (props) => {
    const { disabled, theme } = props;
    return {
      "--iconColor": flat(theme.color.system.gray, disabled ? "40%" : "100%"),
      backgroundColor: disabled ? flat(theme.color.system.gray, "10%") : flat(theme.color.brand.primary, "5%"),
      pointerEvents: disabled ? "none" : "inherit"
    };
  }
);
var ContentWrapper = styled34.span({
  display: "flex",
  gap: "4px",
  justifyContent: "center"
});
var Dot2 = styled34(motion3.span)({
  backgroundColor: "var(--iconColor)",
  borderRadius: "4px",
  height: "4px",
  width: "4px"
});

// lib/components/OverflowMenu/OverflowMenuButton.tsx
var WAVE_ANIMATION_CONTAINER = {
  bounce: {
    transition: {
      duration: 0.35,
      staggerChildren: 0.1
    }
  }
};
var WAVE_ANIMATION_ITEM = { bounce: { translateY: [0, -5, 0] } };
function OverflowMenuButton(props, ref) {
  const { disabled, trigger, ...rest } = props;
  return /* @__PURE__ */ React46.createElement(
    DropdownMenu.Trigger,
    {
      asChild: true,
      disabled,
      ...rest
    },
    trigger || /* @__PURE__ */ React46.createElement(
      Root25,
      {
        ref,
        variants: WAVE_ANIMATION_CONTAINER,
        whileHover: "bounce"
      },
      /* @__PURE__ */ React46.createElement(ContentWrapper, null, /* @__PURE__ */ React46.createElement(Dot2, { variants: WAVE_ANIMATION_ITEM }), /* @__PURE__ */ React46.createElement(Dot2, { variants: WAVE_ANIMATION_ITEM }), /* @__PURE__ */ React46.createElement(Dot2, { variants: WAVE_ANIMATION_ITEM }))
    )
  );
}
var OverflowMenuButton_default = forwardRef35(OverflowMenuButton);

// lib/components/OverflowMenu/OverflowMenu.tsx
var OverflowMenu = forwardRef36(
  ({ children, disabled, trigger, ...rest }, ref) => {
    return /* @__PURE__ */ React47.createElement(DropdownMenu2.Root, null, /* @__PURE__ */ React47.createElement(
      OverflowMenuButton_default,
      {
        disabled,
        trigger,
        ...rest
      }
    ), /* @__PURE__ */ React47.createElement(DropdownMenu2.Portal, null, /* @__PURE__ */ React47.createElement(
      DropdownMenu2.Content,
      {
        asChild: true,
        align: "end"
      },
      /* @__PURE__ */ React47.createElement(
        Menu,
        {
          ref,
          className: trigger ? "top-space" : ""
        },
        children
      )
    )));
  }
);
var OverflowMenu_default = OverflowMenu;

// lib/components/OverflowMenu/OverflowMenuItem.tsx
import * as DropdownMenu3 from "@radix-ui/react-dropdown-menu";
import React48, { forwardRef as forwardRef37, useCallback as useCallback6, useRef as useRef2 } from "react";

// lib/components/OverflowMenu/OverflowMenuItem.styles.tsx
import styled35 from "@emotion/styled";
var Root27 = styled35.div(
  {
    outline: "none",
    padding: "8px 24px 8px 12px"
  },
  (props) => {
    const { disabled, theme } = props;
    return {
      fontSize: theme.fontSize[8],
      fontWeight: theme.fontWeight,
      letterSpacing: theme.letterSpacing,
      lineHeight: theme.lineHeight[8],
      color: flat(theme.color.system.gray, disabled ? "40%" : "100%"),
      cursor: disabled ? "default" : "pointer",
      pointerEvents: disabled ? "none" : "all",
      "&:hover": {
        color: disabled ? flat(theme.color.system.gray, "40%") : flat(theme.color.brand.primary, "100%")
      }
    };
  }
);

// lib/components/OverflowMenu/OverflowMenuItem.tsx
function OverflowMenuItem(props, ref) {
  const { children, disabled, delay, onClick, ...restProps } = props;
  const timeoutRef = useRef2();
  const handleClick = useCallback6(
    (event) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        onClick == null ? void 0 : onClick(event);
      }, delay ?? 0);
      return () => {
        clearTimeout(timeoutRef.current);
      };
    },
    [delay, onClick]
  );
  return /* @__PURE__ */ React48.createElement(
    DropdownMenu3.Item,
    {
      ...restProps,
      asChild: true,
      disabled,
      onClick: onClick ? handleClick : void 0
    },
    /* @__PURE__ */ React48.createElement(
      Root27,
      {
        ref,
        disabled
      },
      children
    )
  );
}
var OverflowMenuItem_default = forwardRef37(OverflowMenuItem);

// lib/components/Pagination/Pagination.tsx
import React49, { forwardRef as forwardRef38, useCallback as useCallback7, useEffect as useEffect2, useRef as useRef3, useState as useState5 } from "react";
import { UilAngleLeft as UilAngleLeft3, UilAngleRight as UilAngleRight3 } from "@iconscout/react-unicons";

// lib/components/Pagination/Pagination.styles.tsx
import styled36 from "@emotion/styled";
var Root28 = styled36.span({
  label: "pagination",
  display: "inline-flex",
  gap: "8px"
});
var PageNumberButton = styled36.button(
  font(7),
  {
    alignItems: "center",
    backgroundImage: "none",
    borderRadius: "4px",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    height: "40px",
    justifyContent: "center",
    textAlign: "center",
    width: "40px"
  },
  (props) => {
    const { theme, current } = props;
    if (current) {
      return {
        backgroundColor: flat(theme.color.brand.primary, "100%"),
        borderColor: "transparent",
        pointerEvents: "none",
        color: "white"
      };
    }
    return {
      backgroundColor: "white",
      borderColor: flat(theme.color.system.gray, "20%"),
      color: flat(theme.color.system.gray, "60%"),
      "&:hover": {
        borderColor: flat(theme.color.brand.primary),
        color: flat(theme.color.brand.primary)
      }
    };
  }
);
var ArrowButton2 = styled36.button(
  {
    width: "40px",
    height: "40px",
    alignItems: "center",
    backgroundImage: "none",
    borderRadius: "4px",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    justifyContent: "center",
    textAlign: "center",
    backgroundColor: "white",
    "&:disabled": {
      pointerEvents: "none",
      borderColor: "transparent"
    }
  },
  (props) => {
    const { theme } = props;
    return {
      borderColor: flat(theme.color.system.gray, "20%"),
      color: flat(theme.color.system.gray, "40%"),
      "&:hover": {
        borderColor: flat(theme.color.brand.primary),
        color: flat(theme.color.brand.primary)
      },
      "&:disabled": {
        backgroundColor: flat(theme.color.system.gray, "20%")
      }
    };
  }
);
var ArrowIcon = styled36.div({
  transform: "translateY(2px)"
});

// lib/components/Pagination/usePagination.ts
import { useMemo } from "react";
var PAGES_TO_DISPLAY = 5;
var getNumberRangeArray = (start, end) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx2) => idx2 + start);
};
var usePagination = (currentPage, pageCount) => {
  const currentPageIndex = Math.floor((currentPage - 1) / PAGES_TO_DISPLAY);
  const start = Math.max(currentPageIndex * PAGES_TO_DISPLAY + 1, 1);
  const end = Math.min(currentPageIndex * PAGES_TO_DISPLAY + 5, pageCount);
  const showPrevOverflow = start >= 2;
  const showNextOverflow = end < pageCount;
  const visiblePageNumbers = useMemo(() => {
    if (PAGES_TO_DISPLAY >= pageCount) {
      return getNumberRangeArray(1, pageCount);
    }
    if (!showPrevOverflow && showNextOverflow) {
      const leftRange = getNumberRangeArray(1, PAGES_TO_DISPLAY);
      return leftRange;
    }
    if (showPrevOverflow && !showNextOverflow) {
      const rightRange = getNumberRangeArray(pageCount - PAGES_TO_DISPLAY + 1, pageCount);
      return rightRange;
    }
    if (showPrevOverflow && showNextOverflow) {
      const middleRange = getNumberRangeArray(start, end);
      return middleRange;
    }
    return [];
  }, [currentPage, pageCount]);
  return { visiblePageNumbers, showPrevOverflow, showNextOverflow };
};

// lib/components/Pagination/Pagination.tsx
var Pagination2 = forwardRef38(
  ({ initialPage = 1, onPageChange, pageSize, totalCount, ...restProps }, ref) => {
    const [currentPage, setCurrentPage] = useState5(initialPage);
    const pageCount = Math.ceil(totalCount / (pageSize || 0));
    const { visiblePageNumbers, showPrevOverflow, showNextOverflow } = usePagination(currentPage, pageCount);
    if (pageCount < 2) {
      return null;
    }
    const oldPage = useRef3(currentPage);
    const onNext = useCallback7(() => {
      setCurrentPage((page) => Math.min(page + 1, pageCount));
    }, [pageCount, setCurrentPage]);
    const onPrevious = useCallback7(() => {
      setCurrentPage((page) => Math.max(page - 1, 1));
    }, [setCurrentPage]);
    const onPrevPageOverflow = useCallback7(() => {
      setCurrentPage(Math.max(visiblePageNumbers[0] - 1, 1));
    }, [setCurrentPage, visiblePageNumbers]);
    const onNextPageOverflow = useCallback7(() => {
      setCurrentPage(Math.min(visiblePageNumbers[visiblePageNumbers.length - 1] + 1, pageCount));
    }, [pageCount, setCurrentPage, visiblePageNumbers]);
    useEffect2(() => {
      if (oldPage.current !== currentPage) {
        oldPage.current = currentPage;
        onPageChange(currentPage);
      }
    }, [currentPage, oldPage, onPageChange]);
    return /* @__PURE__ */ React49.createElement(
      Root28,
      {
        ...restProps,
        ref
      },
      /* @__PURE__ */ React49.createElement(
        ArrowButton2,
        {
          disabled: currentPage === 1,
          onClick: onPrevious
        },
        /* @__PURE__ */ React49.createElement(ArrowIcon, null, /* @__PURE__ */ React49.createElement(UilAngleLeft3, null))
      ),
      showPrevOverflow && /* @__PURE__ */ React49.createElement(
        PageNumberButton,
        {
          current: false,
          onClick: onPrevPageOverflow
        },
        "..."
      ),
      visiblePageNumbers.map((pageNumber) => {
        const onClick = useCallback7(() => setCurrentPage(pageNumber), [pageNumber, setCurrentPage]);
        return /* @__PURE__ */ React49.createElement(
          PageNumberButton,
          {
            current: currentPage === pageNumber,
            onClick,
            key: pageNumber
          },
          pageNumber
        );
      }),
      showNextOverflow && /* @__PURE__ */ React49.createElement(
        PageNumberButton,
        {
          current: false,
          onClick: onNextPageOverflow
        },
        "..."
      ),
      /* @__PURE__ */ React49.createElement(
        ArrowButton2,
        {
          disabled: currentPage === pageCount,
          onClick: onNext
        },
        /* @__PURE__ */ React49.createElement(ArrowIcon, null, /* @__PURE__ */ React49.createElement(UilAngleRight3, null))
      )
    );
  }
);
var Pagination_default = Pagination2;

// lib/components/PillGroup/PillGroup.tsx
import * as ToggleGroup2 from "@radix-ui/react-toggle-group";
import { LayoutGroup, motion as motion4 } from "framer-motion";
import React51, { forwardRef as forwardRef40, useCallback as useCallback8, useState as useState6, useEffect as useEffect3 } from "react";

// lib/components/PillGroup/PillGroupItem.tsx
import { UilSpinnerAlt as LoaderIcon } from "@iconscout/react-unicons";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import React50, { forwardRef as forwardRef39 } from "react";

// lib/components/PillGroup/PillGroup.styles.tsx
import styled37 from "@emotion/styled";
var Root29 = styled37.div(
  font(7),
  {
    label: "pill-group",
    alignItems: "center",
    backgroundImage: "none",
    borderRadius: "100px",
    borderStyle: "solid",
    borderWidth: "1px",
    display: "inline-flex",
    gap: "8px",
    justifyContent: "center",
    padding: "8px",
    textAlign: "center"
  },
  ({ theme, variant }) => ({
    backgroundColor: variant === "primary" ? "transparent" : flat(theme.color.system.gray, "20%"),
    // white / dark 60%
    borderColor: "transparent"
  })
);
var Item6 = styled37.span(
  {
    alignItems: "center",
    cursor: "pointer",
    display: "flex",
    gap: "10px",
    justifyContent: "center",
    padding: "6px 16px"
  },
  ({ theme, variant }) => ({
    color: variant === "secondary" ? flat(theme.color.system.gray, "60%") : flat(theme.color.brand.primary, "60%"),
    // dark gray / primary
    '&[data-state="on"]': {
      backgroundColor: flat(theme.color.system.gray, "20%"),
      // white
      boxShadow: `0px 4px 20px ${rgb3("43 45 45", "20%")}`,
      borderRadius: "24px"
    }
  })
);

// lib/components/PillGroup/PillGroupItem.tsx
function PillGroupItem(props, ref) {
  const { isNavigating, option, selectedValue, variant, ...restProps } = props;
  const { icon, value } = option;
  const showSpinner = selectedValue === value && isNavigating;
  return /* @__PURE__ */ React50.createElement(
    ToggleGroup.Item,
    {
      ...restProps,
      asChild: true,
      value
    },
    /* @__PURE__ */ React50.createElement(
      Item6,
      {
        ref,
        variant
      },
      /* @__PURE__ */ React50.createElement(
        WithIcon_default,
        {
          block: false,
          center: true,
          gap: "8px",
          reversed: false
        },
        showSpinner ? /* @__PURE__ */ React50.createElement(LoaderIcon, null) : icon,
        value
      )
    )
  );
}
var PillGroupItem_default = forwardRef39(PillGroupItem);

// lib/components/PillGroup/PillGroup.tsx
var PillGroup = forwardRef40(
  ({ defaultValue, isNavigation, onSelect, options, variant, ...restProps }, ref) => {
    var _a2;
    const initialValue = defaultValue ?? ((_a2 = options[0]) == null ? void 0 : _a2.value) ?? "";
    const [selectedValue, setSelectedValue] = useState6(initialValue);
    const [isNavigating, setIsNavigating] = useState6(false);
    const handleValueChange = useCallback8(
      (value) => {
        if (value) {
          setSelectedValue(value);
          if (!isNavigation) {
            onSelect(value);
          }
          setIsNavigating(true);
          setTimeout(() => {
            onSelect(value);
            setIsNavigating(false);
          }, 500);
        }
      },
      [setSelectedValue]
    );
    useEffect3(() => {
      setIsNavigating(false);
    }, []);
    const itemElements = options.map((option) => {
      const { value } = option;
      const layoutProps = selectedValue === value ? { layoutId: "id" } : {};
      return /* @__PURE__ */ React51.createElement(
        motion4.div,
        {
          key: value,
          layout: true,
          ...layoutProps
        },
        /* @__PURE__ */ React51.createElement(
          PillGroupItem_default,
          {
            isNavigating,
            option,
            selectedValue,
            variant
          }
        )
      );
    });
    return /* @__PURE__ */ React51.createElement(
      ToggleGroup2.Root,
      {
        ...restProps,
        asChild: true,
        onValueChange: handleValueChange,
        type: "single",
        value: selectedValue
      },
      /* @__PURE__ */ React51.createElement(
        Root29,
        {
          ref,
          variant
        },
        /* @__PURE__ */ React51.createElement(LayoutGroup, null, itemElements)
      )
    );
  }
);
var PillGroup_default = PillGroup;

// lib/components/ProgressBar/ProgressBar.tsx
import React52, { forwardRef as forwardRef41 } from "react";
import { motion as motion5 } from "framer-motion";

// lib/components/ProgressBar/ProgressBar.styles.tsx
import isPropValid8 from "@emotion/is-prop-valid";
import styled38 from "@emotion/styled";
import * as Progress from "@radix-ui/react-progress";
var CSS_VAR_PROGRESS_OFFSET = "--progress-offset";
var Root32 = styled38(Progress.Root)(
  {
    label: "progress",
    overflow: "hidden",
    display: "grid",
    // Force children to take up entire area.
    borderRadius: 9999,
    height: 4,
    width: "100%"
  },
  ({ theme }) => ({
    backgroundColor: rgb3(theme.color.system.gray, "20%")
  })
);
var Indicator6 = styled38(Progress.Indicator, { shouldForwardProp: isPropValid8 })(
  {
    label: "progress-indicator",
    borderRadius: 9999,
    transform: `translateX(var(${CSS_VAR_PROGRESS_OFFSET}))`,
    '&[data-state="indeterminate"]': {
      width: "25%"
    }
  },
  ({ theme, variant }) => {
    const color2 = theme.color.system[variant] ?? theme.color.brand.primary;
    return {
      ...variant === "notice" && {
        '&[data-state="complete"]': {
          backgroundColor: rgb3(theme.color.system.positive)
        }
      },
      backgroundColor: rgb3(color2)
    };
  }
);

// lib/components/ProgressBar/ProgressBar.tsx
var ANIMATION_VARIANTS = {
  // For indeterminate progress, the indicator is at 25% width, sliding from corner to corner.
  indeterminate: {
    // At 25% width, relatively, 1% from the right-corner is at 99% * (100% / 25%) = 396%.
    [CSS_VAR_PROGRESS_OFFSET]: "396%",
    transition: {
      duration: 3,
      repeat: Infinity,
      repeatType: "reverse"
    }
  },
  slideIn: (style) => ({
    ...style,
    transition: { duration: 1, ease: "linear" }
  })
};
var ProgressBar = forwardRef41(
  ({ animate = false, max = 100, style: parentStyle, value, variant = "brand", ...delegated }, ref) => {
    const percentage = (value ?? 0) / max * 100;
    const offset = Math.max(-99, -100 + percentage);
    const style = { [CSS_VAR_PROGRESS_OFFSET]: `${offset}%` };
    const isIndeterminate = value == null;
    const shouldAnimate = animate || isIndeterminate;
    const indicatorElement = /* @__PURE__ */ React52.createElement(Indicator6, { variant });
    return /* @__PURE__ */ React52.createElement(
      Root32,
      {
        asChild: shouldAnimate,
        max,
        ref,
        style: { ...parentStyle, ...style },
        value,
        ...delegated
      },
      shouldAnimate ? /* @__PURE__ */ React52.createElement(
        motion5.div,
        {
          animate: isIndeterminate ? "indeterminate" : "slideIn",
          custom: style,
          initial: "slideIn",
          variants: ANIMATION_VARIANTS
        },
        indicatorElement
      ) : indicatorElement
    );
  }
);

// lib/components/CircularProgressBar/CircularProgressBar.tsx
import React53, { forwardRef as forwardRef42 } from "react";

// lib/components/CircularProgressBar/CircularProgressBar.styles.tsx
import styled39 from "@emotion/styled";
import * as Progress2 from "@radix-ui/react-progress";
import isPropValid9 from "@emotion/is-prop-valid";
var CSS_VAR_CIRCULAR_PROGRESS = "--circular-progress";
var getVariant = (theme, variant) => {
  switch (variant) {
    case "brand":
      return rgb(theme.colorV2.brand, 500);
    case "notice":
      return rgb(theme.colorV2.system.notice, 600);
    default:
      return rgb(theme.colorV2.system[variant]);
  }
};
var Root34 = styled39(Progress2.Root)({
  display: "flex",
  alignItems: "center",
  gap: 8
});
var Percentage = styled39(Typography_default)({
  fontWeight: 500
});
var CircularProgress = styled39(Progress2.Indicator, {
  shouldForwardProp: (prop) => !["variant", "strokeWidth", "size"].includes(prop) && isPropValid9(prop)
})(
  {
    borderRadius: "9999px"
  },
  ({ theme, variant = "brand", size, strokeWidth }) => {
    const track = rgb(theme.colorV2.system.gray, 200);
    return {
      "--progress": getVariant(theme, variant),
      ...variant === "notice" && {
        '&[data-state="complete"]': {
          "--progress": rgb(theme.colorV2.system.positive)
        }
      },
      height: CIRCULAR_PROGRESS_BAR_SIZE[size],
      width: CIRCULAR_PROGRESS_BAR_SIZE[size],
      background: `conic-gradient(var(--progress) 0% var(${CSS_VAR_CIRCULAR_PROGRESS}), ${track} var(${CSS_VAR_CIRCULAR_PROGRESS}))`,
      maskImage: `radial-gradient(circle at center, transparent ${10 * STROKE_WIDTH[strokeWidth]}%, ${track} ${10 * STROKE_WIDTH[strokeWidth]}%)`
    };
  }
);

// lib/components/CircularProgressBar/CircularProgressBar.tsx
var CIRCULAR_PROGRESS_BAR_SIZE = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64
};
var STROKE_WIDTH = {
  sm: 6,
  md: 5,
  lg: 4
};
var CircularProgressBar = forwardRef42(
  ({
    size = "md",
    max = 100,
    style: parentStyle,
    value = 0,
    variant = "brand",
    strokeWidth = "md",
    showPercentage = false,
    ...restProps
  }, ref) => {
    const percentage = Math.round((value ?? 0) / max * 100);
    const style = { [CSS_VAR_CIRCULAR_PROGRESS]: `${percentage}%` };
    return /* @__PURE__ */ React53.createElement(
      Root34,
      {
        max,
        ref,
        style: { ...parentStyle, ...style },
        value,
        ...restProps
      },
      /* @__PURE__ */ React53.createElement(
        CircularProgress,
        {
          size,
          strokeWidth,
          variant
        }
      ),
      showPercentage && /* @__PURE__ */ React53.createElement(Percentage, { level: "8" }, percentage, "%")
    );
  }
);

// lib/components/StackedProgressBar/StackedProgressBar.tsx
import React54, { forwardRef as forwardRef43 } from "react";

// lib/components/StackedProgressBar/StackedProgressBar.styles.tsx
import styled40 from "@emotion/styled";
import * as Progress3 from "@radix-ui/react-progress";
import isPropValid10 from "@emotion/is-prop-valid";
var Root36 = styled40(Progress3.Root)(
  {
    label: "stacked-progress",
    overflow: "hidden",
    display: "grid",
    // Force children to take up entire area.
    borderRadius: 9999,
    height: 4,
    width: "100%"
  },
  ({ theme }) => ({
    backgroundColor: rgb(theme.colorV2.system.gray, 50)
  })
);
var Indicator9 = styled40(Progress3.Indicator, {
  shouldForwardProp: (prop) => isPropValid10(prop) && !["color"].includes(prop)
})(
  {
    label: "stacked-progress-indicator"
  },
  ({ color: color2 }) => ({
    backgroundColor: color2
  })
);
var Indicators = styled40.div({
  display: "flex",
  justifyContent: "flex-start",
  flexWrap: "wrap"
});
var DotIndicators = styled40.div({
  display: "flex",
  alignItems: "center",
  gap: 8
});
var DotIndicator = styled40.div({
  display: "flex",
  alignItems: "center",
  gap: 8
});
var Dot3 = styled40("div", {
  shouldForwardProp: (prop) => isPropValid10(prop) && !["color"].includes(prop)
})(
  {
    width: 8,
    flexShrink: 0,
    height: 8,
    borderRadius: 9999
  },
  ({ color: color2 }) => ({
    backgroundColor: color2
  })
);
var ProgressBarWrapper = styled40.div(({ indicatorsPosition }) => {
  let positionStyles = {};
  switch (indicatorsPosition) {
    case "top":
      positionStyles = {
        flexDirection: "column",
        alignItems: "flex-start"
      };
      break;
    case "bottom":
      positionStyles = {
        flexDirection: "column-reverse",
        alignItems: "flex-start"
      };
      break;
    case "left":
      positionStyles = {
        flexDirection: "row",
        alignItems: "center"
      };
      break;
    case "right":
      positionStyles = {
        flexDirection: "row-reverse",
        alignItems: "center"
      };
      break;
    default:
      positionStyles = {
        flexDirection: "column",
        alignItems: "center"
      };
  }
  return {
    display: "flex",
    gap: "8px",
    ...positionStyles
  };
});
var IndicatorTextTypography = styled40(Typography_default)`
  white-space: nowrap;
  font-weight: 500;
`;

// lib/components/StackedProgressBar/StackedProgressBar.tsx
var DotIndicators2 = ({ indicators }) => {
  return /* @__PURE__ */ React54.createElement(DotIndicators, null, indicators.map(({ name, value, color: color2, icon, ...restProps }) => /* @__PURE__ */ React54.createElement(
    DotIndicator,
    {
      "data-testid": `dot-indicator-${value}`,
      key: name,
      ...restProps
    },
    icon ?? /* @__PURE__ */ React54.createElement(
      Dot3,
      {
        color: color2,
        ...restProps
      }
    ),
    /* @__PURE__ */ React54.createElement(
      IndicatorTextTypography,
      {
        level: "8",
        color: "gray"
      },
      value,
      " ",
      name ?? ""
    )
  )));
};
var StackedProgressBar = forwardRef43(
  ({ stack, indicators = false, indicatorsPosition = "top", ...restProps }, ref) => {
    const valueSum = stack.reduce((acc, { value }) => acc + value, 0);
    if (valueSum === 0) {
      return null;
    }
    const indicatorElement = /* @__PURE__ */ React54.createElement(Indicators, null, stack.map(({ color: color2, value }) => /* @__PURE__ */ React54.createElement(
      Indicator9,
      {
        key: value,
        style: {
          width: `${value / valueSum * 100}%`
        },
        color: color2,
        "data-testid": `progress-indicator-${value}`
      }
    )));
    return (
      // Display indicators on top of the progress bar if `indicators` is true
      /* @__PURE__ */ React54.createElement(
        ProgressBarWrapper,
        {
          indicatorsPosition,
          ref,
          ...restProps
        },
        indicators && /* @__PURE__ */ React54.createElement(DotIndicators2, { indicators: stack }),
        /* @__PURE__ */ React54.createElement(Root36, { value: valueSum }, indicatorElement)
      )
    );
  }
);

// lib/components/Search/Search.tsx
import React55, { forwardRef as forwardRef44, useCallback as useCallback9, useState as useState7 } from "react";
import { UilSearch, UilTimes as UilTimes3 } from "@iconscout/react-unicons";

// lib/components/Search/Search.styles.tsx
import styled41 from "@emotion/styled";
var Field2 = styled41(TextField)({
  label: "search",
  "input::-webkit-search-cancel-button, .clear": {
    display: "none"
  },
  "&:focus-within, &:hover": {
    "& input:not(:placeholder-shown) + .clear": {
      /* input's value is not empty */
      display: "block"
    }
  }
});
var Action3 = styled41.button(
  {
    label: "search-action",
    all: "unset",
    lineHeight: 0
  },
  ({ theme }) => ({
    "&:focus, &:hover": {
      color: rgb3(theme.color.system.gray)
    }
  })
);

// lib/components/Search/Search.tsx
function clearHandler({ currentTarget }) {
  const control = currentTarget.previousSibling;
  if (control instanceof HTMLInputElement) {
    nativeSetValue(control, "");
    control.focus();
  }
}
function ClearIndicator2(props) {
  const [isFormControl, setIsFormControl] = useState7(null);
  const handleRef = useCallback9((node) => {
    if (node) {
      setIsFormControl(node.closest("form") !== null);
    }
  }, []);
  if (isFormControl === null) {
    return /* @__PURE__ */ React55.createElement("span", { ref: handleRef });
  }
  return /* @__PURE__ */ React55.createElement(
    Action3,
    {
      "aria-label": "Clear",
      className: "clear",
      ...isFormControl ? { type: "reset" } : { onClick: clearHandler },
      ...props
    },
    /* @__PURE__ */ React55.createElement(UilTimes3, null)
  );
}
function SearchIcon(props) {
  const [isFormControl, setIsFormControl] = useState7(null);
  const handleRef = useCallback9((node) => {
    if (node) {
      setIsFormControl(node.closest("form") !== null);
    }
  }, []);
  if (isFormControl === null) {
    return /* @__PURE__ */ React55.createElement("span", { ref: handleRef });
  }
  const inner = /* @__PURE__ */ React55.createElement(UilSearch, null);
  if (isFormControl) {
    return /* @__PURE__ */ React55.createElement(
      Action3,
      {
        "aria-label": "Submit",
        type: "submit",
        ...props
      },
      inner
    );
  }
  return inner;
}
var Search = forwardRef44(
  (props, ref) => {
    return /* @__PURE__ */ React55.createElement(
      Field2,
      {
        end: /* @__PURE__ */ React55.createElement(ClearIndicator2, null),
        placeholder: "Search For\u2026",
        ref,
        start: /* @__PURE__ */ React55.createElement(SearchIcon, null),
        type: "search",
        ...props
      }
    );
  }
);

// lib/components/Slider/OptionSlider.tsx
import React57, { forwardRef as forwardRef46, useCallback as useCallback11 } from "react";

// lib/components/Slider/Control.tsx
import React56, { forwardRef as forwardRef45, useCallback as useCallback10, useEffect as useEffect4 } from "react";

// lib/components/Slider/Control.styles.tsx
import styled42 from "@emotion/styled";
var CSS_VAR_RANGE_PROGRESS = "--range-progress";
var CSS_VAR_THUMB_COLOR = "--thumb-color";
function getThumbStyles() {
  return {
    appearance: "none",
    borderWidth: 0,
    borderRadius: 9999,
    width: 12,
    height: 12,
    backgroundColor: `var(${CSS_VAR_THUMB_COLOR})`
  };
}
function getTrackStyles() {
  return {
    paddingTop: 10,
    paddingBottom: 10
  };
}
var Control2 = styled42.input(
  {
    label: "range",
    appearance: "none",
    // In Chrome, without flex, the presence of datalist ticks displaces the thumb.
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
    borderRadius: 9999,
    margin: 0,
    height: 4,
    // Chrome doesn't like grouping these selectors with Mozilla ones.
    "&::-webkit-slider-runnable-track": getTrackStyles(),
    "&::-moz-range-track": getTrackStyles(),
    "&::-webkit-slider-thumb": getThumbStyles(),
    "&::-moz-range-thumb": getThumbStyles()
  },
  ({ theme }) => ({
    "&:disabled": {
      [CSS_VAR_THUMB_COLOR]: "currentColor",
      cursor: "not-allowed",
      background: "none",
      backgroundColor: "currentColor",
      color: rgb3(theme.color.system.gray, "20%")
    }
  }),
  ({ theme, variant }) => {
    if (variant === "gradient") {
      return {
        [CSS_VAR_THUMB_COLOR]: rgb3(theme.color.system.gray, "80%"),
        /**
         * Displays:
         * - First gradient: the remainder of the range not selected. This is rendered on top of:
         * - Second gradient: the portion of the range that is selected.
         */
        background: `
          linear-gradient(
            to right,
            transparent var(${CSS_VAR_RANGE_PROGRESS}),
            ${rgb3(theme.color.system.gray, "20%")} var(${CSS_VAR_RANGE_PROGRESS}),
            ${rgb3(theme.color.system.gray, "20%")} 100%
          ),
          linear-gradient(
            to right,
            ${rgb3(theme.color.system.positive)} 0%,
            ${rgb3(theme.color.system.notice)}   50%,
            ${rgb3(theme.color.system.negative)} 100%
          )
        `
      };
    }
    return {
      [CSS_VAR_THUMB_COLOR]: rgb3(theme.color.brand.primary),
      background: `
        linear-gradient(
          to right,
          var(--thumb-color) var(${CSS_VAR_RANGE_PROGRESS}),
          ${rgb3(theme.color.system.gray, "20%")} var(${CSS_VAR_RANGE_PROGRESS}),
          ${rgb3(theme.color.system.gray, "20%")} 100%
        )
      `
    };
  }
);

// lib/components/Slider/Control.tsx
function getRangeProgress(min, max, value) {
  const range = max - min;
  const start = value - min;
  const percentage = Math.round(start * 100 / range * 100) / 100;
  return `${percentage}%`;
}
function setRangeProgress(target) {
  target.style.setProperty(
    CSS_VAR_RANGE_PROGRESS,
    getRangeProgress(Number(target.min), Number(target.max), target.valueAsNumber)
  );
}
var Control3 = forwardRef45(
  ({ onChange, ...delegated }, outerRef) => {
    const ref = useComposedRefs(outerRef);
    const wrappedOnChange = useCallback10(
      (evt) => {
        setRangeProgress(evt.currentTarget);
        onChange == null ? void 0 : onChange(evt);
      },
      [onChange]
    );
    useEffect4(() => {
      const control = ref.current;
      if (control) {
        setRangeProgress(control);
      }
    }, []);
    return /* @__PURE__ */ React56.createElement(
      Control2,
      {
        onChange: wrappedOnChange,
        ref,
        type: "range",
        ...delegated
      }
    );
  }
);

// lib/components/Slider/OptionSlider.styles.tsx
import styled44 from "@emotion/styled";

// lib/components/Slider/Slider.styles.tsx
import styled43 from "@emotion/styled";
var Wrapper4 = styled43.div(
  {
    label: "wrapper",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap"
  },
  ({ theme }) => ({
    color: rgb3(theme.color.system.gray, "80%"),
    fontSize: theme.fontSize[9],
    ".numberControl": {
      color: rgb3(theme.color.system.gray, "60%"),
      fontSize: theme.fontSize[8],
      '&[aria-disabled="true"]': {
        color: "inherit"
      }
    },
    '&[aria-disabled="true"]': {
      cursor: "not-allowed",
      color: rgb3(theme.color.system.gray, "40%")
    }
  })
);

// lib/components/Slider/OptionSlider.styles.tsx
var DataList = styled44.div(
  {
    label: "datalist",
    display: "flex",
    justifyContent: "space-around",
    gap: "8px",
    width: "100%"
    // Force new flex row.
  },
  ({ segments }) => ({
    /**
     * The range control is divided into length - 1 segments with the first and last option at the
     * edges. Therefore, to align labels with their corresponding tick, we need to adjust *only*
     * the first and last option. Flex allows shrinking these children to their correct width.
     */
    "& > *": {
      flex: `0 0 calc(100% / ${segments - 1} - 8px)`,
      // Subtract gap as flex-basis includes it.
      "&:first-child, &:last-child": {
        flexShrink: 1
      },
      "&:first-child": {
        textAlign: "left"
      },
      "&:last-child": {
        textAlign: "right"
      },
      textAlign: "center",
      // Wrap long labels instead of causing displacement.
      overflow: "hidden",
      textOverflow: "ellipsis"
    }
  })
);

// lib/components/Slider/OptionSlider.tsx
function findIndex(needle, haystack) {
  const needlePrimitive = typeof needle === "string" ? needle : needle.value;
  return haystack.findIndex((value) => {
    const subject = typeof value === "string" ? value : value.value;
    return subject === needlePrimitive;
  });
}
function DataList2({ id, options }) {
  return /* @__PURE__ */ React57.createElement(React57.Fragment, null, /* @__PURE__ */ React57.createElement("datalist", { id }, options.map((option) => /* @__PURE__ */ React57.createElement(
    "option",
    {
      key: typeof option === "string" ? option : option.value,
      label: typeof option === "string" ? option : option.label,
      value: findIndex(option, options)
    }
  ))), /* @__PURE__ */ React57.createElement(DataList, { segments: options.length }, options.map((option) => /* @__PURE__ */ React57.createElement("span", { key: typeof option === "string" ? option : option.value }, typeof option === "string" ? option : option.label))));
}
var OptionSlider = forwardRef46(
  ({
    className,
    defaultValue,
    disabled,
    "aria-invalid": invalid = false,
    onChange,
    options,
    ...delegated
  }, ref) => {
    const dataListId = useId();
    const wrappedOnChange = useCallback11(
      (evt) => {
        onChange == null ? void 0 : onChange(evt, options[Number(evt.currentTarget.value)]);
      },
      [onChange, options]
    );
    const defaultValueIndex = findIndex(defaultValue ?? options[Math.floor(options.length / 2)], options);
    return /* @__PURE__ */ React57.createElement(
      Wrapper4,
      {
        className,
        ...disabled && { "aria-disabled": true },
        ...invalid && { "aria-invalid": true }
      },
      /* @__PURE__ */ React57.createElement(
        Control3,
        {
          defaultValue: defaultValueIndex,
          disabled,
          list: dataListId,
          min: 0,
          max: options.length - 1,
          onChange: wrappedOnChange,
          ref,
          ...delegated
        }
      ),
      /* @__PURE__ */ React57.createElement(
        DataList2,
        {
          id: dataListId,
          options
        }
      )
    );
  }
);

// lib/components/Slider/Slider.tsx
import React58, { forwardRef as forwardRef47, useCallback as useCallback12, useEffect as useEffect5, useRef as useRef4 } from "react";
var Slider = forwardRef47(
  ({
    className,
    disabled,
    "aria-invalid": invalid = false,
    min = 0,
    max = 100,
    onChange,
    step: step2,
    ...delegated
  }, outerRef) => {
    const ref = useComposedRefs(outerRef);
    const numberRef = useRef4(null);
    const wrappedOnChange = useCallback12(
      (evt) => {
        nativeSetValue(numberRef.current, evt.currentTarget.valueAsNumber);
        onChange == null ? void 0 : onChange(evt);
      },
      [onChange]
    );
    const setRangeValue = useCallback12(({ currentTarget }) => {
      const target = ref.current;
      if (target) {
        const value = Number.isNaN(currentTarget.valueAsNumber) ? target.min : currentTarget.valueAsNumber;
        nativeSetValue(target, value);
        nativeSetValue(currentTarget, target.valueAsNumber);
      }
    }, []);
    useEffect5(() => {
      const control = ref.current;
      if (control) {
        nativeSetValue(numberRef.current, control.valueAsNumber);
      }
    }, []);
    const width = String(max).length;
    return /* @__PURE__ */ React58.createElement(
      Wrapper4,
      {
        className,
        ...disabled && { "aria-disabled": true },
        ...invalid && { "aria-invalid": true }
      },
      min,
      /* @__PURE__ */ React58.createElement(
        Control3,
        {
          disabled,
          max,
          min,
          onChange: wrappedOnChange,
          ref,
          step: step2,
          ...delegated
        }
      ),
      max,
      /* @__PURE__ */ React58.createElement(
        NumberInput,
        {
          "aria-label": "Adjust slider value",
          className: "numberControl",
          disabled,
          end: false,
          min,
          max,
          onBlur: setRangeValue,
          ref: numberRef,
          step: step2,
          style: { width: `${width}ch` },
          ...invalid && { "aria-invalid": true }
        }
      )
    );
  }
);

// lib/components/SystemButton/SystemButton.tsx
import React59, { forwardRef as forwardRef48 } from "react";

// lib/components/SystemButton/SystemButton.styles.tsx
import styled45 from "@emotion/styled";
var Root37 = styled45.button(
  font(7),
  {
    alignItems: "center",
    backgroundImage: "none",
    borderRadius: "9999px",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    display: "inline-flex",
    gap: "12px",
    justifyContent: "center",
    minHeight: "40px",
    padding: "4px 12px",
    textAlign: "center",
    "&:active": {
      boxShadow: `inset 0px 4px 8px 0px ${rgb3("0 0 0", "10%")}`
    },
    "&:disabled": {
      pointerEvents: "none"
    }
  },
  (props) => {
    const { theme, variant } = props;
    switch (variant) {
      case "negative-primary": {
        return {
          backgroundColor: flat(theme.color.system.negative),
          borderColor: "transparent",
          color: "white",
          "&:hover": {
            backgroundColor: flat(theme.color.system.negative, "80%"),
            boxShadow: "0px 2px 2px 0px rgba(153, 153, 153, 0.1)"
          },
          "&:active": {
            backgroundColor: flat(theme.color.system.negative, "80%"),
            boxShadow: "inset 0px 2px 2px 0px rgba(153, 153, 153, 0.1)"
          }
        };
      }
      case "negative-secondary": {
        return {
          backgroundColor: "white",
          borderColor: flat(theme.color.system.negative, "20%"),
          color: flat(theme.color.system.negative),
          "&:hover": {
            backgroundColor: flat(theme.color.system.negative, "5%"),
            borderColor: flat(theme.color.system.negative, "20%")
          },
          "&:active": {
            borderColor: flat(theme.color.system.negative),
            boxShadow: "none"
          }
        };
      }
      case "negative-ghost": {
        return {
          backgroundColor: "white",
          borderColor: "white",
          color: flat(theme.color.system.negative),
          "&:hover": {
            backgroundColor: flat(theme.color.system.negative, "5%")
          },
          "&:active": {
            boxShadow: `inset 0px 2px 4px 0px ${flat(theme.color.system.negative, "20%")}`
          }
        };
      }
    }
    return {};
  }
);
var Content14 = styled45(Typography_default)({
  alignItems: "center",
  color: "inherit",
  display: "inline-grid",
  gap: "8px",
  gridAutoFlow: "column",
  justifyContent: "center"
});

// lib/components/SystemButton/SystemButton.tsx
function SystemButton(props, ref) {
  const {
    variant = "negative-primary",
    children,
    disabled,
    icon,
    iconPosition = "start",
    type = "button",
    ...restProps
  } = props;
  return /* @__PURE__ */ React59.createElement(
    Root37,
    {
      ...restProps,
      ref,
      disabled: !!disabled,
      type,
      variant
    },
    iconPosition === "start" && icon && /* @__PURE__ */ React59.createElement(React59.Fragment, null, icon),
    /* @__PURE__ */ React59.createElement(Content14, { level: "7" }, children),
    iconPosition === "end" && icon && /* @__PURE__ */ React59.createElement(React59.Fragment, null, icon)
  );
}
var SystemButton_default = forwardRef48(SystemButton);

// lib/components/Table/Table.tsx
import React72, {
  forwardRef as forwardRef51,
  useImperativeHandle as useImperativeHandle3,
  useCallback as useCallback18,
  useEffect as useEffect6,
  useMemo as useMemo6,
  useState as useState11
} from "react";
import { AnimatePresence as AnimatePresence4, motion as motion7 } from "framer-motion";

// lib/components/Table/TableBodyRow.tsx
import React61, { useCallback as useCallback14 } from "react";

// lib/components/Table/TableBodyRow.styles.tsx
import isPropValid11 from "@emotion/is-prop-valid";
import styled46 from "@emotion/styled";
function getRowBackground({ disabled = false, isNew = false, isSelected = false, theme }) {
  if (disabled)
    return flat(theme.color.system.gray, "10%");
  if (isNew)
    return flat(theme.color.system.notice, "5%");
  if (isSelected)
    return flat(theme.color.brand.primary, "5%");
  return "white";
}
var TableBodyRow = styled46("tr", {
  shouldForwardProp: (prop) => isPropValid11(prop) && !["disabled", "isNew"].includes(prop)
})(
  {
    borderStyle: "solid",
    display: "grid",
    gridAutoFlow: "column",
    gridTemplateColumns: "repeat(auto-fit, minmax(5rem, 1fr))",
    minHeight: "56px",
    paddingInline: "16px"
  },
  ({ disabled, isNew, isSelected, theme, variant, onClick }) => ({
    borderWidth: variant === "ghost" ? "1px 0px 0px 0px" : "1px 1px 0px 1px",
    backgroundColor: getRowBackground({
      disabled: disabled ?? false,
      isNew: isNew ?? false,
      isSelected: isSelected ?? false,
      theme
    }),
    borderColor: flat(theme.color.system.gray, "20%"),
    color: flat(theme.color.system.gray, "60%"),
    ...onClick && {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: flat(theme.color.brand.primary, "5%")
      }
    }
  })
);
var RoundedTableBodyRow = styled46.tr(
  {
    backgroundColor: "white",
    borderRadius: "8px",
    display: "grid",
    gridAutoFlow: "column",
    gridTemplateColumns: "repeat(auto-fit, minmax(5rem, 1fr))",
    minHeight: "72px",
    paddingInline: "16px",
    marginTop: "8px"
  },
  ({ theme, onClick }) => ({
    boxShadow: `0 2px 2px ${flat(theme.color.system.gray, "10%")}`,
    border: `1px solid  ${flat(theme.color.system.gray, "20%")}`,
    color: flat(theme.color.system.gray, "60%"),
    ...onClick && {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: flat(theme.color.brand.primary, "5%")
      }
    }
  })
);

// lib/components/Table/TableBodyCell.tsx
import React60, { forwardRef as forwardRef49, useCallback as useCallback13, useState as useState8 } from "react";
import { flexRender } from "@tanstack/react-table";

// lib/components/Table/TableBodyCell.styles.tsx
import styled47 from "@emotion/styled";
var TableBodyCell = styled47.td(
  {
    alignItems: "center",
    display: "inline-flex",
    lineHeight: "1.5rem",
    minWidth: "64px",
    padding: "12px 8px",
    textAlign: "start",
    "& > .text": {
      display: "-webkit-box",
      WebkitLineClamp: "3",
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      wordBreak: "break-word"
    }
  },
  ({ theme }) => ({
    fontSize: theme.fontSize[8],
    fontWeight: theme.fontWeight,
    letterSpacing: theme.letterSpacing,
    color: rgb3(theme.color.system.gray, "100%")
  })
);

// lib/components/Table/TableBodyCell.tsx
var TableBodyCellWithoutPopover = forwardRef49(
  ({ cell, ...restProps }, ref) => /* @__PURE__ */ React60.createElement(
    TableBodyCell,
    {
      className: "TableBodyCell",
      key: cell.id,
      "data-col-id": cell.id,
      "data-col-index": cell.index,
      ...restProps
    },
    /* @__PURE__ */ React60.createElement(
      "span",
      {
        className: "text",
        ref
      },
      flexRender(cell.column.columnDef.cell, cell.getContext())
    )
  )
);
function TableBodyCell2({ cell, ...restProps }) {
  const [isTextClamped, setIsTextClamped] = useState8(null);
  const evaluateIsTextClamped = useCallback13((node) => {
    if (node) {
      setIsTextClamped(node.scrollHeight > node.offsetHeight);
    }
  }, []);
  if (isTextClamped === false) {
    return /* @__PURE__ */ React60.createElement(
      TableBodyCellWithoutPopover,
      {
        cell,
        ...restProps
      }
    );
  }
  return /* @__PURE__ */ React60.createElement(PopoverProvider_default, null, /* @__PURE__ */ React60.createElement(
    Popover_default,
    {
      variant: "primary",
      trigger: /* @__PURE__ */ React60.createElement(
        TableBodyCellWithoutPopover,
        {
          cell,
          ...isTextClamped === null && { ref: evaluateIsTextClamped },
          ...restProps
        }
      )
    },
    cell.row.original[cell.column.id]
  ));
}

// lib/components/Table/TableBodyRow.tsx
function TableBodyRow2(props) {
  const { rowProps, disabled = false, isNew = false, row, variant, rowAction } = props;
  const Comp = ["primary", "ghost"].includes(variant) ? TableBodyRow : RoundedTableBodyRow;
  const cellElements = row.getVisibleCells().map((cell) => /* @__PURE__ */ React61.createElement(
    TableBodyCell2,
    {
      key: cell.id,
      cell
    }
  ));
  const onClick = useCallback14(
    (e) => {
      if (e.currentTarget.contains(e.target) && rowAction) {
        rowAction(row.original, e);
      }
    },
    [rowAction, row.original]
  );
  return /* @__PURE__ */ React61.createElement(
    Comp,
    {
      disabled,
      isNew,
      isSelected: row.getIsSelected(),
      variant,
      onClick: rowAction ? onClick : void 0,
      ...rowProps
    },
    cellElements
  );
}

// lib/components/Table/TableHeaderRow.tsx
import React63 from "react";

// lib/components/Table/TableHeaderRow.styles.tsx
import styled48 from "@emotion/styled";
var TableHeaderRow = styled48.tr(
  {
    borderStyle: "solid",
    display: "grid",
    gridAutoFlow: "column",
    gridTemplateColumns: "repeat(auto-fit, minmax(5rem, 1fr))",
    minHeight: "40px",
    paddingInline: "16px"
  },
  ({ theme, variant }) => ({
    borderWidth: variant === "ghost" ? "0px 0px 2px 0px" : "0px 1px 2px 1px",
    background: flat(theme.color.system.gray, "5%"),
    borderColor: flat(theme.color.system.gray, "20%"),
    color: flat(theme.color.system.gray, "40%")
  })
);

// lib/components/Table/TableHeaderCell.tsx
import React62 from "react";
import { UilArrowDown as ArrowDownIcon, UilArrowUp as ArrowUpIcon } from "@iconscout/react-unicons";
import { flexRender as flexRender2 } from "@tanstack/react-table";

// lib/components/Table/TableHeaderCell.styles.tsx
import styled49 from "@emotion/styled";
var TableHeaderCell = styled49.th(
  {
    alignItems: "stretch",
    border: "none",
    display: "inline-flex",
    justifyContent: "stretch",
    lineHeight: "1",
    padding: "0px",
    position: "relative",
    textAlign: "start"
  },
  ({ theme }) => ({
    fontSize: theme.fontSize[9],
    fontWeight: theme.fontWeight,
    letterSpacing: theme.letterSpacing,
    color: rgb3(theme.color.system.gray, "80%")
  })
);
var TableHeaderButton = styled49.button(
  {
    alignItems: "center",
    background: "none",
    border: "none",
    color: "inherit",
    display: "flex",
    flex: "auto",
    justifyContent: "start",
    gap: "6px",
    paddingInline: "8px"
  },
  ({ canSort, theme }) => ({
    fontSize: theme.fontSize[9],
    fontWeight: theme.fontWeight,
    letterSpacing: theme.letterSpacing,
    lineHeight: theme.lineHeight[9],
    cursor: canSort ? "pointer" : "default",
    "&:hover": {
      backgroundColor: canSort ? flat(theme.color.system.gray, "20%") : "transparent"
    }
  })
);
var TableHeaderSortIcon = styled49.span({
  alignItems: "center",
  display: "flex",
  justifyContent: "center"
});

// lib/components/Table/TableHeaderCell.tsx
var SORT_OPTIONS = {
  ASC: "asc",
  DESC: "desc"
};
var SORT_OPTION_LABELS = {
  [SORT_OPTIONS.ASC]: "Sort Ascending",
  [SORT_OPTIONS.DESC]: "Sort Descending"
};
var TableHeaderCell2 = ({ header, sorting }) => {
  if (header.isPlaceholder)
    return null;
  const canSort = header.column.getCanSort() && sorting === true;
  const handleClick = canSort ? header.column.getToggleSortingHandler() : null;
  const isSorted = header.column.getIsSorted();
  const descending = isSorted === SORT_OPTIONS.DESC;
  const sortingLabelElement = isSorted && /* @__PURE__ */ React62.createElement(
    TableHeaderSortIcon,
    {
      "aria-hidden": true,
      "aria-label": `Sort ${descending ? "Descending" : "Ascending"}`,
      onClick: handleClick
    },
    descending ? /* @__PURE__ */ React62.createElement(ArrowDownIcon, { size: 16 }) : /* @__PURE__ */ React62.createElement(ArrowUpIcon, { size: 16 })
  );
  const label = isSorted ? SORT_OPTION_LABELS[isSorted] : "Sort Column";
  return /* @__PURE__ */ React62.createElement(
    TableHeaderCell,
    {
      key: header.id,
      className: "TableHeaderCell",
      "data-col-id": header.id,
      "data-col-index": header.index,
      "aria-label": label
    },
    /* @__PURE__ */ React62.createElement(
      TableHeaderButton,
      {
        as: canSort ? "button" : "div",
        canSort,
        onClick: handleClick,
        type: "button"
      },
      flexRender2(header.column.columnDef.header, header.getContext()),
      canSort && sortingLabelElement
    )
  );
};
var TableHeaderCell_default = TableHeaderCell2;

// lib/components/Table/TableHeaderRow.tsx
function TableHeaderRow2(props) {
  const { group, sorting, variant } = props;
  const cellElements = group.headers.map((header) => /* @__PURE__ */ React63.createElement(
    TableHeaderCell_default,
    {
      key: header.id,
      header,
      sorting
    }
  ));
  return /* @__PURE__ */ React63.createElement(
    TableHeaderRow,
    {
      variant,
      className: "TableHeaderRow"
    },
    cellElements
  );
}

// lib/components/Table/TablePaginationBar.tsx
import React64, { useCallback as useCallback15, useMemo as useMemo2 } from "react";
import * as Toolbar from "@radix-ui/react-toolbar";
import { useTheme as useTheme2 } from "@emotion/react";
import { UilAngleLeft as UilAngleLeft4, UilAngleRight as UilAngleRight4, UilPrevious, UilStepForward } from "@iconscout/react-unicons";

// lib/components/Table/TablePaginationBar.styles.tsx
import styled50 from "@emotion/styled";
import { UilEllipsisH } from "@iconscout/react-unicons";
import { Separator as BaseSeparator } from "@radix-ui/react-toolbar";
var Root38 = styled50.div(
  {
    alignItems: "center",
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    borderStyle: "solid",
    padding: "6px"
  },
  ({ theme, variant }) => ({
    borderWidth: variant === "ghost" ? "0px" : "0px 1px 1px 1px",
    backgroundColor: flat(theme.color.system.gray, "5%"),
    borderColor: flat(theme.color.system.gray, "20%"),
    boxShadow: variant === "ghost" ? "initial" : `${theme.boxShadow.drop} ${translucent(theme.color.shadow.drop, "10%")}`
  })
);
var Separator2 = styled50(BaseSeparator)(
  {
    height: "28px",
    width: "2px"
  },
  (props) => {
    const { theme } = props;
    return {
      backgroundColor: flat(theme.color.system.gray, "20%")
    };
  }
);
var ToolbarButton = styled50(Button_default)(
  {
    minHeight: "40px",
    minWidth: "40px",
    padding: "0"
  },
  (props) => {
    const { theme } = props;
    return {
      "&:hover": {
        borderRadius: theme.radius[1],
        boxShadow: "none"
      },
      "&:disabled": {
        color: flat(theme.color.system.gray, "20%"),
        cursor: "not-allowed"
      }
    };
  }
);
var PaginationCountWrapper = styled50.div`
  display: flex;
  align-items: center;
`;
var EllipsisIcon = styled50(UilEllipsisH)`
  margin-top: 6px;
`;

// lib/components/Table/TablePaginationBar.tsx
function calculateRangeForDisplay(currentPage, pageSize, totalRowsInDataSet) {
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalRowsInDataSet);
  return `${startIndex}-${endIndex} of ${totalRowsInDataSet}`;
}
function TablePaginationBar(props) {
  const { table, state, variant, rowCount, manualPagination } = props;
  const theme = useTheme2();
  const pageCount = table.getPageCount();
  const currentPage = state.pagination.pageIndex;
  const canGetNextPage = table.getCanNextPage();
  const canGetPreviousPage = table.getCanPreviousPage();
  const handlePaginationClick = useCallback15(
    (value) => {
      table.setPageIndex(value);
    },
    [state.pagination.pageIndex]
  );
  let pages = [];
  if (pageCount <= 5) {
    Array.from(Array(pageCount).keys()).forEach((page) => {
      pages.push(page);
    });
  } else if (currentPage <= 2) {
    pages = [0, 1, 2, 3, 4];
  } else if (currentPage >= pageCount - 3) {
    pages = [pageCount - 5, pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1];
  } else {
    pages = [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
  }
  const paginationElements = pages.map((page) => {
    const pageNumberToDisplay = page + 1;
    return /* @__PURE__ */ React64.createElement(
      Toolbar.Button,
      {
        asChild: true,
        key: page
      },
      /* @__PURE__ */ React64.createElement(
        ToolbarButton,
        {
          variant: "ghost",
          onClick: () => handlePaginationClick(page),
          disabled: page === currentPage
        },
        pageNumberToDisplay
      )
    );
  });
  const handleClickFirst = useCallback15(() => {
    table.setPageIndex(0);
  }, [table]);
  const handleClickLast = useCallback15(() => {
    table.setPageIndex(pageCount - 1);
  }, [table, pageCount]);
  const handleClickNext = useCallback15(() => {
    table.nextPage();
  }, [table.nextPage]);
  const handleClickPrevious = useCallback15(() => {
    table.previousPage();
  }, [table.previousPage]);
  const totalRowsInDataset = manualPagination ? rowCount : table.getFilteredRowModel().rows.length;
  const formattedDisplayForDataset = useMemo2(
    () => calculateRangeForDisplay(currentPage + 1, state.pagination.pageSize, totalRowsInDataset),
    [currentPage, state.pagination.pageSize, totalRowsInDataset]
  );
  return /* @__PURE__ */ React64.createElement(Toolbar.Root, { asChild: true }, /* @__PURE__ */ React64.createElement(Root38, { variant }, /* @__PURE__ */ React64.createElement(
    Typography_default,
    {
      level: "8",
      color: "gray",
      strength: "80%",
      as: "p"
    },
    formattedDisplayForDataset
  ), /* @__PURE__ */ React64.createElement(Separator2, { orientation: "vertical" }), /* @__PURE__ */ React64.createElement(PaginationCountWrapper, null, /* @__PURE__ */ React64.createElement(Toolbar.Button, { asChild: true }, /* @__PURE__ */ React64.createElement(
    ToolbarButton,
    {
      variant: "ghost",
      onClick: handleClickFirst,
      disabled: !canGetPreviousPage
    },
    /* @__PURE__ */ React64.createElement(UilPrevious, null)
  )), /* @__PURE__ */ React64.createElement(Toolbar.Button, { asChild: true }, /* @__PURE__ */ React64.createElement(
    ToolbarButton,
    {
      variant: "ghost",
      onClick: handleClickPrevious,
      disabled: !canGetPreviousPage
    },
    /* @__PURE__ */ React64.createElement(UilAngleLeft4, null)
  )), pages[0] !== 0 && /* @__PURE__ */ React64.createElement(
    EllipsisIcon,
    {
      color: canGetPreviousPage ? flat(theme.color.brand.primary) : flat(theme.color.system.gray, "20%"),
      size: 14
    }
  ), paginationElements, pages[pages.length - 1] !== pageCount - 1 && /* @__PURE__ */ React64.createElement(
    EllipsisIcon,
    {
      color: canGetNextPage ? flat(theme.color.brand.primary) : flat(theme.color.system.gray, "20%"),
      size: 14
    }
  ), /* @__PURE__ */ React64.createElement(Toolbar.Button, { asChild: true }, /* @__PURE__ */ React64.createElement(
    ToolbarButton,
    {
      variant: "ghost",
      onClick: handleClickNext,
      disabled: !canGetNextPage
    },
    /* @__PURE__ */ React64.createElement(UilAngleRight4, null)
  )), /* @__PURE__ */ React64.createElement(Toolbar.Button, { asChild: true }, /* @__PURE__ */ React64.createElement(
    ToolbarButton,
    {
      variant: "ghost",
      onClick: handleClickLast,
      disabled: !canGetNextPage
    },
    /* @__PURE__ */ React64.createElement(UilStepForward, null)
  )))));
}
var TablePaginationBar_default = TablePaginationBar;

// lib/components/Table/InlineTableFilter.tsx
import React65, { useMemo as useMemo3 } from "react";
import { useTheme as useTheme3 } from "@emotion/react";
import { UilTimes as UilTimes4 } from "@iconscout/react-unicons";

// lib/components/Table/InlineTableFilter.styles.tsx
import styled51 from "@emotion/styled";
var InlineTableFilterWrapper = styled51.div(
  {
    padding: "12px 16px",
    borderStyle: "solid",
    borderWidth: "1px",
    minHeight: "56px",
    borderTop: "none",
    display: "flex",
    gap: "16px",
    alignItems: "center",
    whiteSpace: "nowrap"
  },
  ({ theme }) => ({
    borderColor: flat(theme.color.system.gray, "20%"),
    backgroundColor: flat(theme.color.system.gray, "10%")
  })
);
var Separator3 = styled51.div(
  {
    height: "32px",
    width: "2px"
  },
  (props) => {
    const { theme } = props;
    return {
      backgroundColor: flat(theme.color.system.gray, "20%")
    };
  }
);
var FilterPillButtonWrapper = styled51.div({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
  flex: "1 1 0%"
});
var FilterPillButton = styled51.button(
  {
    padding: "4px 8px",
    borderStyle: "solid",
    borderWidth: "1px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "white",
    cursor: "pointer",
    borderRadius: "4px",
    gap: "12px"
  },
  ({ theme }) => ({
    borderColor: flat(theme.color.system.gray, "20%")
  })
);
var FilterWrapper = styled51.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "2px"
});
var ClearAllButton = styled51.button(
  {
    background: "none",
    color: "inherit",
    border: "none",
    cursor: "pointer",
    outline: "inherit"
  },
  ({ theme }) => ({
    "&:hover": {
      backgroundColor: flat(theme.color.brand.primary, "10%")
    }
  })
);

// lib/components/Table/InlineTableFilter.tsx
function InlineTableFilter(props) {
  const { columnFilters, setColumnFilters, filters } = props;
  const theme = useTheme3();
  const filtersApplied = useMemo3(() => columnFilters.map((columnFilter) => columnFilter.value).flat(), [columnFilters]);
  const handleRemoveFilter = (filter) => {
    const filterGroup = columnFilters.find((columnFilter) => columnFilter.value.includes(filter));
    if (filterGroup) {
      const newColumnFilters = columnFilters.map((columnFilter) => {
        if (columnFilter.id === filterGroup.id) {
          return {
            ...columnFilter,
            value: columnFilter.value.filter((item) => item !== filter)
          };
        }
        return columnFilter;
      });
      newColumnFilters.forEach((columnFilter, index) => {
        if ((columnFilter.value || []).length === 0) {
          newColumnFilters.splice(index, 1);
        }
      });
      setColumnFilters(newColumnFilters);
    }
  };
  const handleClearFilters = () => {
    setColumnFilters([]);
  };
  return /* @__PURE__ */ React65.createElement(InlineTableFilterWrapper, null, /* @__PURE__ */ React65.createElement(
    Typography_default,
    {
      color: "gray",
      strength: "80%",
      level: "7"
    },
    filtersApplied.length,
    " Filters"
  ), /* @__PURE__ */ React65.createElement(Separator3, null), /* @__PURE__ */ React65.createElement(FilterPillButtonWrapper, null, filtersApplied.map((filter) => {
    var _a2;
    const groupName = (_a2 = filters.find((filterGroup) => filterGroup.filterOptions.includes(filter))) == null ? void 0 : _a2.filterName;
    return /* @__PURE__ */ React65.createElement(React65.Fragment, { key: `${groupName}${filter}` }, /* @__PURE__ */ React65.createElement(FilterPillButton, { onClick: () => handleRemoveFilter(filter) }, /* @__PURE__ */ React65.createElement(FilterWrapper, null, /* @__PURE__ */ React65.createElement(
      Typography_default,
      {
        color: "gray",
        strength: "60%",
        level: "8"
      },
      groupName
    ), ":", /* @__PURE__ */ React65.createElement(
      Typography_default,
      {
        color: "gray",
        strength: "100%",
        level: "8"
      },
      filter
    )), /* @__PURE__ */ React65.createElement(UilTimes4, { color: flat(theme.color.system.gray, "40%") })));
  })), /* @__PURE__ */ React65.createElement(
    ClearAllButton,
    {
      type: "button",
      onClick: handleClearFilters
    },
    /* @__PURE__ */ React65.createElement(
      Typography_default,
      {
        color: "primary",
        strength: "100%",
        level: "7"
      },
      "Clear All"
    )
  ));
}
var InlineTableFilter_default = InlineTableFilter;

// lib/components/Table/useTable.tsx
import React66, { useCallback as useCallback16, useMemo as useMemo4, useRef as useRef5 } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
var HELPER = createColumnHelper();
var COLUMNS = {
  SELECTION: HELPER.display({
    cell: ({ row }) => {
      const handleCheckedChange = row.getToggleSelectedHandler();
      const isChecked = row.getIsSelected();
      return /* @__PURE__ */ React66.createElement(
        Checkbox_default,
        {
          checked: isChecked,
          onCheckedChange: handleCheckedChange,
          onClick: (e) => e.stopPropagation()
        }
      );
    },
    header: ({ table }) => {
      const isChecked = table.getIsAllRowsSelected();
      const isIndeterminate = !isChecked && table.getIsSomeRowsSelected();
      const value = isIndeterminate ? "indeterminate" : isChecked;
      const handleCheckedChange = useCallback16(
        (nextValue) => {
          if (typeof nextValue === "boolean")
            table.toggleAllRowsSelected(nextValue);
        },
        [table.toggleAllRowsSelected]
      );
      return /* @__PURE__ */ React66.createElement(
        Checkbox_default,
        {
          checked: value,
          onCheckedChange: handleCheckedChange
        }
      );
    },
    id: "selection",
    enableHiding: false
  })
};
function createRowModels({ pagination, sorting, search }) {
  return {
    core: getCoreRowModel(),
    pagination: pagination ? getPaginationRowModel() : void 0,
    sorting: sorting ? getSortedRowModel() : void 0,
    search: search ? getFilteredRowModel() : void 0
  };
}
function useTable(props) {
  const { columns, pagination, selection, sorting, search, rowCount, manualPaginationState, ...rest } = props;
  const rowModels = useRef5(null);
  if (!rowModels.current)
    rowModels.current = createRowModels({ pagination, sorting, search });
  const finalColumns = useMemo4(() => {
    const included = [...columns];
    if (selection)
      included.unshift(COLUMNS.SELECTION);
    return included;
  }, [columns, selection]);
  const pageCount = manualPaginationState && Math.ceil(rowCount / manualPaginationState.pageSize);
  return useReactTable({
    columns: finalColumns,
    enableRowSelection: !!selection,
    pageCount,
    getCoreRowModel: rowModels.current.core,
    getPaginationRowModel: rowModels.current.pagination,
    getSortedRowModel: rowModels.current.sorting,
    getFilteredRowModel: rowModels.current.search,
    ...rest
  });
}

// lib/components/Table/Table.styles.tsx
import styled52 from "@emotion/styled";
var Table = styled52.table(
  {
    display: "block",
    "--columns": "1fr repeat(3, 216px)",
    "&.table--subgrid": {
      "--columns": "repeat(var(--fragments), auto)",
      display: "grid",
      gridTemplateColumns: "var(--columns)",
      "& thead, & tbody, & tr": {
        gridTemplateColumns: "subgrid",
        gridColumn: "1 / -1"
        // Span entire row.
      }
    }
  },
  ({ theme }) => ({
    boxShadow: `${theme.boxShadow.drop} ${translucent(theme.color.shadow.drop, "10%")}`
  })
);
var TableBody = styled52.tbody(
  {
    borderStyle: "solid",
    borderWidth: "0px",
    display: "grid"
  },
  ({ theme }) => ({
    borderColor: flat(theme.color.system.gray, "20%")
  })
);
var TableHeader = styled52.thead`
  display: grid;
`;
var TableEmptyStateWrapper = styled52.div(
  {
    padding: "16px",
    textAlign: "center",
    borderStyle: "solid"
  },
  ({ theme, variant }) => ({
    borderWidth: variant === "ghost" ? "0px 0px 1px 0px" : "0px 1px 1px 1px",
    borderColor: flat(theme.color.system.gray, "20%")
  })
);

// lib/components/Table/TableTitleBar.tsx
import React71, { useCallback as useCallback17, useMemo as useMemo5, useState as useState10 } from "react";
import { useTheme as useTheme6 } from "@emotion/react";
import { UilCheck as CheckIcon2, UilSearch as UilSearch2, UilAngleDown as UilAngleDown4, UilAngleLeft as UilAngleLeft5 } from "@iconscout/react-unicons";
import * as Toolbar3 from "@radix-ui/react-toolbar";
import { AnimatePresence as AnimatePresence3, motion as motion6 } from "framer-motion";

// lib/components/Table/TableTitleBar.styles.tsx
import styled53 from "@emotion/styled";
import isPropValid12 from "@emotion/is-prop-valid";
import * as Toolbar2 from "@radix-ui/react-toolbar";
var Root41 = styled53(Toolbar2.Root, {
  shouldForwardProp: isPropValid12
})(
  {
    alignItems: "center",
    display: "grid",
    gridAutoFlow: "column",
    gridTemplateColumns: "1fr auto",
    height: 56,
    paddingInline: "16px",
    borderStyle: "solid"
  },
  ({ barState, theme, variant, isTableCollapsed }) => ({
    borderRadius: isTableCollapsed ? "8px" : "8px 8px 0px 0px",
    borderWidth: variant === "ghost" ? "0px 0px 1px 0px" : "1px",
    backgroundColor: barState === BAR_STATES.BULK_ACTIONS ? flat(theme.color.brand.primary) : "white",
    borderColor: barState === BAR_STATES.BULK_ACTIONS ? flat(theme.color.brand.primary) : flat(theme.color.system.gray, "20%"),
    color: barState === BAR_STATES.BULK_ACTIONS ? "white" : flat(theme.color.system.gray)
  })
);
var Actions5 = styled53.div({
  alignItems: "center",
  display: "flex",
  gap: 12,
  justifyContent: "flex-end"
});
var DefaultActionsContainer = styled53(Actions5, {
  shouldForwardProp: (prop) => !["barState"].includes(prop)
})(({ barState }) => ({
  display: barState === BAR_STATES.BULK_ACTIONS ? "none" : "flex"
}));
var Dot4 = styled53("div", {
  shouldForwardProp: (prop) => prop !== "color"
})(({ theme, color: color2 }) => {
  return {
    height: "8px",
    width: "8px",
    borderRadius: "50%",
    backgroundColor: color2 ?? flat(theme.color.system.gray, "60%")
  };
});
var Count = styled53(Typography_default)({
  fontWeight: 500
});
var Indicator10 = styled53("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px"
});
var Indicators2 = styled53("div")({
  display: "flex",
  gap: "16px"
});
var SearchButton = styled53.button(
  {
    background: "none",
    color: "inherit",
    border: "none",
    padding: "5px",
    borderRadius: "9px",
    cursor: "pointer",
    outline: "inherit",
    margin: "0 12px"
  },
  ({ theme }) => ({
    "&:hover": {
      backgroundColor: flat(theme.color.system.gray, "20%")
    }
  })
);
var Separator5 = styled53(Toolbar2.Separator)(
  {
    width: "1px",
    marginLeft: "12px",
    marginRight: "12px",
    height: "32px"
  },
  ({ theme }) => ({
    backgroundColor: flat(theme.color.brand.primary, "90%")
  })
);
var Separators = styled53("div")(
  {
    position: "relative"
  },
  ({ theme }) => ({
    "&:before": {
      content: '""',
      position: "absolute",
      top: "0",
      left: "0",
      width: "1px",
      height: "100%",
      backgroundColor: flat(theme.color.system.gray, "20%"),
      paddingRight: "8px,"
    },
    "&:after": {
      content: '""',
      position: "absolute",
      top: "0",
      right: "0",
      width: "1px",
      height: "100%",
      backgroundColor: flat(theme.color.system.gray, "20%"),
      paddingLeft: "8px,"
    }
  })
);
var Title5 = styled53.div({
  alignItems: "center",
  display: "flex",
  gap: 8,
  justifyContent: "flex-start",
  paddingLeft: "8px"
});
var IconButton3 = styled53.div({
  label: "collapse-button",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer"
});
var Styles4 = {
  Actions: Actions5,
  Count,
  DefaultActionsContainer,
  Dot: Dot4,
  Indicator: Indicator10,
  Indicators: Indicators2,
  Root: Root41,
  SearchButton,
  Separator: Separator5,
  Separators,
  Title: Title5,
  IconButton: IconButton3
};

// lib/components/Table/TableToolbarButton.styles.tsx
import styled54 from "@emotion/styled";
var ToolbarButton2 = styled54(Button_default)(
  {
    padding: "2px 12px",
    minHeight: "32px",
    "& > span": {
      lineHeight: "24px"
    }
  },
  ({ theme }) => ({
    "& > span": {
      fontSize: theme.fontSize["8"]
    },
    "&:hover": {
      backgroundColor: flat(theme.color.brand.primary, "90%")
    },
    "&:active": {
      backgroundColor: flat(theme.color.brand.primary, "90%")
    },
    "&:disabled": {
      color: flat(theme.color.system.gray, "40%"),
      backgroundColor: flat(theme.color.brand.primary)
    }
  })
);
var Styles5 = {
  ToolbarButton: ToolbarButton2
};

// lib/components/TextInput/TextInput.tsx
import React67, { forwardRef as forwardRef50 } from "react";
import { UilCheckCircle as UilCheckCircle3, UilExclamationCircle as UilExclamationCircle4 } from "@iconscout/react-unicons";

// lib/components/TextInput/TextInput.styles.tsx
import styled55 from "@emotion/styled";
var Root42 = styled55.label(
  {
    label: "text-input",
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  ({ error, theme }) => ({
    p: {
      color: flat(theme.color.system.gray, "40%")
    },
    ".secondary": {
      color: error ? flat(theme.color.system.negative) : flat(theme.color.system.gray, "40%")
    }
  })
);
var InputWrapper = styled55.div(
  {
    position: "relative",
    svg: {
      position: "absolute",
      top: "8px",
      width: "16px",
      height: "16px"
    },
    "svg:first-child": {
      left: "8px",
      "& + input": {
        paddingLeft: "28px"
      }
    },
    "svg:last-child": {
      right: "8px"
    }
  },
  ({ theme }) => ({
    "--accent": flat(theme.color.system.gray, "40%"),
    "svg:first-child": {
      fill: flat(theme.color.system.gray, "60%")
    },
    "&:hover input:not(:disabled)": {
      "--accent": flat(theme.color.brand.primary)
    }
  })
);
var Input2 = styled55.input(
  {
    borderColor: "var(--accent)",
    borderRadius: "4px",
    borderStyle: "solid",
    borderWidth: "1px",
    padding: "8px 12px",
    outlineWidth: 0,
    width: "100%",
    "&[aria-invalid]": {
      paddingRight: "28px"
    },
    "&:disabled": {
      backgroundColor: "var(--accent)"
    },
    "&:focus": {
      color: "var(--accent)"
    }
  },
  ({ theme }) => ({
    /* react-hook-form doesn't leverage native :invalid and :valid */
    '&[aria-invalid="true"]': {
      "--accent": flat(theme.color.system.negative),
      "& + svg": {
        fill: flat(theme.color.system.negative)
      }
    },
    '&[aria-invalid="false"]': {
      "--accent": flat(theme.color.system.positive),
      "& + svg": {
        fill: flat(theme.color.system.positive)
      }
    },
    "&:focus": {
      "--accent": flat(theme.color.brand.primary)
    }
  })
);
var MandatoryLabel2 = styled55(Typography_default)({}, ({ theme }) => ({
  backgroundColor: rgb(theme.colorV2.system.negative, 600),
  lineHeight: 0,
  width: "0.2em",
  height: "0.2em",
  display: "inline-block",
  borderRadius: 9999
}));
var ErrorDescription = styled55(Typography_default)({}, ({ theme }) => ({
  color: flat(theme.color.system.negative)
}));

// lib/components/TextInput/TextInput.tsx
var TextInput = forwardRef50(
  ({ description, error = false, icon, label, required = false, touched = false, ...restProps }, ref) => {
    const secondary = typeof error === "string" ? error : description;
    return /* @__PURE__ */ React67.createElement(Root42, { error: typeof error === "string" }, /* @__PURE__ */ React67.createElement(
      Typography_default,
      {
        as: "p",
        level: "9"
      },
      label,
      required ? /* @__PURE__ */ React67.createElement(React67.Fragment, null, " ", /* @__PURE__ */ React67.createElement(
        MandatoryLabel2,
        {
          "aria-label": "Required",
          as: "sup",
          level: "7"
        }
      )) : null
    ), /* @__PURE__ */ React67.createElement(InputWrapper, null, icon, /* @__PURE__ */ React67.createElement(
      Input2,
      {
        ref,
        required,
        ...touched && { "aria-invalid": !!error },
        ...restProps
      }
    ), !error && touched ? /* @__PURE__ */ React67.createElement(UilCheckCircle3, null) : null, error ? /* @__PURE__ */ React67.createElement(UilExclamationCircle4, null) : null), secondary ? /* @__PURE__ */ React67.createElement(
      Typography_default,
      {
        className: "secondary",
        level: "9"
      },
      secondary
    ) : null);
  }
);
var TextInput_default = TextInput;

// lib/components/Table/TableFiltersMenu.tsx
import React69 from "react";
import { useTheme as useTheme4 } from "@emotion/react";
import { UilFilter } from "@iconscout/react-unicons";
import * as Accordion4 from "@radix-ui/react-accordion";
import * as DropdownMenu5 from "@radix-ui/react-dropdown-menu";

// lib/components/Table/TableFilterMenu.styles.tsx
import styled56 from "@emotion/styled";
import * as DropdownMenu4 from "@radix-ui/react-dropdown-menu";
var FilterButton = styled56.button(
  {
    background: "none",
    color: "inherit",
    border: "none",
    padding: "5px",
    borderRadius: "9px",
    cursor: "pointer",
    outline: "inherit",
    margin: "auto"
  },
  ({ theme }) => ({
    "&:hover": {
      backgroundColor: flat(theme.color.system.gray, "20%")
    }
  })
);
var DropdownMenuContent = styled56(DropdownMenu4.Content)(
  {
    marginTop: "12px",
    borderRadius: "8px",
    background: "white",
    overflowY: "scroll",
    borderWidth: "1px",
    borderStyle: "solid",
    width: "328px",
    maxHeight: "calc(var(--radix-popper-available-height) - 24px)"
  },
  ({ theme }) => ({
    borderColor: flat(theme.color.system.gray, "20%")
  })
);

// lib/components/Table/TableFilterMenuItem.tsx
import React68, { useState as useState9 } from "react";
import { UilAngleDown as UilAngleDown3, UilAngleUp, UilCheck } from "@iconscout/react-unicons";
import * as Accordion3 from "@radix-ui/react-accordion";

// lib/components/Table/TableFilterMenuItem.styles.tsx
import styled57 from "@emotion/styled";
import * as Accordion2 from "@radix-ui/react-accordion";
import * as Checkbox2 from "@radix-ui/react-checkbox";
var SectionHeader = styled57(Accordion2.Header)({}, ({ theme }) => ({
  borderTop: `1px solid ${flat(theme.color.system.gray, "20%")}`
}));
var SectionMenu = styled57(Accordion2.Trigger)(
  {
    width: "100%",
    border: "none",
    padding: "8px 12px",
    fontSize: "14px",
    textAlign: "left",
    cursor: "pointer"
  },
  ({ theme }) => ({
    color: flat(theme.color.system.gray, "60%")
  })
);
var CheckboxIndicator = styled57(Checkbox2.Indicator)({
  display: "inline-flex"
});
var FilterCheckbox = styled57(Checkbox2.Root)(
  {
    width: "16px",
    height: "16px",
    marginRight: "8px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    outline: "none",
    cursor: "pointer"
  },
  (props) => {
    const { theme } = props;
    return {
      // Normal Checked State
      color: rgb(theme.colorV2.system.white),
      backgroundColor: rgb(theme.colorV2.system.white),
      border: `1px solid ${flat(theme.color.system.gray, "40%")}`,
      '&[data-state="checked"]': {
        backgroundColor: flat(theme.color.brand.primary),
        borderWidth: 0
      }
    };
  }
);
var SubMenuItem = styled57.label(
  {
    padding: "12px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "row",
    justifyContent: "start",
    alignItems: "center",
    fontWeight: 500
  },
  ({ theme }) => ({
    color: flat(theme.color.system.gray, "100%"),
    "&:hover": {
      backgroundColor: flat(theme.color.brand.primary, "10%")
    }
  })
);
var ShowMoreButtonContainer = styled57.div(
  {
    padding: "4px 12px"
  },
  ({ theme }) => ({
    borderTop: `1px solid ${rgb(theme.colorV2.system.border, 50)}`
  })
);

// lib/components/Table/TableFilterMenuItem.tsx
var DEFAULT_VISIBLE_OPTIONS_COUNT = 8;
function getOptions(showMoreOptions, isShowMoreOptionsVisible, options) {
  if (isShowMoreOptionsVisible && !showMoreOptions) {
    return options.slice(0, DEFAULT_VISIBLE_OPTIONS_COUNT);
  }
  return options;
}
function TableFilterMenuItem({ filter, handleCheckChange, checkedFilters }) {
  const [showMoreOptions, setShowMoreOptions] = useState9(false);
  const options = filter.filterOptions;
  const isShowMoreOptionsVisible = options.length > DEFAULT_VISIBLE_OPTIONS_COUNT;
  const filterOptions = getOptions(showMoreOptions, isShowMoreOptionsVisible, options);
  return /* @__PURE__ */ React68.createElement(
    Accordion3.Item,
    {
      value: filter.id,
      key: filter.id
    },
    /* @__PURE__ */ React68.createElement(SectionHeader, null, /* @__PURE__ */ React68.createElement(SectionMenu, null, filter.filterName)),
    /* @__PURE__ */ React68.createElement(Accordion3.Content, null, filterOptions.map((option) => {
      return /* @__PURE__ */ React68.createElement(SubMenuItem, { key: option }, /* @__PURE__ */ React68.createElement(
        FilterCheckbox,
        {
          onCheckedChange: (checked) => handleCheckChange(checked, filter.id, option),
          checked: checkedFilters.indexOf(option) > -1
        },
        /* @__PURE__ */ React68.createElement(CheckboxIndicator, null, /* @__PURE__ */ React68.createElement(UilCheck, { size: 16 }))
      ), option);
    }), isShowMoreOptionsVisible && /* @__PURE__ */ React68.createElement(ShowMoreButtonContainer, null, /* @__PURE__ */ React68.createElement(
      Button_default,
      {
        icon: showMoreOptions ? /* @__PURE__ */ React68.createElement(UilAngleUp, null) : /* @__PURE__ */ React68.createElement(UilAngleDown3, null),
        iconPosition: "end",
        type: "button",
        variant: "ghost",
        onClick: () => setShowMoreOptions((prev) => !prev)
      },
      "Show ",
      showMoreOptions ? "Less" : "More"
    )))
  );
}
var TableFilterMenuItem_default = TableFilterMenuItem;

// lib/components/Table/TableFiltersMenu.tsx
function TableFiltersMenu(props) {
  const theme = useTheme4();
  const { filters, setColumnFilters, columnFilters } = props;
  const handleCheckChange = (checked, id, value) => {
    const existingColumnFilters = [...columnFilters];
    if (!existingColumnFilters.find((columnFilter) => columnFilter.id === id)) {
      existingColumnFilters.push({
        id,
        value: []
      });
    }
    const newColumnFilters = existingColumnFilters.map((columnFilter) => {
      if (columnFilter.id === id) {
        if (checked) {
          return {
            ...columnFilter,
            value: [...columnFilter.value, value]
          };
        }
        return {
          ...columnFilter,
          value: columnFilter.value.filter((item) => item !== value)
        };
      }
      return columnFilter;
    });
    newColumnFilters.forEach((columnFilter, index) => {
      if ((columnFilter.value || []).length === 0) {
        newColumnFilters.splice(index, 1);
      }
    });
    setColumnFilters(newColumnFilters);
  };
  return /* @__PURE__ */ React69.createElement(DropdownMenu5.Root, null, /* @__PURE__ */ React69.createElement(DropdownMenu5.Trigger, { asChild: true }, /* @__PURE__ */ React69.createElement(FilterButton, null, /* @__PURE__ */ React69.createElement(
    UilFilter,
    {
      size: 20,
      color: flat(theme.color.system.gray, "60%")
    }
  ))), /* @__PURE__ */ React69.createElement(DropdownMenu5.Portal, null, /* @__PURE__ */ React69.createElement(DropdownMenuContent, { avoidCollisions: false }, /* @__PURE__ */ React69.createElement(DropdownMenu5.Item, { disabled: true }, /* @__PURE__ */ React69.createElement(
    Accordion4.Root,
    {
      type: "multiple",
      defaultValue: [filters[0].id]
    },
    filters.map((filter) => {
      var _a2;
      return /* @__PURE__ */ React69.createElement(
        TableFilterMenuItem_default,
        {
          key: filter.id,
          filter,
          handleCheckChange,
          checkedFilters: ((_a2 = columnFilters.find((columnFilter) => columnFilter.id === filter.id)) == null ? void 0 : _a2.value) || []
        }
      );
    })
  )))));
}
var TableFiltersMenu_default = TableFiltersMenu;

// lib/components/Table/TableColumnSelection.tsx
import React70 from "react";
import * as DropdownMenu7 from "@radix-ui/react-dropdown-menu";
import { UilColumns } from "@iconscout/react-unicons";
import { useTheme as useTheme5 } from "@emotion/react";

// lib/components/Table/TableColumnSelection.styles.tsx
import * as DropdownMenu6 from "@radix-ui/react-dropdown-menu";
import styled58 from "@emotion/styled";
var DropdownMenuContent2 = styled58(DropdownMenu6.Content)(
  {
    marginTop: "12px",
    borderRadius: "8px",
    background: "white",
    overflow: "hidden",
    borderWidth: "1px",
    borderStyle: "solid",
    minWidth: "193px"
  },
  ({ theme }) => ({
    borderColor: flat(theme.color.system.gray, "20%")
  })
);
var ColumnSelectionButton = styled58.button(
  {
    background: "none",
    color: "inherit",
    border: "none",
    padding: "5px",
    borderRadius: "9px",
    cursor: "pointer",
    outline: "inherit",
    margin: "auto"
  },
  ({ theme }) => ({
    "&:hover": {
      backgroundColor: flat(theme.color.system.gray, "20%")
    }
  })
);
var ColumnSelectionCheckbox = styled58.div({
  display: "flex",
  padding: "16px",
  alignItems: "center",
  gap: "8px"
});

// lib/components/Table/TableColumnSelection.tsx
var TableColumnSelection = ({ table }) => {
  const theme = useTheme5();
  const toggleVisibility = (event, column) => {
    const visibleColumns = table.getVisibleFlatColumns().filter((col) => col.getCanHide());
    if (visibleColumns.length <= 1 && !event) {
      return;
    }
    column.toggleVisibility(event);
  };
  return /* @__PURE__ */ React70.createElement(DropdownMenu7.Root, null, /* @__PURE__ */ React70.createElement(DropdownMenu7.Trigger, { asChild: true }, /* @__PURE__ */ React70.createElement(ColumnSelectionButton, null, /* @__PURE__ */ React70.createElement(
    UilColumns,
    {
      size: 20,
      color: flat(theme.color.system.gray, "60%")
    }
  ))), /* @__PURE__ */ React70.createElement(DropdownMenu7.Portal, null, /* @__PURE__ */ React70.createElement(DropdownMenuContent2, null, table.getAllFlatColumns().filter((column) => column.getCanHide()).map((column) => {
    return /* @__PURE__ */ React70.createElement(
      DropdownMenu7.Item,
      {
        key: column.id,
        disabled: true
      },
      /* @__PURE__ */ React70.createElement(ColumnSelectionCheckbox, { key: column.id }, /* @__PURE__ */ React70.createElement(
        Checkbox_default,
        {
          checked: column.getIsVisible(),
          onCheckedChange: (e) => toggleVisibility(e, column)
        }
      ), /* @__PURE__ */ React70.createElement(
        Typography_default,
        {
          as: "label",
          level: "8"
        },
        column.columnDef.header
      ))
    );
  }))));
};
var TableColumnSelection_default = TableColumnSelection;

// lib/components/Table/TableTitleBar.tsx
var BAR_STATES = {
  BULK_ACTIONS: "bulk_actions",
  DEFAULT: "default"
};
var Indicators3 = ({ indicators }) => {
  return /* @__PURE__ */ React71.createElement(Styles4.Indicators, null, indicators.map(({ name, value, color: color2, icon }) => /* @__PURE__ */ React71.createElement(Styles4.Indicator, null, icon ?? /* @__PURE__ */ React71.createElement(Styles4.Dot, { color: color2 }), /* @__PURE__ */ React71.createElement(
    Styles4.Count,
    {
      as: "b",
      level: "8"
    },
    value,
    " ",
    name ?? null
  ))));
};
var SearchAction = ({
  globalFilter,
  setGlobalFilter
}) => {
  const [isSearching, setIsSearching] = useState10(false);
  const showSearch = useMemo5(() => () => setIsSearching(true), []);
  const hideSearch = useMemo5(() => () => setIsSearching(!!globalFilter), [globalFilter]);
  const theme = useTheme6();
  return isSearching ? /* @__PURE__ */ React71.createElement(AnimatePresence3, null, /* @__PURE__ */ React71.createElement(
    motion6.div,
    {
      initial: { opacity: 0, x: -20 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -20 },
      transition: { duration: 0.25 }
    },
    /* @__PURE__ */ React71.createElement(
      TextInput_default,
      {
        label: "",
        value: globalFilter,
        onChange: setGlobalFilter,
        onBlur: hideSearch,
        autoFocus: true,
        "data-testid": "table-search-input"
      }
    )
  )) : /* @__PURE__ */ React71.createElement(Styles4.Separators, null, /* @__PURE__ */ React71.createElement(
    Styles4.SearchButton,
    {
      "aria-label": "Search",
      onClick: showSearch,
      type: "button",
      "data-testid": "table-search-button"
    },
    /* @__PURE__ */ React71.createElement(
      UilSearch2,
      {
        size: 20,
        color: flat(theme.color.system.gray, "60%")
      }
    )
  ));
};
function TableTitleBar(props) {
  const {
    filtering,
    filters,
    icon = null,
    table,
    title,
    search,
    setGlobalFilter,
    bulkActions,
    columnSelection,
    setColumnFilters,
    headerActions = null,
    indicators,
    variant,
    state,
    collapsible,
    isTableCollapsed,
    toggleTableRows
  } = props;
  const selectedRowModel = table.getSelectedRowModel();
  const selectedRows = selectedRowModel.rows;
  const selectedRowCount = selectedRows.length;
  const barState = table.getSelectedRowModel().rows.length > 0 ? BAR_STATES.BULK_ACTIONS : BAR_STATES.DEFAULT;
  const handleCancel = useCallback17(() => {
    table.toggleAllRowsSelected(false);
  }, [table.toggleAllRowsSelected]);
  let content;
  const renderedBulkActions = bulkActions ? bulkActions(selectedRows) : null;
  switch (barState) {
    case BAR_STATES.BULK_ACTIONS: {
      content = /* @__PURE__ */ React71.createElement(Styles4.Title, null, /* @__PURE__ */ React71.createElement(CheckIcon2, { size: 20 }), /* @__PURE__ */ React71.createElement(
        Phrasing_default,
        {
          level: "7",
          style: { color: "inherit" }
        },
        selectedRowCount,
        " Items Selected"
      ));
      break;
    }
    default: {
      content = /* @__PURE__ */ React71.createElement(Styles4.Title, null, icon, /* @__PURE__ */ React71.createElement(
        Phrasing_default,
        {
          level: "7",
          style: { color: "inherit" }
        },
        title
      ));
    }
  }
  return /* @__PURE__ */ React71.createElement(
    Styles4.Root,
    {
      barState,
      variant,
      isTableCollapsed
    },
    content,
    /* @__PURE__ */ React71.createElement(Styles4.Actions, null, /* @__PURE__ */ React71.createElement(Styles4.DefaultActionsContainer, { barState }, indicators.length > 0 && /* @__PURE__ */ React71.createElement(Indicators3, { indicators }), search && /* @__PURE__ */ React71.createElement(
      SearchAction,
      {
        globalFilter: state.globalFilter,
        setGlobalFilter
      }
    ), filtering && /* @__PURE__ */ React71.createElement(
      TableFiltersMenu_default,
      {
        filters,
        columnFilters: state.columnFilters,
        setColumnFilters
      }
    ), columnSelection && /* @__PURE__ */ React71.createElement(TableColumnSelection_default, { table }), headerActions && headerActions, collapsible && /* @__PURE__ */ React71.createElement(
      Styles4.IconButton,
      {
        "aria-label": "Toggle",
        role: "button",
        onClick: toggleTableRows
      },
      isTableCollapsed ? /* @__PURE__ */ React71.createElement(UilAngleLeft5, null) : /* @__PURE__ */ React71.createElement(UilAngleDown4, null)
    )), barState === BAR_STATES.BULK_ACTIONS && /* @__PURE__ */ React71.createElement(React71.Fragment, null, renderedBulkActions, /* @__PURE__ */ React71.createElement(Styles4.Separator, null), /* @__PURE__ */ React71.createElement(Toolbar3.Button, { asChild: true }, /* @__PURE__ */ React71.createElement(Styles5.ToolbarButton, { onClick: handleCancel }, "Cancel"))))
  );
}

// lib/components/Table/Table.tsx
var DEFAULT_PAGE_SIZE = 10;
var DEFAULT_ARRAY_STATE = [];
var DEFAULT_STATE = {};
var TOGGLE_ANIMATION_VARIANTS = {
  exit: {
    opacity: 0,
    y: -24,
    transition: {
      ease: "easeIn",
      duration: 0.5
    }
  },
  animate: {
    opacity: [0, 1],
    y: [-24, 0],
    transition: {
      ease: "easeIn",
      duration: 0.5
    }
  }
};
var Table2 = (props, ref) => {
  const {
    bulkActions,
    columnSelection,
    defaultSortedBy,
    defaultFilters,
    emptyState = "No results",
    filtering,
    filters = DEFAULT_ARRAY_STATE,
    headerActions,
    indicators = DEFAULT_ARRAY_STATE,
    pageSize = DEFAULT_PAGE_SIZE,
    pagination,
    rowCount,
    manualPagination,
    manualPaginationState,
    onPaginationChange,
    search = false,
    selectionState,
    subgrid = false,
    rowAction,
    sorting,
    style: parentStyle,
    title,
    variant = "primary",
    shouldHideTitleBar = false,
    extendedState = DEFAULT_STATE,
    onFilterChange,
    onSortChange,
    onSearchChange,
    onSaveState,
    hideColumnHeadings = false,
    collapsible = false,
    onCollapsedChange,
    collapsed = false,
    ...restProps
  } = props;
  const [selectedRows, setSelectedRows] = useState11({});
  const [sortedRows, setSortedRows] = useState11(defaultSortedBy ? [defaultSortedBy] : []);
  const [searchFilter, setSearchFilter] = useState11("");
  const [paginationState, setPaginationState] = useState11(
    manualPaginationState || { pageIndex: 0, pageSize }
  );
  const [columnFilters, setColumnFilters] = useState11(defaultFilters || []);
  const filtersApplied = useMemo6(() => columnFilters.map((columnFilter) => columnFilter.value).flat(), [columnFilters]);
  const filtersAreApplied = filtersApplied.length > 0;
  const [isTableCollapsed, setIsTableCollapsed] = useState11(collapsed);
  useEffect6(() => {
    if (collapsed != null && collapsed !== isTableCollapsed) {
      setIsTableCollapsed(collapsed);
    }
  }, [collapsed]);
  const toggleTableRows = useCallback18(() => {
    if (onCollapsedChange) {
      onCollapsedChange(!isTableCollapsed);
    }
    setIsTableCollapsed((isCollapsed) => !isCollapsed);
  }, [onCollapsedChange, setIsTableCollapsed, isTableCollapsed]);
  const setGlobalFilter = useCallback18(
    (e) => {
      if (onSearchChange) {
        onSearchChange((e == null ? void 0 : e.target.value) || "");
      }
      setSearchFilter((e == null ? void 0 : e.target.value) || "");
    },
    [setSearchFilter, onSearchChange]
  );
  const { state: propState, ...restPropsWithoutState } = restProps;
  const state = propState ?? {
    rowSelection: (selectionState == null ? void 0 : selectionState.selectedRows) || selectedRows,
    sorting: sortedRows,
    globalFilter: searchFilter,
    pagination: paginationState,
    columnFilters,
    ...extendedState
  };
  const paginationChangeHandler = useCallback18(
    (updater) => {
      const newState = typeof updater === "function" ? updater(paginationState) : updater;
      if (onPaginationChange) {
        onPaginationChange(newState);
      }
      setPaginationState(newState);
    },
    [setPaginationState, onPaginationChange, paginationState]
  );
  const filterChangeHandler = useCallback18(
    (updater) => {
      if (onFilterChange) {
        onFilterChange(updater);
      }
      setColumnFilters(updater);
    },
    [setColumnFilters, onFilterChange]
  );
  const sortChangeHandler = useCallback18(
    (updater) => {
      const newState = typeof updater === "function" ? updater(sortedRows) : updater;
      if (onSortChange) {
        onSortChange(newState);
      }
      setSortedRows(newState);
    },
    [setSortedRows, onSortChange, sortedRows]
  );
  const table = useTable({
    getColumnCanGlobalFilter: (column) => {
      var _a2, _b;
      if (column.columnDef.enableGlobalFilter) {
        return true;
      }
      const value = (_b = (_a2 = table.getCoreRowModel().flatRows[0]) == null ? void 0 : _a2._getAllCellsByColumnId()[column.id]) == null ? void 0 : _b.getValue();
      return typeof value === "string" || typeof value === "number" || value === null;
    },
    onRowSelectionChange: (selectionState == null ? void 0 : selectionState.setSelectedRows) || setSelectedRows,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: sortChangeHandler,
    onPaginationChange: paginationChangeHandler,
    onColumnFiltersChange: filterChangeHandler,
    pagination,
    manualPagination,
    manualPaginationState,
    rowCount,
    sorting,
    state,
    search,
    ...restPropsWithoutState
  });
  useEffect6(() => {
    setPaginationState((current) => {
      return {
        ...current,
        pageSize
      };
    });
  }, [pageSize]);
  useEffect6(() => {
    return () => {
      if (onSaveState) {
        onSaveState(table.getState());
      }
    };
  }, [onSaveState]);
  const headerRowElements = Array.from(table.getHeaderGroups(), (group) => /* @__PURE__ */ React72.createElement(
    TableHeaderRow2,
    {
      key: group.id,
      group,
      sorting,
      variant
    }
  ));
  const { rowState } = extendedState;
  const bodyRowElements = Array.from(table.getRowModel().rows, (row) => /* @__PURE__ */ React72.createElement(
    TableBodyRow2,
    {
      key: row.id,
      row,
      variant,
      rowAction,
      rowProps: rowState == null ? void 0 : rowState[row.id]
    }
  ));
  const { data, columns, ...validTableProps } = restProps;
  const fragments = columns.length + (columnSelection ? 1 : 0);
  useImperativeHandle3(ref, () => table, [table]);
  return /* @__PURE__ */ React72.createElement("div", null, !shouldHideTitleBar && /* @__PURE__ */ React72.createElement(
    TableTitleBar,
    {
      state,
      table,
      title,
      search,
      setGlobalFilter,
      bulkActions,
      filters,
      filtering,
      columnSelection,
      setColumnFilters: filterChangeHandler,
      headerActions,
      indicators,
      variant,
      collapsible,
      toggleTableRows,
      isTableCollapsed
    }
  ), filtersAreApplied && /* @__PURE__ */ React72.createElement(
    InlineTableFilter_default,
    {
      filters,
      columnFilters,
      setColumnFilters: filterChangeHandler
    }
  ), /* @__PURE__ */ React72.createElement(AnimatePresence4, { initial: false }, !isTableCollapsed && /* @__PURE__ */ React72.createElement(
    motion7.div,
    {
      animate: "animate",
      exit: "exit",
      variants: TOGGLE_ANIMATION_VARIANTS
    },
    /* @__PURE__ */ React72.createElement(
      Table,
      {
        className: ["Table", ...subgrid ? ["table--subgrid"] : []].join(" "),
        style: { "--fragments": fragments, ...parentStyle },
        ...validTableProps
      },
      !hideColumnHeadings && /* @__PURE__ */ React72.createElement(TableHeader, { className: "TableHeader" }, headerRowElements),
      /* @__PURE__ */ React72.createElement(TableBody, { className: "TableBody" }, bodyRowElements)
    )
  )), !bodyRowElements.length && !isTableCollapsed && /* @__PURE__ */ React72.createElement(TableEmptyStateWrapper, { variant }, emptyState), pagination && /* @__PURE__ */ React72.createElement(
    TablePaginationBar_default,
    {
      table,
      state,
      variant,
      manualPagination,
      rowCount
    }
  ));
};
var Table_default = forwardRef51(Table2);

// lib/components/Table/TableToolbarButton.tsx
import React73, { forwardRef as forwardRef52 } from "react";
function TableToolbarButton(props, ref) {
  return /* @__PURE__ */ React73.createElement(
    Styles5.ToolbarButton,
    {
      ref,
      variant: "primary",
      ...props
    }
  );
}
var TableToolbarButton_default = forwardRef52(TableToolbarButton);

// lib/components/Tabs/Tabs.tsx
import { List as TabList, Content as TabContent } from "@radix-ui/react-tabs";

// lib/components/Tabs/TabRoot.tsx
import React74, { forwardRef as forwardRef53 } from "react";
import { Root as BaseRoot2 } from "@radix-ui/react-tabs";

// lib/components/Tabs/TabContext.tsx
import { createContext as createContext3 } from "react";
var TabContext = createContext3("tab");

// lib/components/Tabs/TabRoot.tsx
function TabRoot(props, ref) {
  const { variant = "content-switcher", ...rest } = props;
  return /* @__PURE__ */ React74.createElement(TabContext.Provider, { value: variant }, /* @__PURE__ */ React74.createElement(
    BaseRoot2,
    {
      ref,
      ...rest,
      "data-foo": "foo"
    }
  ));
}
var TabRoot_default = forwardRef53(TabRoot);

// lib/components/Tabs/TabTrigger.tsx
import React75, { forwardRef as forwardRef54, useContext as useContext3 } from "react";
import * as RadixTabs3 from "@radix-ui/react-tabs";

// lib/components/Tabs/TabTrigger.styles.tsx
import styled59 from "@emotion/styled";
var Trigger10 = styled59.button(
  {
    border: "1px solid transparent",
    padding: "8px 12px",
    cursor: "pointer",
    transitionDuration: "192ms",
    transitionProperty: "background-color, border-color, color",
    transitionTimingFunction: "ease-in-out"
  },
  ({ theme, _badge }) => ({
    minHeight: _badge ? "48px" : "32px",
    fontSize: theme.fontSize["8"],
    fontWeight: theme.fontWeight,
    "& .badge": {
      transitionDuration: "192ms",
      transitionProperty: "background-color, border-color, color",
      transitionTimingFunction: "ease-in-out"
    },
    '&[data-state="inactive"]': {
      "& .badge": {
        backgroundColor: flat(theme.color.system.gray, "20%"),
        color: flat(theme.color.system.gray, "60%"),
        borderColor: flat(theme.color.system.gray, "20%")
      },
      "&:hover": {
        "& .badge": {
          backgroundColor: flat(theme.color.brand.primary, "100%"),
          color: "white",
          borderColor: flat(theme.color.brand.primary, "100%")
        }
      }
    }
  }),
  ({ theme, _variant }) => {
    switch (_variant) {
      case "content-switcher": {
        return {
          backgroundColor: flat(theme.color.brand.primary, "5%"),
          borderLeftColor: flat(theme.color.brand.primary, "40%"),
          borderWidth: "1px 0px 1px 1px",
          color: flat(theme.color.brand.primary),
          "&:first-of-type": {
            borderBottomLeftRadius: "6px",
            borderLeftColor: "transparent",
            borderTopLeftRadius: "6px"
          },
          "&:last-of-type": {
            borderBottomRightRadius: "6px",
            borderRightWidth: "1px",
            borderTopRightRadius: "6px"
          },
          '&[data-state="active"]': {
            backgroundColor: flat(theme.color.brand.primary),
            borderColor: flat(theme.color.brand.primary),
            color: rgb3(theme.color.system.white),
            '& + [data-state="inactive"]': {
              borderLeftColor: flat(theme.color.brand.primary)
            }
          },
          "&:hover": {
            borderColor: flat(theme.color.brand.primary),
            '& + [data-state="inactive"]': {
              borderLeftColor: flat(theme.color.brand.primary)
            }
          },
          '&[data-disabled=""]': {
            backgroundColor: flat(theme.color.system.gray, "10%"),
            color: flat(theme.color.system.gray, "40%"),
            pointerEvents: "none"
          }
        };
      }
      case "tab": {
        return {
          backgroundColor: flat(theme.color.system.gray, "5%"),
          borderBottomWidth: "0px",
          borderColor: flat(theme.color.system.gray, "20%"),
          borderTopLeftRadius: "6px",
          borderTopRightRadius: "6px",
          color: flat(theme.color.system.gray, "80%"),
          "&:hover": {
            backgroundColor: flat(theme.color.brand.primary, "5%"),
            borderColor: flat(theme.color.brand.dark, "20%"),
            color: flat(theme.color.brand.primary)
          },
          '&[data-state="active"]': {
            backgroundColor: "white",
            color: flat(theme.color.brand.primary)
          },
          '&[data-disabled=""]': {
            backgroundColor: flat(theme.color.system.gray, "10%"),
            color: flat(theme.color.system.gray, "40%"),
            pointerEvents: "none"
          }
        };
      }
      case "tab-switcher": {
        return {
          backgroundColor: "white",
          borderColor: flat(theme.color.system.gray, "20%"),
          borderWidth: "1px 0px 1px 1px",
          color: flat(theme.color.system.gray, "60%"),
          padding: "8px 16px",
          "&:first-of-type": {
            borderBottomLeftRadius: "4px",
            borderTopLeftRadius: "4px"
          },
          "&:last-of-type": {
            borderBottomRightRadius: "4px",
            borderRightWidth: "1px",
            borderTopRightRadius: "4px"
          },
          '&[data-state="active"]': {
            backgroundColor: flat(theme.color.brand.primary, "5%"),
            color: flat(theme.color.brand.primary)
          },
          "&:hover": {
            borderColor: flat(theme.color.brand.primary),
            color: flat(theme.color.brand.primary),
            '& + [role="tab"]': {
              borderLeftColor: flat(theme.color.brand.primary)
            }
          },
          '&[data-disabled=""]': {
            backgroundColor: flat(theme.color.system.gray, "10%"),
            color: flat(theme.color.system.gray, "40%"),
            pointerEvents: "none"
          }
        };
      }
    }
    return {};
  }
);
var TriggerChildrenContainer = styled59.span({
  display: "flex",
  alignItems: "center",
  gap: "8px"
});

// lib/components/Tabs/TabTrigger.tsx
function TabTrigger(props, ref) {
  const variant = useContext3(TabContext);
  const { badge, children, icon, iconPosition = "start", value, ...restProps } = props;
  return /* @__PURE__ */ React75.createElement(
    RadixTabs3.Trigger,
    {
      asChild: true,
      value,
      ...restProps
    },
    /* @__PURE__ */ React75.createElement(
      Trigger10,
      {
        ref,
        _badge: badge != null,
        _variant: variant
      },
      /* @__PURE__ */ React75.createElement(TriggerChildrenContainer, null, iconPosition === "start" && icon && /* @__PURE__ */ React75.createElement(React75.Fragment, null, icon), children, iconPosition === "end" && icon && /* @__PURE__ */ React75.createElement(React75.Fragment, null, icon), variant === "tab" && badge != null && /* @__PURE__ */ React75.createElement(
        Badge_default,
        {
          className: "badge",
          variant: "primary"
        },
        badge
      ))
    )
  );
}
var TabTrigger_default = forwardRef54(TabTrigger);

// lib/components/Tabs/TabsList.tsx
import React76 from "react";

// lib/components/Tabs/TabsList.styles.tsx
import styled60 from "@emotion/styled";
import * as RadixTabs4 from "@radix-ui/react-tabs";
var Root47 = styled60(RadixTabs4.TabsList)({
  display: "flex"
});

// lib/components/Tabs/TabsList.tsx
function TabsList3(props) {
  return /* @__PURE__ */ React76.createElement(Root47, { ...props });
}
var TabsList_default = TabsList3;

// lib/components/Toast/index.ts
import { Provider as ToastProvider } from "@radix-ui/react-toast";

// lib/components/Toast/Toast.tsx
import React77 from "react";
import * as RadixToastPrimitives from "@radix-ui/react-toast";
import { Uil10Plus, UilMultiply, UilExclamationTriangle as UilExclamationTriangle2, UilExclamationCircle as UilExclamationCircle5 } from "@iconscout/react-unicons";

// lib/components/Toast/Toast.styles.tsx
import { keyframes } from "@emotion/react";
import styled61 from "@emotion/styled";
import * as ToastPrimitives from "@radix-ui/react-toast";
var VIEWPORT_PADDING = 25;
var hide = keyframes({
  "0%": { opacity: 1 },
  "100%": { opacity: 0 }
});
var slideIn = keyframes({
  from: { transform: `translateX(calc(100% + ${VIEWPORT_PADDING}px))` },
  to: { transform: "translateX(0)" }
});
var swipeOut = keyframes({
  from: { transform: "translateX(var(--radix-toast-swipe-end-x))" },
  to: { transform: `translateX(calc(100% + ${VIEWPORT_PADDING}px))` }
});
var TitleBar = styled61.div({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between"
});
var Title7 = styled61(ToastPrimitives.Title)({
  minWidth: 0,
  flex: 1
});
var Root49 = styled61(ToastPrimitives.Root)({
  label: "toast",
  display: "grid",
  gridTemplateColumns: "64px 1fr",
  backgroundColor: "white",
  width: "462px",
  '&[data-state="open"]': {
    animation: `${slideIn} 150ms cubic-bezier(0.16, 1, 0.3, 1)`
  },
  '&[data-state="closed"]': {
    animation: `${hide} 100ms ease-in`
  },
  '&[data-swipe="move"]': {
    transform: "translateX(var(--radix-toast-swipe-move-x))"
  },
  '&[data-swipe="cancel"]': {
    transform: "translateX(0)",
    transition: "transform 200ms ease-out"
  },
  '&[data-swipe="end"]': {
    animation: `${swipeOut} 100ms ease-out`
  }
});
var Content18 = styled61.div(
  {
    borderTopRightRadius: "8px",
    borderBottomRightRadius: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "16px"
  },
  ({ theme }) => {
    return {
      border: `1px solid ${flat(theme.color.system.gray, "20%")}`,
      borderLeft: "none"
    };
  }
);
var IconContainer = styled61.div(
  {
    alignItems: "center",
    borderTopLeftRadius: "8px",
    borderBottomLeftRadius: "8px",
    display: "flex",
    justifyContent: "center"
  },
  (props) => {
    const { theme, action } = props;
    if (action !== void 0) {
      return {
        backgroundColor: flat(theme.color.system[action], "10%"),
        border: `1px solid ${flat(theme.color.system[action], "40%")}`,
        borderRight: "none"
      };
    }
    return {
      backgroundColor: flat(theme.color.brand.primary, "10%"),
      border: `1px solid ${flat(theme.color.brand.primary, "40%")}`,
      borderRight: "none"
    };
  }
);
var ActionButton = styled61(ToastPrimitives.Action)`
  margin-top: 12px;
  width: fit-content;
  min-height: 32px;
`;
var CloseButton = styled61(ToastPrimitives.Close)`
  background: none;
  border: none;
  color: inherit;
`;

// lib/components/Toast/Toast.tsx
var DEFAULT_ICON_PER_VARIANT = {
  positive: /* @__PURE__ */ React77.createElement(Uil10Plus, null),
  notice: /* @__PURE__ */ React77.createElement(UilExclamationTriangle2, null),
  negative: /* @__PURE__ */ React77.createElement(UilExclamationCircle5, null)
};
function Toast(props) {
  const { variant = "positive", label, description, actions, icon, ...rest } = props;
  const iconToDisplay = icon ?? DEFAULT_ICON_PER_VARIANT[variant] ?? DEFAULT_ICON_PER_VARIANT.positive;
  return /* @__PURE__ */ React77.createElement(Root49, { ...rest }, /* @__PURE__ */ React77.createElement(IconContainer, { action: variant }, iconToDisplay), /* @__PURE__ */ React77.createElement(Content18, null, /* @__PURE__ */ React77.createElement(TitleBar, null, /* @__PURE__ */ React77.createElement(Title7, { asChild: true }, /* @__PURE__ */ React77.createElement(Heading_default, { level: "7" }, label)), /* @__PURE__ */ React77.createElement(CloseButton, { "aria-label": "Close" }, /* @__PURE__ */ React77.createElement(
    UilMultiply,
    {
      "aria-hidden": true,
      size: "16"
    }
  ))), /* @__PURE__ */ React77.createElement(RadixToastPrimitives.Description, null, /* @__PURE__ */ React77.createElement(Typography_default, { level: "8" }, description)), /* @__PURE__ */ React77.createElement(
    ActionButton,
    {
      altText: "Toast Action",
      asChild: true
    },
    actions
  )));
}
var Toast_default = Toast;

// lib/components/Toast/ToastViewport.styles.tsx
import styled62 from "@emotion/styled";
import { Viewport } from "@radix-ui/react-toast";
var ToastViewport = styled62(Viewport)({
  label: "toast-viewport",
  /* Visually separate toasts. */
  "& > * + *": {
    marginTop: 8
  }
});

// lib/components/Toggle/Toggle.tsx
import React78, { forwardRef as forwardRef55 } from "react";
import { UilInfoCircle as UilInfoCircle2 } from "@iconscout/react-unicons";

// lib/components/Toggle/Toggle.styles.tsx
import styled63 from "@emotion/styled";
import * as Switch from "@radix-ui/react-switch";
var Root51 = styled63(Switch.Root)(
  {
    label: "toggle",
    width: "30px",
    height: "16px",
    borderRadius: "100px",
    position: "relative",
    cursor: "pointer",
    borderStyle: "solid",
    borderWidth: "2px",
    outline: "none"
  },
  ({ theme }) => ({
    backgroundColor: flat(theme.color.system.gray, "40%"),
    borderColor: flat(theme.color.system.gray, "40%"),
    '&[data-state="checked"]': {
      backgroundColor: flat(theme.color.brand.primary),
      borderColor: flat(theme.color.brand.primary)
    },
    "&:hover": {
      boxShadow: `0 4px 20px ${flat(theme.color.system.gray, "20%")}`
    },
    "&:disabled": {
      backgroundColor: flat(theme.color.system.gray, "10%"),
      borderColor: flat(theme.color.system.gray, "10%"),
      pointerEvents: "none",
      boxShadow: "none"
    }
  })
);
var Thumb2 = styled63(Switch.Thumb)(
  {
    position: "absolute",
    top: 0,
    left: 0,
    width: "12px",
    height: "12px",
    borderRadius: "9999px",
    transition: "all 0.2s ease-in-out",
    transform: "translateX(2px)"
  },
  ({ theme }) => ({
    backgroundColor: rgb3(theme.color.system.white),
    '&[data-state="checked"]': {
      transform: "translateX(13px)"
    },
    "&:disabled": {
      backgroundColor: flat(theme.color.system.gray, "20%")
    }
  })
);
var ContentWrapper2 = styled63.div({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  width: "fit-content"
});
var FieldTitleWrapper = styled63.div({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  cursor: "pointer",
  width: "fit-content"
});
var FieldTitleLabel = styled63.div({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  cursor: "pointer",
  width: "fit-content"
});
var Label3 = styled63(Typography_default)({
  cursor: "pointer"
});

// lib/components/Toggle/Toggle.tsx
var Toggle = forwardRef55(
  ({ leftLabelText, rightLabelText, fieldTitle, ...restProps }, ref) => {
    return /* @__PURE__ */ React78.createElement(FieldTitleWrapper, { as: "label" }, fieldTitle && /* @__PURE__ */ React78.createElement(FieldTitleLabel, null, /* @__PURE__ */ React78.createElement(UilInfoCircle2, { size: 15 }), /* @__PURE__ */ React78.createElement(Typography_default, { level: "9" }, fieldTitle)), /* @__PURE__ */ React78.createElement(ContentWrapper2, null, leftLabelText && /* @__PURE__ */ React78.createElement(Label3, { level: "9" }, leftLabelText), /* @__PURE__ */ React78.createElement(
      Root51,
      {
        ...restProps,
        ref
      },
      /* @__PURE__ */ React78.createElement(Thumb2, null)
    ), rightLabelText && /* @__PURE__ */ React78.createElement(Label3, { level: "9" }, rightLabelText)));
  }
);
var Toggle_default = Toggle;

// lib/components/TextArea/TextArea.tsx
import React79, { forwardRef as forwardRef56 } from "react";

// lib/components/TextArea/TextArea.styles.tsx
import styled64 from "@emotion/styled";
var TextArea = styled64.textarea(
  {
    label: "textarea",
    // Clear input styling, but keep box-sizing behavior.
    all: "unset",
    boxSizing: "border-box",
    border: "1px solid transparent",
    borderRadius: "4px",
    padding: "12px",
    width: "100%"
  },
  ({ theme }) => {
    return {
      backgroundColor: rgb3(theme.color.system.white),
      borderColor: rgb3(theme.color.system.border),
      boxShadow: `${base_default.boxShadow.inner} ${rgb3(theme.color.shadow.inner, "10%")}`,
      "&::placeholder": {
        color: rgb3(theme.color.system.gray, "60%")
      },
      '&[aria-invalid="true"]': {
        borderColor: rgb3(theme.color.system.negative)
      },
      "&:focus-within, &:hover": {
        borderColor: rgb3(theme.color.brand.primary),
        boxShadow: "none"
      },
      "&:focus-within": {
        boxShadow: `0 0 8px 0 ${rgb3(theme.color.brand.lighter)}`
      },
      "&:disabled, &:disabled::placeholder": {
        backgroundColor: rgb3(theme.color.system.gray, "10%"),
        borderColor: "transparent",
        boxShadow: "none",
        color: rgb3(theme.color.system.gray, "40%"),
        cursor: "not-allowed"
      }
    };
  }
);

// lib/components/TextArea/TextArea.tsx
var TextArea2 = forwardRef56(
  ({ rows = 5, ...props }, ref) => {
    return /* @__PURE__ */ React79.createElement(
      TextArea,
      {
        ref,
        rows,
        ...props
      }
    );
  }
);

// lib/components/PasswordInput/PasswordInput.tsx
import React80, { forwardRef as forwardRef57, useCallback as useCallback19 } from "react";
import { UilEyeSlash, UilEye } from "@iconscout/react-unicons";

// lib/components/PasswordInput/PasswordInput.styles.tsx
import styled65 from "@emotion/styled";
var Action5 = styled65.button(
  {
    label: "password-action",
    all: "unset",
    padding: 4,
    lineHeight: 0
  },
  ({ theme }) => ({
    "&:focus, &:hover": {
      color: rgb3(theme.color.system.gray),
      cursor: "pointer"
    },
    "&:disabled": {
      color: "inherit",
      pointerEvents: "none"
    }
  })
);

// lib/components/PasswordInput/PasswordInput.tsx
function Actions6({ onClick, visible, disabled }) {
  return /* @__PURE__ */ React80.createElement(
    Action5,
    {
      "aria-label": visible ? "Hide password" : "Show password",
      disabled,
      onClick,
      type: "button"
    },
    visible ? /* @__PURE__ */ React80.createElement(UilEye, { size: "1em" }) : /* @__PURE__ */ React80.createElement(UilEyeSlash, { size: "1em" })
  );
}
var PasswordInput = forwardRef57(
  ({ disabled, end, ...delegated }, ref) => {
    const [visible, setVisibility] = React80.useState(false);
    const toggleVisibility = useCallback19(() => setVisibility((value) => !value), [setVisibility]);
    return /* @__PURE__ */ React80.createElement(
      TextField,
      {
        ref,
        disabled,
        end: end ?? /* @__PURE__ */ React80.createElement(
          Actions6,
          {
            disabled,
            onClick: toggleVisibility,
            visible
          }
        ),
        type: visible ? "text" : "password",
        ...delegated
      }
    );
  }
);

// lib/components/ProgressIndicator/ProgressIndicator.tsx
import React81, { forwardRef as forwardRef58 } from "react";

// lib/components/ProgressIndicator/ProgressIndicator.styles.tsx
import styled66 from "@emotion/styled";
var Root52 = styled66.ol(
  {
    label: "progressindicator",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    listStyleType: "none",
    counterReset: "list-item",
    // This selects only direct children of list and will helps maintain same styling when using asChild prop
    "& > *": {
      counterIncrement: "list-item",
      display: "grid",
      gridTemplateAreas: '"icon label separator"',
      gridTemplateColumns: "max-content 1fr"
    }
  },
  ({ theme }) => ({
    '&[data-variant="large"]': {
      fontSize: theme.fontSize[7],
      "--separator-size": "32px"
    },
    '&[data-variant="small"]': {
      fontSize: theme.fontSize[8],
      "--separator-size": "18px"
    },
    color: rgb(theme.colorV2.system.gray, 600),
    "& > *:after": {
      content: '""',
      width: "1px",
      height: "1px",
      backgroundColor: rgb(theme.colorV2.system.gray, 200)
    },
    '&[data-orientation="vertical"]': {
      flexDirection: "column",
      "& > *": {
        gridTemplateAreas: '"icon label" "separator ."',
        gridTemplateColumns: "max-content 1fr"
      },
      "& > *::after": {
        height: "min(var(--separator-size), 26px)",
        gridArea: "separator",
        justifySelf: "center"
      }
    },
    '&[data-orientation="horizontal"]': {
      "& > *::after": {
        width: "var(--separator-size)"
      }
    },
    "& > *:last-child::after ": {
      display: "none"
    }
  })
);

// lib/components/ProgressIndicator/ProgressIndicator.tsx
var ProgressIndicator = forwardRef58(
  ({ variant = "small", orientation = "horizontal", withNumbers = false, ...delegated }, ref) => {
    return /* @__PURE__ */ React81.createElement(
      Root52,
      {
        "data-orientation": orientation,
        "data-variant": variant,
        "data-show-numbers": withNumbers,
        className: "progress-indicator",
        ref,
        ...delegated
      }
    );
  }
);

// lib/components/ProgressIndicator/ProgressIndicatorItem.tsx
import React82, { forwardRef as forwardRef59 } from "react";
import { UilCheck as CheckIcon3 } from "@iconscout/react-unicons";
import { Slot as Slot8, Slottable as Slottable4 } from "@radix-ui/react-slot";

// lib/components/ProgressIndicator/ProgressIndicatorItem.styles.tsx
import styled67 from "@emotion/styled";
var ProgressIndicatorListItem = styled67.li(() => ({
  alignItems: "center",
  gap: "8px"
}));
var IconWrapper = styled67.span(
  {
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 9999,
    width: 32,
    height: 32,
    padding: 7,
    '[data-variant="small"] &': {
      width: 12,
      height: 12,
      padding: 0,
      svg: {
        display: "none"
      }
    },
    '[data-show-numbers="true"] &:empty::before': {
      content: "counter(list-item)"
    }
  },
  ({ theme }) => ({
    border: `1px solid ${rgb(theme.colorV2.system.border)}`,
    '[data-state="active"] &': {
      borderColor: "transparent",
      backgroundColor: rgb(theme.colorV2.brand),
      color: rgb(theme.colorV2.system.white)
    },
    '[data-state="complete"] &': {
      borderColor: "transparent",
      backgroundColor: rgb(theme.colorV2.system.positive),
      color: rgb(theme.colorV2.system.white)
    },
    '[data-orientation="horizontal"][data-variant="large"] [data-state="complete"] &': {
      backgroundColor: "transparent",
      padding: 0,
      color: rgb(theme.colorV2.system.positive)
    }
  })
);

// lib/components/ProgressIndicator/ProgressIndicatorItem.tsx
var IndicatorIcon = ({ icon, state }) => {
  const inner = icon ?? (state === "complete" ? /* @__PURE__ */ React82.createElement(CheckIcon3, { className: "check-icon" }) : null);
  return /* @__PURE__ */ React82.createElement(IconWrapper, { className: "iconwrapper" }, inner);
};
var ProgressIndicatorItem = forwardRef59(
  ({ asChild = false, children, icon, state = "default", ...delegated }, ref) => {
    const Comp = asChild ? ProgressIndicatorListItem.withComponent(Slot8) : ProgressIndicatorListItem;
    return /* @__PURE__ */ React82.createElement(
      Comp,
      {
        "data-state": state,
        ref,
        ...delegated
      },
      /* @__PURE__ */ React82.createElement(
        IndicatorIcon,
        {
          icon,
          state
        }
      ),
      /* @__PURE__ */ React82.createElement(Slottable4, null, children)
    );
  }
);

// lib/components/SplitButton/SplitButton.tsx
import React83, { forwardRef as forwardRef60 } from "react";
import { UilAngleDown as ArrowIcon2 } from "@iconscout/react-unicons";

// lib/components/SplitButton/SplitButton.styles.tsx
import styled68 from "@emotion/styled";
var SplitButton = styled68(Button_default)(
  {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0
  },
  (props) => {
    const { theme, variant } = props;
    const borderColor = variant === "secondary" ? rgb(theme.colorV2.brand, 200) : rgb(theme.colorV2.brand, 500);
    return {
      borderRight: `.5px solid ${borderColor}`,
      "&:disabled": {
        borderRightColor: rgb(theme.colorV2.system.gray, 100)
      }
    };
  }
);
var Root53 = styled68.div({
  display: "flex"
});
var ArrowButton3 = styled68(Button_default)(
  {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    gap: "8px",
    // as we are passing only icon to button, it still takes some empty space for button label
    // which looks weired & this will remove that right side extra space
    padding: "4px 0 4px 4px",
    "& svg": {
      transition: "transform .2s linear"
    },
    '&[data-state="open"] svg': {
      transform: "rotate(180deg)"
    }
  },
  (props) => {
    const { theme, variant } = props;
    const borderColor = variant === "secondary" ? rgb(theme.colorV2.brand, 200) : rgb(theme.colorV2.brand, 500);
    return {
      borderLeft: `.5px solid ${borderColor}`,
      "&:disabled": {
        borderLeftColor: rgb(theme.colorV2.system.gray, 100)
      }
    };
  }
);

// lib/components/SplitButton/SplitButton.tsx
var SplitButton2 = forwardRef60(
  ({ variant, disabled = false, children, label, className, ...delegated }, ref) => {
    return /* @__PURE__ */ React83.createElement(Root53, { className }, /* @__PURE__ */ React83.createElement(
      SplitButton,
      {
        ref,
        disabled,
        variant,
        ...delegated
      },
      label
    ), /* @__PURE__ */ React83.createElement(
      OverflowMenu_default,
      {
        trigger: /* @__PURE__ */ React83.createElement(
          ArrowButton3,
          {
            type: "button",
            iconPosition: "start",
            "aria-label": "Options",
            variant,
            disabled,
            icon: /* @__PURE__ */ React83.createElement(ArrowIcon2, null)
          }
        ),
        disabled
      },
      children
    ));
  }
);
var SplitButton_default = SplitButton2;
export {
  Accordion_default as Accordion,
  AccordionItem_default as AccordionItem,
  AnnouncementDialog_default as AnnouncementDialog,
  AnnouncementDialogTabsContent_default as AnnouncementDialogTabsContent,
  List as AnnouncementDialogTabsList,
  AnnouncementDialogTabsTrigger_default as AnnouncementDialogTabsTrigger,
  AppLogo_default as AppLogo,
  Avatar_default as Avatar,
  AvatarWithPopoverContent_default as AvatarWithPopoverContent,
  Badge_default as Badge,
  Banner_default as Banner,
  BreadcrumbItem,
  Breadcrumbs,
  Button_default as Button,
  Card_default as Card,
  CardActions_default as CardActions,
  CardContent_default as CardContent,
  CardTitle_default as CardTitle,
  Checkbox_default as Checkbox,
  CircularProgressBar,
  Color,
  Dropdown,
  ExternalTheme,
  Field,
  FileUpload2 as FileUpload,
  Heading_default as Heading,
  IconButton2 as IconButton,
  InternalTheme,
  Link2 as Link,
  Modal,
  ModalButton_default as ModalButton,
  NotificationContainer,
  NotificationContextProvider as NotificationProvider,
  NumberInput,
  OptionSlider,
  OverflowMenu_default as OverflowMenu,
  OverflowMenuItem_default as OverflowMenuItem,
  Overlay4 as Overlay,
  Pagination_default as Pagination,
  PasswordInput,
  Phrasing_default as Phrasing,
  PillGroup_default as PillGroup,
  Popover_default as Popover,
  PopoverProvider_default as PopoverProvider,
  ProgressBar,
  ProgressIndicator,
  ProgressIndicatorItem,
  RadioButton,
  RadioButtonGroup,
  Search,
  ClearIndicator2 as SearchClearIndicator,
  SearchIcon,
  Slider,
  SplitButton_default as SplitButton,
  StackedProgressBar,
  SystemButton_default as SystemButton,
  TabContent,
  TabsList_default as TabList,
  TabRoot_default as TabRoot,
  TabTrigger_default as TabTrigger,
  Table_default as Table,
  TableToolbarButton_default as TableToolbarButton,
  Tag3 as Tag,
  TextArea2 as TextArea,
  TextField,
  TextInput_default as TextInput,
  Toast_default as Toast,
  ToastProvider,
  ToastViewport,
  Toggle_default as Toggle,
  Root51 as ToggleRoot,
  Thumb2 as ToggleThumb,
  TrustCloudTheme,
  Typography_default as Typography,
  base_default as base,
  rgb as color,
  flat,
  rgb3 as rgb,
  rgba,
  translucent,
  useModal_default as useModal,
  useNotificationContext
};
